import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  sendMessageToAPI,
  API_BASE_URL,
  CONVERSATIONS_UI_ENABLED,
  CONVERSATIONS_DELETE_UI_ENABLED,
  CHATROOMS_UI_ENABLED,
  clearStoredIdentity,
  getBoundUserId,
  getBoundEmail,
  getBillingStatus,
  getMemoryStatus,
  setMemoryEnabled,
  deleteMemorySummaries,
  trimMemoryHistory,
  forgetMe,
  setConsent,
  getMemoryList,
  deleteMemoryItem,
  getIdentityCard,
  postIdentityCard,
  deleteIdentityCard,
  acceptAudiobookOffer,
  dismissAudiobookOffer,
  bootstrapSession,
  bootstrapAboutAuthFromUrl,
  getAboutAccessStatus,
  hasAboutFullAiAccess,
  getDragaoStatus,
  acceptDragaoMode,
  disableDragaoMode,
  identifyDragao,
  submitDragaoBaseline,
  submitDragaoCheckin,
  finalizeDragaoReview,
  getTermsStatus,
  acceptTerms,
  listConversations,
  createConversation,
  getParableShortConclusion,
  getConversationMessages,
  deleteConversation,
  deleteConversationMessages,
  deleteAllConversations,
  CONVERSATIONS_AUTO_LOAD_ON_OPEN,
} from "./api";
import {
  WAIT_BRIDGE_ARM_DELAY_MS,
  fetchWaitBridge,
  isWaitBridgeEnabled,
  logWaitBridge,
  shouldRenderWaitBridge,
} from "./waitBridge";
import { todasMusicas } from "./musicas";
import { detectUiLang, tr, LANG_OPTIONS } from "./i18n";
import {
  LEGAL_PT,
  LEGAL_EN,
  LEGAL_ES,
  PRIVACY_PT,
  PRIVACY_EN,
  PRIVACY_ES,
  TERMS_PT,
  TERMS_EN,
  TERMS_ES,
  PRICING_PT,
  PRICING_EN,
  PRICING_ES,
  REFUND_PT,
  REFUND_EN,
  REFUND_ES,
  DATA_REQUEST_PT,
  DATA_REQUEST_EN,
  DATA_REQUEST_ES,
  ABOUT_PT,
  ABOUT_EN,
  ABOUT_ES,
  getLegalLangFromBrowser,
} from "./legalContent";
import { ARQUIVO_PT, ARQUIVO_EN, ARQUIVO_ES } from "./arquivoConfidencialContent";
import {
  VOICE_LANGS,
  isSTTSupported,
  createRecognizer,
  stopSpeak,
} from "./voice";
import EpicResponseRenderer from "./components/EpicResponseRenderer";
import HunterSuggestion from "./components/HunterSuggestion";
import MobileMenuFab from "./components/MobileMenuFab";
import VHChatRoomInterface from "./components/chatrooms/VHChatRoomInterface";
import VHAtmosphereLayer from "./VHAtmosphereLayer";
import {
  resolveVisualStateFromPayload,
  shouldApplyVisualState,
  withAppliedAt,
} from "./visualAtmosphere";
import {
  getHunterSuggestionsByLocale,
  getRandomHunterSuggestion,
} from "./hunterSuggestions";
import {
  cancelPendingVillainInterruption,
  notifyRequestStarted as notifyVillainRequestStarted,
  notifyResponseFinished as notifyVillainResponseFinished,
} from "./audio/villainInterruptionManager";
import {
  PROLOGUE_AUTO_UNLOCK,
  PROLOGUE_AUDIO_ID,
  VH_SPECIAL_AUDIO_UNLOCKS_ENABLED,
  clearPendingSpecialAudioOffer,
  clearSpecialAudioState,
  createDefaultSpecialAudioState,
  emitSpecialAudioTelemetry,
  ensureAutoPrologueUnlocked,
  ensurePrologueOfferPending,
  getPendingPrologueOffer,
  getPendingSpecialAudioOffers,
  getSpecialAudioOffer,
  insertAudiobookOfferInQueue,
  isLocalSpecialAudiobookOffer,
  loadSpecialAudioState,
  markSpecialAudioPhraseShown,
  persistSpecialAudioState,
  pullNextQueuedAudiobookOffer,
  registerSuccessfulNormalTurn,
  resolveLocalAudiobookSource,
} from "./vhSpecialAudiobooks";
import "./App.css";

const FIRST_QUESTION_TRACK = "/musicas/Full%20%5B2.11%5D%20by%20AliveTunes.mp3";
const LEGAL_COPY_V2 = String(process.env.REACT_APP_LEGAL_COPY_V2 || "1") !== "0";
const LEGAL_PAGES = String(process.env.REACT_APP_LEGAL_PAGES || "1") !== "0";
const LEGAL_ROUTE_SET = new Set(["/legal", "/privacy", "/terms", "/pricing", "/refund-policy", "/data-request", "/about", "/arquivo"]);
const TERMS_CONTENT_VERSION = "vh_terms_v2_legal_notice_expanded";
const MAIN_AI_RETURN_SENTINEL = "__vh_main_ai__";
const CONTACT_EMAIL = "contact@universevh.org";
const CONTACT_HREF = `mailto:${CONTACT_EMAIL}`;
const INSTITUTIONAL_BASE_URL = "https://universevh.org";
const QUICK_START_GUIDE_RENDER_ENABLED = false;
const DAY_HEADER_RE = /^Dia\s+(?:\d+|99\+)\s*$/;
const DRAGAO_ARMOR_KEYS = [
  "p1_visao",
  "p2_forca",
  "p3_estrutura",
  "p4_fortaleza",
  "p5_dominio_mental",
];
const DRAGAO_ARMOR_FLASH_MS = 2800;
const DRAGAO_CELEBRATION_MS = 4200;
const DRAGAO_CELEBRATION_SOUND_ENABLED =
  String(process.env.REACT_APP_VH_DRAGAO_CELEBRATION_SOUND || "1") === "1";
const DRAGAO_CHAT_LED_MODE = true;
const DRAGAO_HOME_CID_PREFIX = "vh_dragao_home_cid:";
const VH_FONT_SCALE_KEY = "vh_font_scale";
const TRANSIENT_SESSION_MAX_ITEMS = 6;
const TRANSIENT_SESSION_MAX_CHARS_PER_ITEM = 600;
const SPECIAL_AUDIO_GUEST_UID = "vh_guest_local";
const VH_CONVERSATION_STYLE_BIAS_PREFIX = "vh:conversation-style-bias:v1:";
const VH_AUTHORITY_PORTAL_URL = "https://vanhelsin.com";
const VH_ABOUT_FULL = "https://vanhelsin.com/checkout/review/full";
const VH_ABOUT_LOGIN = "https://vanhelsin.com/login";
const VH_ABOUT_ACCESS_RECOVER = "https://vanhelsin.com/access/recover";
const VH_BGM_BASE_VOLUME = 0.70;
function getAboutLoginUrl(lang) {
  const code = String(lang || "").toLowerCase().startsWith("pt")
    ? "pt"
    : String(lang || "").toLowerCase().startsWith("es")
      ? "es"
      : "en";
  return `${VH_ABOUT_LOGIN}?lang=${code}&src=universevh&returnTo=${encodeURIComponent("/ai-return")}`;
}
export function getAboutRecoverUrl(lang) {
  const code = String(lang || "").toLowerCase().startsWith("pt")
    ? "pt"
    : String(lang || "").toLowerCase().startsWith("es")
      ? "es"
      : "en";
  return `${VH_ABOUT_ACCESS_RECOVER}?lang=${code}&src=universevh`;
}

function getAuthorityPortalUrl(lang) {
  const code = String(lang || "").toLowerCase().startsWith("pt")
    ? "pt"
    : String(lang || "").toLowerCase().startsWith("es")
      ? "es"
      : "en";
  return `${VH_AUTHORITY_PORTAL_URL}/?lang=${code}`;
}

function getAuthorityPricingUrl(lang, options = {}) {
  const code = String(lang || "").toLowerCase().startsWith("pt")
    ? "pt"
    : String(lang || "").toLowerCase().startsWith("es")
      ? "es"
      : "en";
  const params = new URLSearchParams();
  params.set("lang", code);
  params.set("src", "universevh");
  const surface = String(options?.surface || "").trim();
  const plan = String(options?.plan || "").trim();
  if (surface) params.set("surface", surface);
  if (plan) params.set("plan", plan);
  return `${VH_AUTHORITY_PORTAL_URL}/pricing?${params.toString()}`;
}

function getRoutePath() {
  if (typeof window === "undefined") return "/";
  return String(window.location.pathname || "/").toLowerCase();
}

function normalizePath(p) {
  const s = String(p || "/").toLowerCase().replace(/\/+$/, "") || "/";
  return s === "" ? "/" : s;
}

function isAboutFullBillingStatus(status) {
  return Boolean(
    hasAboutFullAiAccess() ||
      status?.can_use_ai_premium === true ||
      String(status?.plan || "").toLowerCase() === "about_full" ||
      String(status?.access_state || "").toLowerCase() === "premium_full"
  );
}

function buildAboutFullBillingStatus() {
  const aboutStatus = getAboutAccessStatus();
  return {
    plan: "paid",
    paywall_mode: "off",
    free_used: 0,
    free_limit: 0,
    can_use_ai_premium: true,
    access_state: aboutStatus.access_state || "premium_full",
    source: "about_handoff",
  };
}

export function resolveInAppNavigationPath(path) {
  const requestedPath = String(path || "/");
  return normalizePath(requestedPath === MAIN_AI_RETURN_SENTINEL ? "/" : requestedPath);
}

export function shouldCaptureMainAiReturn({
  currentPath,
  appReady,
  showCinematicIntro,
  showChatroomsPage,
  hasEnteredMainAI,
  introDismissed,
}) {
  return (
    normalizePath(currentPath) === "/" &&
    Boolean(appReady) &&
    !showCinematicIntro &&
    !showChatroomsPage &&
    (Boolean(hasEnteredMainAI) || Boolean(introDismissed))
  );
}

function splitUltraDayHeader(text) {
  const raw = String(text ?? "");
  const lines = raw.split(/\r?\n/);
  if (!lines.length) return { dayLabel: null, bodyText: raw };
  const first = String(lines[0] || "").trim();
  if (!DAY_HEADER_RE.test(first)) return { dayLabel: null, bodyText: raw };
  const bodyText = lines.slice(1).join("\n");
  return { dayLabel: first, bodyText: bodyText || raw };
}

function LoginAccessRecoverBridge({ lang, onRecover, introSurface = false }) {
  const currentLang = lang || detectUiLang();
  return (
    <section
      aria-label={tr(currentLang, "loginRecoverTitle")}
      style={{
        margin: introSurface ? "18px 0 0" : "16px 0 0",
        padding: "14px 16px",
        border: "1px solid rgba(255,255,255,0.18)",
        borderRadius: "8px",
        background: "rgba(10, 10, 14, 0.82)",
        boxShadow: "0 10px 28px rgba(0, 0, 0, 0.22)",
      }}
    >
      <div style={{ fontSize: "0.98rem", fontWeight: 700, marginBottom: "6px" }}>
        {tr(currentLang, "loginRecoverTitle")}
      </div>
      <p style={{ margin: 0, opacity: 0.92, lineHeight: 1.5 }}>
        {tr(currentLang, "loginRecoverText")}
      </p>
      <button
        type="button"
        className="btn gothic-btn"
        style={{ marginTop: "12px" }}
        onClick={onRecover}
      >
        {tr(currentLang, "loginRecoverButton")}
      </button>
    </section>
  );
}

function buildSessionTurnsPayload(threadMessages, currentRequestText) {
  if (!Array.isArray(threadMessages) || threadMessages.length === 0) return undefined;
  const normalized = [];
  for (const item of threadMessages) {
    if (item?.transient) continue;
    const sender = String(item?.sender || "").trim().toLowerCase();
    const role = sender === "user" ? "user" : sender === "ia" ? "assistant" : "";
    if (!role) continue;
    const rawText = typeof item?.text === "string" ? item.text : "";
    const content = rawText.trim();
    if (!content) continue;
    normalized.push({
      role,
      content:
        content.length > TRANSIENT_SESSION_MAX_CHARS_PER_ITEM
          ? content.slice(0, TRANSIENT_SESSION_MAX_CHARS_PER_ITEM)
          : content,
    });
  }
  if (!normalized.length) return undefined;
  const recent = normalized.slice(-TRANSIENT_SESSION_MAX_ITEMS);
  const currentText = String(currentRequestText || "").trim();
  const last = recent[recent.length - 1];
  if (currentText && last?.role === "user" && last.content === currentText) {
    recent.pop();
  }
  return recent.length ? recent : undefined;
}

function responseStyleBiasScopeKey({ mode, conversationId }) {
  const modeTag = String(mode || "normal").trim().toLowerCase() === "dragao" ? "dragao" : "normal";
  const cid = String(conversationId || "").trim() || "session";
  return `${modeTag}:${cid}`;
}

export function shouldShowParableContextControls(message) {
  return message?.sender === "ia" && message?.usedParable === true;
}

export function shouldShowDirectPreferenceAction(messages, index) {
  const list = Array.isArray(messages) ? messages : [];
  if (index < 0 || index >= list.length) return false;
  if (!shouldShowParableContextControls(list[index])) return false;
  for (let i = index + 1; i < list.length; i += 1) {
    if (String(list[i]?.sender || "") === "ia") {
      return false;
    }
  }
  return true;
}

export function shouldFetchShortConclusion(cacheMap, assistantMessageId, loadingId) {
  const mid = String(assistantMessageId || "").trim();
  if (!mid) return false;
  if (String(loadingId || "").trim() === mid) return false;
  return !(cacheMap && Object.prototype.hasOwnProperty.call(cacheMap, mid));
}

export function getShortConclusionStateKey(message, fallbackKey = "") {
  const mid = String(message?.assistantMessageId || "").trim();
  if (mid) return mid;
  const fallback = String(fallbackKey || message?.id || "").trim();
  return fallback;
}

export function shouldShowShortConclusionAction(message, cacheMap, fallbackKey = "") {
  if (!shouldShowParableContextControls(message)) return false;
  const key = getShortConclusionStateKey(message, fallbackKey);
  if (!key) return false;
  return !(cacheMap && Object.prototype.hasOwnProperty.call(cacheMap, key));
}

export function shouldShowShortConclusionContent(expandedMap, shortConclusionKey) {
  const key = String(shortConclusionKey || "").trim();
  if (!key) return false;
  return expandedMap?.[key] !== false;
}

function splitConclusionSentences(text) {
  return String(text || "")
    .split(/(?<=[.!?])\s+/)
    .map((item) => String(item || "").trim())
    .filter(Boolean);
}

export function buildLocalShortConclusionFallback(responseText, unavailableText) {
  const fallbackText = String(unavailableText || "").trim();
  const raw = String(responseText || "").trim();
  if (!raw) return fallbackText;

  const paragraphs = raw
    .split(/\n\s*\n+/)
    .map((item) => String(item || "").trim())
    .filter((item) => item && !/^[-–—_*#>\s]+$/.test(item));

  if (!paragraphs.length) return fallbackText;

  const markerRe =
    /\b(moral|li[cç][aã]o|isso mostra|isso revela|isso sugere|em resumo|em termos diretos|portanto|logo|in short|in summary|therefore|en resumen)\b/i;
  const markerCandidates = [];
  const tailCandidates = [];

  paragraphs.slice(-3).forEach((paragraph) => {
    splitConclusionSentences(paragraph).forEach((sentence) => {
      const clean = sentence.replace(/\s+/g, " ").trim();
      if (!clean) return;
      if (clean.length < 24) return;
      if (markerRe.test(clean)) {
        markerCandidates.push(clean);
      } else {
        tailCandidates.push(clean);
      }
    });
  });

  const selected = markerCandidates.length ? markerCandidates.slice(0, 3) : tailCandidates.slice(-2);
  const unique = [];
  const seen = new Set();
  selected.forEach((item) => {
    const key = item.toLocaleLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    unique.push(item);
  });

  const joined = unique.join(" ").trim();
  if (joined) {
    return joined.length > 320 ? `${joined.slice(0, 320).trimEnd()}...` : joined;
  }

  const lastParagraph = String(paragraphs[paragraphs.length - 1] || "")
    .replace(/\s+/g, " ")
    .trim();
  if (lastParagraph.length >= 18) {
    return lastParagraph.length > 320
      ? `${lastParagraph.slice(0, 320).trimEnd()}...`
      : lastParagraph;
  }

  const lastSentence = splitConclusionSentences(raw).slice(-1)[0] || "";
  const cleanLastSentence = String(lastSentence).replace(/\s+/g, " ").trim();
  if (cleanLastSentence.length >= 12) {
    return cleanLastSentence.length > 320
      ? `${cleanLastSentence.slice(0, 320).trimEnd()}...`
      : cleanLastSentence;
  }

  const compactRaw = raw.replace(/\s+/g, " ").trim();
  if (compactRaw.length > 0) {
    return compactRaw.length > 320 ? `${compactRaw.slice(0, 320).trimEnd()}...` : compactRaw;
  }

  return fallbackText;
}

function inspectRenderedMessageText(triggerNode) {
  const root =
    triggerNode && typeof triggerNode.closest === "function" ? triggerNode.closest(".msg") : null;
  if (!root || typeof root.querySelector !== "function") {
    return {
      text: "",
      source: "no-root",
      epicPlainText: "",
      epicResponseText: "",
      rootText: "",
    };
  }
  const epicPlainNode = root.querySelector(".epic-plain");
  const epicResponseNode = root.querySelector(".epic-response-container");
  const epicPlainText = String(epicPlainNode?.innerText || epicPlainNode?.textContent || "").trim();
  const epicResponseText = String(
    epicResponseNode?.innerText || epicResponseNode?.textContent || ""
  ).trim();
  const rootText = String(root.innerText || root.textContent || "").trim();
  let source = "empty";
  let raw = "";
  if (epicPlainText) {
    source = "epic-plain";
    raw = epicPlainText;
  } else if (epicResponseText) {
    source = "epic-response-container";
    raw = epicResponseText;
  } else if (rootText) {
    source = "msg-root";
    raw = rootText;
  }
  const text = String(raw)
    .replace(/\s*·{3}\s*\(.*?\)\s*$/i, "")
    .trim()
    .replace(/^(Herói das Sombras|Exorcista Financeiro|Van Helsin IA)\s*:\s*/i, "")
    .replace(/\b(Só a conclusão|Just the conclusion|Solo la conclusión)\b\s*:?\s*$/i, "")
    .trim();
  return {
    text,
    source,
    epicPlainText,
    epicResponseText,
    rootText,
  };
}

function summarizeConclusionDebugText(value, maxLen = 160) {
  const compact = String(value || "").replace(/\s+/g, " ").trim();
  if (!compact) return "";
  return compact.length > maxLen ? `${compact.slice(0, maxLen).trimEnd()}...` : compact;
}

function emitShortConclusionDebug(eventName, payload = {}) {
  try {
    const entry = {
      ts: new Date().toISOString(),
      event: String(eventName || "").trim() || "unknown",
      ...payload,
    };
    if (typeof window !== "undefined") {
      const current = Array.isArray(window.__vhConclusionDebug) ? window.__vhConclusionDebug : [];
      const next = current.concat(entry).slice(-60);
      window.__vhConclusionDebug = next;
      window.__vhConclusionDebugLatest = entry;
    }
    if (typeof console !== "undefined" && typeof console.info === "function") {
      console.info("[VH_SHORT_CONCLUSION_DEBUG]", entry);
    }
  } catch {}
}

export function resolveSpecialAudioUid(boundUid) {
  const uid = String(boundUid || "").trim();
  return uid || SPECIAL_AUDIO_GUEST_UID;
}

export function getConversationStyleBiasStorageKey(conversationId) {
  const cid = String(conversationId || "").trim();
  return cid ? `${VH_CONVERSATION_STYLE_BIAS_PREFIX}${cid}` : "";
}

export function readPersistedConversationResponseStyleBias(conversationId, storage = null) {
  const key = getConversationStyleBiasStorageKey(conversationId);
  const store = storage || (typeof window !== "undefined" ? window.localStorage : null);
  if (!key || !store) return "default";
  try {
    const raw = String(store.getItem(key) || "").trim();
    return raw === "direct_preferred" ? "direct_preferred" : "default";
  } catch {
    return "default";
  }
}

export function writePersistedConversationResponseStyleBias(
  conversationId,
  bias,
  storage = null
) {
  const key = getConversationStyleBiasStorageKey(conversationId);
  const store = storage || (typeof window !== "undefined" ? window.localStorage : null);
  if (!key || !store) return;
  try {
    if (String(bias || "").trim() === "direct_preferred") {
      store.setItem(key, "direct_preferred");
    } else {
      store.removeItem(key);
    }
  } catch {}
}

export function clearPersistedConversationResponseStyleBias(conversationId, storage = null) {
  const key = getConversationStyleBiasStorageKey(conversationId);
  const store = storage || (typeof window !== "undefined" ? window.localStorage : null);
  if (!key || !store) return;
  try {
    store.removeItem(key);
  } catch {}
}

export function clearAllPersistedConversationResponseStyleBias(storage = null) {
  const store = storage || (typeof window !== "undefined" ? window.localStorage : null);
  if (!store) return;
  try {
    const keys = [];
    for (let i = 0; i < store.length; i += 1) {
      const key = String(store.key(i) || "");
      if (key.startsWith(VH_CONVERSATION_STYLE_BIAS_PREFIX)) {
        keys.push(key);
      }
    }
    keys.forEach((key) => store.removeItem(key));
  } catch {}
}

export function emitParableUiTelemetry(event, meta = {}) {
  try {
    if (typeof console === "undefined" || typeof console.info !== "function") return;
    const name = String(event || "").trim();
    if (!name) return;
    console.info("[VH-PARABLE-UI]", JSON.stringify({
      event: name,
      ts: Date.now(),
      ...((meta && typeof meta === "object") ? meta : {}),
    }));
  } catch {}
}

export function getRenderableParableTelemetryKeys(messages, conversationId = "") {
  const cid = String(conversationId || "").trim() || "session";
  const list = Array.isArray(messages) ? messages : [];
  return list.flatMap((message, index) => {
    if (!shouldShowParableContextControls(message)) return [];
    const assistantMessageId = String(message?.assistantMessageId || "").trim();
    return [`${cid}:${assistantMessageId || `idx:${index}`}`];
  });
}

export function hasValidUserMessageInThread(threadMessages) {
  if (!Array.isArray(threadMessages)) return true;
  return threadMessages.some(
    (message) =>
      String(message?.sender || "").trim().toLowerCase() === "user" &&
      String(message?.text || "").trim().length > 0
  );
}

export function buildQuickStartInputValue(currentInput, quickStartPrefix) {
  const prefix = String(quickStartPrefix || "").trim();
  const current = String(currentInput || "");
  if (!prefix) return current;
  const trimmedStart = current.trimStart();
  if (!trimmedStart) return `${prefix} `;
  if (trimmedStart.startsWith(prefix)) return current;
  return `${prefix} ${trimmedStart}`;
}

export function shouldShowQuickStartGuide({
  hasValidUserMessage,
  quickStartReopened,
  quickStartDismissed,
  showAnyLegalPage,
  showIntro,
  showCinematicIntro,
}) {
  if (showAnyLegalPage || showIntro || showCinematicIntro) return false;
  if (quickStartReopened) return true;
  if (quickStartDismissed) return false;
  return hasValidUserMessage === false;
}

export function shouldShowQuickStartReopen({
  hasValidUserMessage,
  quickStartVisible,
  quickStartDismissed,
  showAnyLegalPage,
  showIntro,
  showCinematicIntro,
}) {
  if (showAnyLegalPage || showIntro || showCinematicIntro) return false;
  if (quickStartVisible) return false;
  return hasValidUserMessage === true || quickStartDismissed === true;
}

export function shouldShowHunterSuggestionReopen({
  hunterSuggestionVisible,
  hasSuggestion,
  inputValue,
  hasUserStartedTyping,
  hunterSuggestionDismissed,
  showAnyLegalPage,
  showIntro,
  showCinematicIntro,
}) {
  if (showAnyLegalPage || showIntro || showCinematicIntro) return false;
  if (hunterSuggestionVisible) return false;
  if (!hasSuggestion) return false;
  if (String(inputValue || "").length > 0) return false;
  return hasUserStartedTyping === true || hunterSuggestionDismissed === true;
}

function genUuidV4() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {}
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function dragaoHomeCidKey() {
  const uid = String(getBoundUserId() || "").trim();
  if (!uid) return "";
  return `${DRAGAO_HOME_CID_PREFIX}${uid}`;
}

function readDragaoHomeCid() {
  try {
    const key = dragaoHomeCidKey();
    if (!key) return "";
    return String(localStorage.getItem(key) || "").trim();
  } catch {
    return "";
  }
}

export function shouldShowResetDirectPreferenceAction(messages, index, responseStyleBias) {
  return shouldShowDirectPreferenceAction(messages, index)
    && String(responseStyleBias || "").trim() === "direct_preferred";
}

function writeDragaoHomeCid(conversationId) {
  const cid = String(conversationId || "").trim();
  if (!cid) return;
  try {
    const key = dragaoHomeCidKey();
    if (!key) return;
    localStorage.setItem(key, cid);
  } catch {}
}

function normalizeFontScale(value) {
  const numeric = Number(value || 1);
  if (!Number.isFinite(numeric)) return 1;
  return Math.min(1.6, Math.max(0.9, numeric));
}

function readStoredFontScale() {
  try {
    const stored = localStorage.getItem(VH_FONT_SCALE_KEY);
    if (stored == null || String(stored).trim() === "") return 1.1;
    return normalizeFontScale(stored);
  } catch {
    return 1.1;
  }
}

function playDragaoUnlockChime() {
  if (!DRAGAO_CELEBRATION_SOUND_ENABLED) return;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const notes = [392, 523.25, 659.25, 784];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + i * 0.12);
      gain.gain.exponentialRampToValueAtTime(0.18, now + i * 0.12 + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.12 + 0.28);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + i * 0.12);
      osc.stop(now + i * 0.12 + 0.3);
    });
    window.setTimeout(() => {
      try {
        ctx.close();
      } catch {}
    }, 1200);
  } catch {}
}

function convT(lang, key) {
  const l = String(lang || "pt").toLowerCase();
  const dict = {
    pt: {
      historyTitle: "Historico de sessoes",
      newConversation: "Nova conversa",
      loading: "Carregando...",
      noConversations: "Nenhuma conversa salva.",
      open: "Abrir",
      deleteConv: "Apagar conversa",
      deleteAll: "Apagar todo o historico",
      deleteAllWarn: "Acao irreversivel. Digite APAGAR para confirmar.",
      confirmWord: "APAGAR",
      deleteSelected: "Apagar selecionadas",
      noMessages: "Sem mensagens nesta conversa.",
      sessionsManage: "Gerenciar historico de sessoes",
      sessionsBack: "Voltar ao painel",
      refreshList: "Atualizar",
      newConversationHint: "Limpar conversa e iniciar nova sessao",
      sessionsFail: "Falha ao carregar historico de sessoes.",
      conversationFail: "Falha ao abrir conversa.",
      deleteFail: "Falha ao apagar historico.",
    },
    en: {
      historyTitle: "Session history",
      newConversation: "New conversation",
      loading: "Loading...",
      noConversations: "No saved conversations.",
      open: "Open",
      deleteConv: "Delete conversation",
      deleteAll: "Delete all history",
      deleteAllWarn: "Irreversible action. Type DELETE to confirm.",
      confirmWord: "DELETE",
      deleteSelected: "Delete selected",
      noMessages: "No messages in this conversation.",
      sessionsManage: "Manage session history",
      sessionsBack: "Back to panel",
      refreshList: "Refresh",
      newConversationHint: "Clear conversation and start new session",
      sessionsFail: "Failed to load session history.",
      conversationFail: "Failed to open conversation.",
      deleteFail: "Failed to delete history.",
    },
    es: {
      historyTitle: "Historial de sesiones",
      newConversation: "Nueva conversacion",
      loading: "Cargando...",
      noConversations: "No hay conversaciones guardadas.",
      open: "Abrir",
      deleteConv: "Borrar conversacion",
      deleteAll: "Borrar todo el historial",
      deleteAllWarn: "Accion irreversible. Escriba BORRAR para confirmar.",
      confirmWord: "BORRAR",
      deleteSelected: "Borrar seleccionadas",
      noMessages: "Sin mensajes en esta conversacion.",
      sessionsManage: "Gestionar historial de sesiones",
      sessionsBack: "Volver al panel",
      refreshList: "Actualizar",
      newConversationHint: "Borrar conversacion e iniciar nueva sesion",
      sessionsFail: "Error al cargar historial de sesiones.",
      conversationFail: "Error al abrir conversacion.",
      deleteFail: "Error al borrar historial.",
    },
  };
  const langDict = dict[l] || dict.pt;
  return langDict[key] || dict.pt[key] || key;
}

function normalizeConversationMessages(items) {
  const list = Array.isArray(items) ? items : [];
  return list.map((item, idx) => {
    const role = String(item?.role || "").toLowerCase();
    const sender = role === "user" ? "user" : role === "assistant" ? "ia" : "ia";
    const mode = sender === "ia" ? "heroi" : undefined;
    return {
      id: String(item?.id || `msg-${idx}`),
      text: String(item?.content || ""),
      sender,
      mode,
      createdAt: Number(item?.created_at || 0),
      role,
      usedParable: item?.used_parable === true,
      assistantMessageId:
        sender === "ia" && typeof item?.assistant_message_id === "string"
          ? String(item.assistant_message_id || "").trim()
          : "",
    };
  });
}

function normalizeDragaoThreadMessages(items) {
  const list = Array.isArray(items) ? items : [];
  return list.map((item, idx) => {
    const role = String(item?.role || "").toLowerCase();
    const sender = role === "user" ? "user" : "ia";
    return {
      id: String(item?.id || `local-dragao-${idx}`),
      text: String(item?.content || ""),
      sender,
      mode: "heroi",
      createdAt: 0,
      role,
    };
  });
}

function detectLegalPageLang() {
  try {
    const queryLang = new URLSearchParams(window.location.search).get("lang") || "";
    const code = (queryLang.match(/^(pt|en|es)(-|$)/i) || [])[1]?.toLowerCase();
    if (code) return code;
  } catch {}
  try {
    const stored = sessionStorage.getItem("vh_ui_lang") || "";
    if (stored.startsWith("pt")) return "pt";
    if (stored.startsWith("es")) return "es";
    if (stored.startsWith("en")) return "en";
  } catch {}
  return getLegalLangFromBrowser();
}

export function isLegalOrInstitutionalPath(path) {
  return LEGAL_ROUTE_SET.has(normalizePath(path));
}

export function isValidInternalReturnRoute(path) {
  const p = normalizePath(path);
  if (p === MAIN_AI_RETURN_SENTINEL) return true;
  if (isLegalOrInstitutionalPath(p)) return false;
  return p.startsWith("/chatrooms");
}

export function resolveLegalReturnPath(lastNonLegalRoute) {
  const p = normalizePath(lastNonLegalRoute);
  if (p === MAIN_AI_RETURN_SENTINEL) return "/";
  return isValidInternalReturnRoute(p) ? p : "/";
}

function LegalPageBar({ lang, currentPage, onLangChange, onNavigate, returnPath = "/", returnTarget = "/" }) {
  const nav = onNavigate || ((p) => { window.location.href = p; });
  const handleBack = (e) => {
    e.preventDefault();
    nav(returnTarget);
  };
  return (
    <div className="legal-top-bar">
      <a className="legal-bar-back" href={returnPath} onClick={handleBack}>
        ← {tr(lang, "backToChat")}
      </a>
      <nav className="legal-bar-nav">
        <a href="/legal" className={currentPage === "legal" ? "legal-bar-link active" : "legal-bar-link"} onClick={(e) => { e.preventDefault(); nav("/legal"); }}>
          {tr(lang, "legalNotice")}
        </a>
        <span className="legal-bar-sep">|</span>
        <a href="/privacy" className={currentPage === "privacy" ? "legal-bar-link active" : "legal-bar-link"} onClick={(e) => { e.preventDefault(); nav("/privacy"); }}>
          {tr(lang, "privacyPolicy")}
        </a>
        <span className="legal-bar-sep">|</span>
        <a href="/terms" className={currentPage === "terms" ? "legal-bar-link active" : "legal-bar-link"} onClick={(e) => { e.preventDefault(); nav("/terms"); }}>
          {tr(lang, "termsOfService")}
        </a>
        <span className="legal-bar-sep">|</span>
        <a href="/pricing" className={currentPage === "pricing" ? "legal-bar-link active" : "legal-bar-link"} onClick={(e) => { e.preventDefault(); nav("/pricing"); }}>
          {tr(lang, "pricing")}
        </a>
        <span className="legal-bar-sep">|</span>
        <a href="/refund-policy" className={currentPage === "refund-policy" ? "legal-bar-link active" : "legal-bar-link"} onClick={(e) => { e.preventDefault(); nav("/refund-policy"); }}>
          {tr(lang, "refunds")}
        </a>
        <span className="legal-bar-sep">|</span>
        <a href="/data-request" className={currentPage === "data-request" ? "legal-bar-link active" : "legal-bar-link"} onClick={(e) => { e.preventDefault(); nav("/data-request"); }}>
          {tr(lang, "dataRequest")}
        </a>
        <span className="legal-bar-sep">|</span>
        <a href="/about" className={currentPage === "about" ? "legal-bar-link active" : "legal-bar-link"} onClick={(e) => { e.preventDefault(); nav("/about"); }}>
          {tr(lang, "about")}
        </a>
        <span className="legal-bar-sep">|</span>
        <a href="/arquivo" className={currentPage === "arquivo" ? "legal-bar-link active" : "legal-bar-link"} onClick={(e) => { e.preventDefault(); nav("/arquivo"); }}>
          {tr(lang, "confidentialFile")}
        </a>
      </nav>
      {onLangChange && (
        <div className="legal-bar-lang">
          {LANG_OPTIONS.map((opt) => (
            <button
              key={opt.code}
              type="button"
              className={`legal-bar-lang-btn ${lang === opt.code ? "active" : ""}`}
              onClick={() => onLangChange(opt.code)}
              title={opt.label}
            >
              {opt.flag}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LegalStaticPage({ onLangChange, onNavigate, returnPath, returnTarget }) {
  const lang = detectLegalPageLang();
  const content = lang === "pt" ? LEGAL_PT : lang === "es" ? LEGAL_ES : LEGAL_EN;
  return (
    <div className="legal-page-wrapper gothic-text">
      <LegalPageBar lang={lang} currentPage="legal" onLangChange={onLangChange} onNavigate={onNavigate} returnPath={returnPath} returnTarget={returnTarget} />
      <div className="legal-page-scroll">
        <div className="legal-page">
          <h1>{content.title}</h1>
          {content.sections.map((s, i) =>
            Array.isArray(s.p) ? (
              <section key={i}>
                <p><strong>{s.h}:</strong></p>
                {s.p.map((para, j) => <p key={j}>{para}</p>)}
              </section>
            ) : (
              <p key={i}><strong>{s.h}:</strong> {s.p}</p>
            )
          )}
          {content.integrationNote ? <p>{content.integrationNote}</p> : null}
          <InstitutionalCompanyBlock lang={lang} />
        </div>
      </div>
    </div>
  );
}

function renderOfficialContactText(text) {
  const raw = String(text || "");
  const emailIndex = raw.indexOf(CONTACT_EMAIL);
  if (emailIndex < 0) return raw;
  const before = raw.slice(0, emailIndex);
  const after = raw.slice(emailIndex + CONTACT_EMAIL.length);
  return (
    <>
      {before}
      <a href={CONTACT_HREF}>{CONTACT_EMAIL}</a>
      {after}
    </>
  );
}

function getInstitutionalCompanyLines(lang) {
  if (lang === "pt") {
    return {
      operatedBy: "Operado por Van Helsin IA Ltda.",
      registered: "Empresa registrada no Brasil.",
      supportLabel: "Suporte:",
    };
  }
  if (lang === "es") {
    return {
      operatedBy: "Operado por Van Helsin IA Ltda.",
      registered: "Empresa registrada en Brasil.",
      supportLabel: "Soporte:",
    };
  }
  return {
    operatedBy: "Operated by Van Helsin IA Ltda.",
    registered: "Registered company in Brazil.",
    supportLabel: "Support:",
  };
}

function InstitutionalCompanyBlock({ lang, compact = false }) {
  const c = getInstitutionalCompanyLines(lang);
  if (compact) {
    return (
      <>
        {c.operatedBy}
        {" · "}
        {c.registered}
        {" · "}
        {c.supportLabel} <a href={CONTACT_HREF}>{CONTACT_EMAIL}</a>
      </>
    );
  }
  return (
    <div className="legal-data-request">
      <p>{c.operatedBy}</p>
      <p>{c.registered}</p>
      <p>{c.supportLabel} <a href={CONTACT_HREF}>{CONTACT_EMAIL}</a></p>
    </div>
  );
}

function AudiobookOfferCard({
  lang,
  audiobookOffer,
  audiobookBusy,
  interactionLocked = false,
  onAccept,
  onClose,
}) {
  if (!audiobookOffer || interactionLocked) return null;
  return (
    <div className="audiobook-offer-card">
      <div className="audiobook-offer-title">
        {tr(lang, "audiobookSuggested")}: {audiobookOffer.title || audiobookOffer.audio_id}
      </div>
      <div className="audiobook-offer-actions">
        <button
          type="button"
          className="btn gothic-btn"
          onClick={() => onAccept("en")}
          disabled={audiobookBusy || interactionLocked}
        >
          English
        </button>
        <button
          type="button"
          className="btn gothic-btn"
          onClick={() => onAccept("es")}
          disabled={audiobookBusy || interactionLocked}
        >
          Español
        </button>
        <button
          type="button"
          className="btn gothic-btn"
          onClick={() => onAccept("pt")}
          disabled={audiobookBusy || interactionLocked}
        >
          Português
        </button>
        <button
          type="button"
          className="btn gothic-btn"
          onClick={onClose}
          disabled={audiobookBusy || interactionLocked}
        >
          {tr(lang, "notNow")}
        </button>
      </div>
    </div>
  );
}

function IntroGateNotice({
  currentLang,
  aiConsentChecked,
  setAiConsentChecked,
  termsEnforced,
  termsAccepted,
  termsChecked,
  setTermsChecked,
  termsBusy,
  termsError,
  navigateTo,
  handleLangChange,
  handleAcceptTermsGate,
  handleDismissIntroWarning,
  showAccessRecoverCta,
  onAccessRecoverClick,
}) {
  return (
    <div className="vh-intro-gate-fixed" role="alert" aria-live="assertive">
      <div className="vh-intro-gate-shell">
      <div className="vh-intro-frame">
        <div className="vh-intro-lang-switch" role="group" aria-label={currentLang === "pt" ? "Idioma do aviso" : currentLang === "es" ? "Idioma del aviso" : "Notice language"}>
          {LANG_OPTIONS.map((opt) => (
            <button
              key={`intro-lang-${opt.code}`}
              type="button"
              className={`vh-intro-lang-btn ${currentLang === opt.code ? "active" : ""}`}
              onClick={() => handleLangChange(opt.code)}
              aria-pressed={currentLang === opt.code}
            >
              {String(opt.code || "").toUpperCase()}
            </button>
          ))}
        </div>
        <div className="vh-intro-sigil" aria-hidden="true">✠</div>

        <div className="vh-intro-title">
          {currentLang === "pt" ? "ATENÇÃO" : currentLang === "es" ? "ATENCIÓN" : "NOTICE"}
        </div>

        <div className="vh-intro-text">
          {LEGAL_COPY_V2 ? (
            <>
              <strong>
                {currentLang === "pt"
                  ? "Experiência narrativa simbólica e ficcional."
                  : currentLang === "es"
                  ? "Experiencia narrativa simbólica y ficticia."
                  : "Symbolic fictional narrative experience."}
              </strong>
              <br /><br />
              {currentLang === "pt"
                ? "Não use para decisões médicas, psicológicas, jurídicas, financeiras ou pessoais."
                : currentLang === "es"
                ? "No use para decisiones médicas, psicológicas, jurídicas, financieras o personales."
                : "Do not use for medical, psychological, legal, financial or personal decisions."}
              <br /><br />
              {currentLang === "pt"
                ? "Crise ou risco imediato pede presença humana — serviços de emergência existem para isso."
                : currentLang === "es"
                ? "Crisis o riesgo inmediato requiere presencia humana — los servicios de emergencia existen para ello."
                : "Crisis or immediate risk requires human presence — emergency services exist for that."}
              <br /><br />
              {currentLang === "pt"
                ? "Ao prosseguir, você entende que é ficção interpretativa, sem autoridade."
                : currentLang === "es"
                ? "Al continuar, usted entiende que es ficción interpretativa, sin autoridad."
                : "By proceeding, you understand this is interpretive fiction, without authority."}
              <br /><br />
              {currentLang === "pt" ? (
                <>
                  Consulte o <a href="/legal" onClick={(e) => { e.preventDefault(); navigateTo("/legal"); }}>Aviso Legal completo</a> para mais detalhes.
                </>
              ) : currentLang === "es" ? (
                <>
                  Consulte el <a href="/legal" onClick={(e) => { e.preventDefault(); navigateTo("/legal"); }}>Aviso Legal completo</a> para más detalles.
                </>
              ) : (
                <>
                  See the full <a href="/legal" onClick={(e) => { e.preventDefault(); navigateTo("/legal"); }}>Legal Notice</a> for more details.
                </>
              )}
            </>
          ) : (
            <>
              <strong>
                {currentLang === "pt" ? "Aviso inicial." : currentLang === "es" ? "Aviso inicial." : "Initial notice."}
              </strong>
              <br /><br />
              {currentLang === "pt" ? "Conteúdo narrativo e ficcional." : currentLang === "es" ? "Contenido narrativo y ficticio." : "Narrative and fictional content."}
            </>
          )}
        </div>

        <div className="vh-terms-gate" style={{ marginTop: "10px" }}>
          <label className="vh-terms-checkbox">
            <input
              type="checkbox"
              checked={aiConsentChecked}
              onChange={(e) => setAiConsentChecked(e.target.checked)}
              disabled={termsBusy}
            />
            <span>{tr(currentLang, "checkboxLabel")}</span>
          </label>
        </div>

        {termsEnforced && !termsAccepted ? (
          <div className="vh-terms-gate">
            <label className="vh-terms-checkbox">
              <input
                type="checkbox"
                checked={termsChecked}
                onChange={(e) => setTermsChecked(e.target.checked)}
                disabled={termsBusy}
              />
              <span>
                {currentLang === "pt" ? (
                  <>
                    Declaro que li e concordo com o{" "}
                    <a href="/legal" onClick={(e) => { e.preventDefault(); navigateTo("/legal"); }}>
                      Aviso Legal
                    </a>
                    {" "}e com os{" "}
                    <a href="/terms" onClick={(e) => { e.preventDefault(); navigateTo("/terms"); }}>
                      Termos de Uso
                    </a>.
                  </>
                ) : currentLang === "es" ? (
                  <>
                    Declaro que he leído y acepto el{" "}
                    <a href="/legal" onClick={(e) => { e.preventDefault(); navigateTo("/legal"); }}>
                      Aviso Legal
                    </a>
                    {" "}y los{" "}
                    <a href="/terms" onClick={(e) => { e.preventDefault(); navigateTo("/terms"); }}>
                      Términos de Uso
                    </a>.
                  </>
                ) : (
                  <>
                    I declare that I have read and agree to the{" "}
                    <a href="/legal" onClick={(e) => { e.preventDefault(); navigateTo("/legal"); }}>
                      Legal Notice
                    </a>
                    {" "}and the{" "}
                    <a href="/terms" onClick={(e) => { e.preventDefault(); navigateTo("/terms"); }}>
                      Terms of Service
                    </a>.
                  </>
                )}
              </span>
            </label>
            {termsError ? <div className="vh-terms-error">{termsError}</div> : null}
          </div>
        ) : termsError ? <div className="vh-terms-error">{termsError}</div> : null}

        <div className="vh-intro-hint">
          {currentLang === "pt"
            ? "O aviso desaparece ao confirmar o aceite e seguir para o chat."
            : currentLang === "es"
            ? "El aviso desaparece al confirmar la aceptación y seguir al chat."
            : "The notice disappears when you confirm and proceed to chat."}
        </div>
        {showAccessRecoverCta ? (
          <LoginAccessRecoverBridge
            lang={currentLang}
            onRecover={onAccessRecoverClick}
            introSurface
          />
        ) : null}
        <button
          type="button"
          className="vh-intro-dismiss"
          onClick={
            termsEnforced && !termsAccepted
              ? handleAcceptTermsGate
              : handleDismissIntroWarning
          }
          disabled={termsBusy}
        >
          {termsEnforced && !termsAccepted
            ? (termsBusy
              ? (currentLang === "pt" ? "Registrando..." : currentLang === "es" ? "Registrando..." : "Registering...")
              : (currentLang === "pt" ? "Prosseguir na narrativa" : currentLang === "es" ? "Continuar en la narrativa" : "Proceed to narrative"))
            : (currentLang === "pt" ? "Prosseguir na narrativa" : currentLang === "es" ? "Continuar en la narrativa" : "Proceed to narrative")}
        </button>
      </div>
      </div>
    </div>
  );
}

function PrivacyStaticPage({ onLangChange, onNavigate, returnPath, returnTarget }) {
  const lang = detectLegalPageLang();
  const c = lang === "pt" ? PRIVACY_PT : lang === "es" ? PRIVACY_ES : PRIVACY_EN;
  return (
    <div className="legal-page-wrapper gothic-text">
      <LegalPageBar lang={lang} currentPage="privacy" onLangChange={onLangChange} onNavigate={onNavigate} returnPath={returnPath} returnTarget={returnTarget} />
      <div className="legal-page-scroll">
        <div className="legal-page">
          <h1>{c.title}</h1>
          <p><strong>{c.controller}:</strong> {c.controllerDesc}</p>
          <section>
            <p><strong>{c.legalBasisSectionTitle || c.legalBasis}</strong></p>
            <ul>
              {(c.legalBasisItems || [c.legalBasisDesc]).map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </section>
          <p><strong>{c.retention}:</strong> {c.retentionDesc}</p>
          <p><strong>{c.rights}:</strong> {c.rightsDesc}</p>
          <p><strong>{c.transfer}:</strong> {c.transferDesc}</p>
          <p><strong>{c.contact}:</strong> {renderOfficialContactText(c.contactDesc)}</p>
          {c.dataRequestNote && <p className="legal-data-request">{c.dataRequestNote}</p>}
          <InstitutionalCompanyBlock lang={lang} />
        </div>
      </div>
    </div>
  );
}

function TermsStaticPage({ onLangChange, onNavigate, returnPath, returnTarget }) {
  const lang = detectLegalPageLang();
  const content = lang === "pt" ? TERMS_PT : lang === "es" ? TERMS_ES : TERMS_EN;
  return (
    <div className="legal-page-wrapper gothic-text">
      <LegalPageBar lang={lang} currentPage="terms" onLangChange={onLangChange} onNavigate={onNavigate} returnPath={returnPath} returnTarget={returnTarget} />
      <div className="legal-page-scroll">
        <div className="legal-page">
          <h1>{content.title}</h1>
          {content.sections.map((s, i) => (
            <p key={i}><strong>{s.h}:</strong> {s.p}</p>
          ))}
          {content.brWithdrawal && (
            <section className="legal-br-withdrawal">
              <h2>{content.brWithdrawal.title}</h2>
              <p>{content.brWithdrawal.body}</p>
            </section>
          )}
          {content.euWithdrawal && (
            <section className="legal-eu-withdrawal">
              <h2>{content.euWithdrawal.title}</h2>
              <p>{content.euWithdrawal.body}</p>
              <p className="legal-checkbox-text"><strong>{tr(lang, "mandatoryCheckbox")}:</strong> &ldquo;{content.euWithdrawalCheckbox}&rdquo;</p>
            </section>
          )}
          <InstitutionalCompanyBlock lang={lang} />
        </div>
      </div>
    </div>
  );
}

function PricingStaticPage({ onLangChange, onNavigate, returnPath, returnTarget }) {
  const lang = detectLegalPageLang();
  const content = lang === "pt" ? PRICING_PT : lang === "es" ? PRICING_ES : PRICING_EN;
  return (
    <div className="legal-page-wrapper gothic-text">
      <LegalPageBar lang={lang} currentPage="pricing" onLangChange={onLangChange} onNavigate={onNavigate} returnPath={returnPath} returnTarget={returnTarget} />
      <div className="legal-page-scroll">
        <div className="legal-page">
          <h1>{content.title}</h1>
          <p>{content.intro}</p>
          {content.plans.map((plan) => (
            <section key={plan.name} className="legal-pricing-plan">
              <p><strong>{plan.name}:</strong> {plan.description}</p>
              <ul>
                {plan.features.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
              <p><strong>{plan.cta}</strong> — {plan.note}</p>
            </section>
          ))}
          <p className="legal-data-request">{content.closing}</p>
          <InstitutionalCompanyBlock lang={lang} />
        </div>
      </div>
    </div>
  );
}

function RefundPolicyStaticPage({ onLangChange, onNavigate, returnPath, returnTarget }) {
  const lang = detectLegalPageLang();
  const content = lang === "pt" ? REFUND_PT : lang === "es" ? REFUND_ES : REFUND_EN;
  return (
    <div className="legal-page-wrapper gothic-text">
      <LegalPageBar lang={lang} currentPage="refund-policy" onLangChange={onLangChange} onNavigate={onNavigate} returnPath={returnPath} returnTarget={returnTarget} />
      <div className="legal-page-scroll">
        <div className="legal-page">
          <h1>{content.title}</h1>
          <p>{content.intro}</p>
          {content.sections.map((s, i) => (
            <p key={i}><strong>{s.h}:</strong> {s.p}</p>
          ))}
          <InstitutionalCompanyBlock lang={lang} />
        </div>
      </div>
    </div>
  );
}

function AboutStaticPage({ onLangChange, onNavigate, returnPath, returnTarget }) {
  const lang = detectLegalPageLang();
  const c = lang === "pt" ? ABOUT_PT : lang === "es" ? ABOUT_ES : ABOUT_EN;
  return (
    <div className="legal-page-wrapper gothic-text">
      <LegalPageBar lang={lang} currentPage="about" onLangChange={onLangChange} onNavigate={onNavigate} returnPath={returnPath} returnTarget={returnTarget} />
      <div className="legal-page-scroll">
        <div className="legal-page about-page">
          <h1 className="about-title">{c.title}</h1>
          <h2 className="about-subtitle">{c.subtitle}</h2>
          <div className="about-orb-wrap">
            <img src="/fotos/vanhelsinorb.png" alt="Van Helsin" className="about-orb-img" />
          </div>
          <div className="about-intro">
            {c.intro.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
          {c.sections.map((sec, i) => (
            <section key={i} className="about-section">
              <h3 className="about-section-title">{sec.title}</h3>
              {sec.paragraphs.map((para, j) => (
                <p key={j}>{para}</p>
              ))}
            </section>
          ))}
          {c.institutionalBlock && (
            <section className="about-institutional">
              {c.institutionalBlock.map((line, i) => (
                <React.Fragment key={i}>
                  <p className={`about-institutional-p${i === 0 ? " about-institutional-core" : ""}`}>{line}</p>
                  {i === 0 && (
                    <div className="about-orb-wrap">
                      <img src="/fotos/orb.png" alt="Van Helsin C.O.R.E" className="about-orb-img" />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function DataRequestStaticPage({ onLangChange, onNavigate, returnPath, returnTarget }) {
  const lang = detectLegalPageLang();
  const content = lang === "pt" ? DATA_REQUEST_PT : lang === "es" ? DATA_REQUEST_ES : DATA_REQUEST_EN;

  return (
    <div className="legal-page-wrapper gothic-text">
      <LegalPageBar lang={lang} currentPage="data-request" onLangChange={onLangChange} onNavigate={onNavigate} returnPath={returnPath} returnTarget={returnTarget} />
      <div className="legal-page-scroll">
        <div className="legal-page">
          <h1>{content.title}</h1>
          <p>{content.intro}</p>
          <section style={{ marginTop: "20px" }}>
            <p><strong>{content.selfServiceTitle}</strong></p>
            <ul>
              {content.selfServiceItems.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </section>
          <p style={{ marginTop: "18px" }}><strong>{content.memoryPanelLabel}:</strong> {content.memoryPanelDesc}</p>
          <p style={{ marginTop: "18px" }}>{content.legalNote}</p>
          <p className="legal-data-request" style={{ marginTop: "18px" }}>{renderOfficialContactText(content.contactFallback)}</p>
        </div>
      </div>
    </div>
  );
}

function ArquivoConfidencialPage({ onLangChange, onNavigate, returnPath, returnTarget }) {
  const lang = detectLegalPageLang();
  const content = lang === "pt" ? ARQUIVO_PT : lang === "es" ? ARQUIVO_ES : ARQUIVO_EN;

  return (
    <div className="legal-page-wrapper gothic-text arquivo-confidencial-wrapper">
      <LegalPageBar lang={lang} currentPage="arquivo" onLangChange={onLangChange} onNavigate={onNavigate} returnPath={returnPath} returnTarget={returnTarget} />
      <div className="legal-page-scroll">
        <div className="legal-page arquivo-confidencial-page">
          <div className="arquivo-meta">
            <div className="arquivo-meta-line">{content.classificationLabel}: {content.classificationValue}</div>
            <div className="arquivo-meta-line">{content.protocolLabel}: {content.protocolValue}</div>
            <div className="arquivo-meta-line">{content.accessLabel}: {content.accessValue}</div>
            <div className="arquivo-meta-line">{content.leakStatusLabel}: {content.leakStatusValue}</div>
          </div>
          <h1 className="arquivo-h1">{content.title}</h1>
          <h2 className="arquivo-h2">{content.subtitle}</h2>
          <hr className="arquivo-divider" />
          {content.entities.map((ent) => (
            <article key={ent.id} className="arquivo-entity-card">
              <div className="arquivo-entity-content">
                {ent.image ? (
                  <div className="arquivo-entity-img-wrap">
                    <img src={ent.image} alt={ent.name} className="arquivo-entity-img" onError={(e) => { e.target.style.display = "none"; e.target.nextSibling?.classList?.add("visible"); }} />
                    <div className="arquivo-entity-img-placeholder" />
                  </div>
                ) : (
                  <div className="arquivo-entity-img-placeholder visible" />
                )}
                <div className="arquivo-entity-dossier">
                  <h3 className="arquivo-entity-name">{ent.name}{ent.alias ? ` (${ent.alias})` : ""}</h3>
                  <div className="arquivo-entity-tags">
                    {ent.tags.map((t) => (
                      <span key={t} className="arquivo-tag">{t}</span>
                    ))}
                  </div>
                  {ent.sections.map((sec, i) => (
                    <div key={i} className="arquivo-section">
                      <div className="arquivo-section-heading">{sec.heading}:</div>
                      {sec.lines.map((ln, j) => (
                        <div key={j} className="arquivo-line">{ln}</div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </article>
          ))}
          <p className="arquivo-nota">{content.notaInstitucional}</p>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [messages, setMessages] = useState([]);
  const [activeMode, setActiveMode] = useState("normal");
  const [normalMessages, setNormalMessages] = useState([]);
  const [dragaoMessages, setDragaoMessages] = useState([]);
  const [input, setInput] = useState("");
  const [responseStyleBiasByScope, setResponseStyleBiasByScope] = useState({});
  const [selectedHunterSuggestion, setSelectedHunterSuggestion] = useState("");
  const [quickStartReopened, setQuickStartReopened] = useState(false);
  const [quickStartDismissed, setQuickStartDismissed] = useState(false);
  const [hunterSuggestionReopened, setHunterSuggestionReopened] = useState(false);
  const [hunterSuggestionDismissed, setHunterSuggestionDismissed] = useState(false);
  const [shortConclusionByMessageId, setShortConclusionByMessageId] = useState({});
  const [shortConclusionLoadingId, setShortConclusionLoadingId] = useState("");
  const [expandedShortConclusionByMessageId, setExpandedShortConclusionByMessageId] = useState({});
  const [hasUserStartedTyping, setHasUserStartedTyping] = useState(false);
  const [currentMode, setCurrentMode] = useState("heroi");
  const [playerMode, setPlayerMode] = useState("bgm");
  const [musicaAtual, setMusicaAtual] = useState(null);
  const [audiobookSrc, setAudiobookSrc] = useState(null);
  const [audiobookOffer, setAudiobookOffer] = useState(null);
  const [audiobookBusy, setAudiobookBusy] = useState(false);
  const [specialAudioState, setSpecialAudioState] = useState(() =>
    createDefaultSpecialAudioState()
  );
  const [specialAudioHydrated, setSpecialAudioHydrated] = useState(false);
  const [isAudiobookPaused, setIsAudiobookPaused] = useState(false);
  const [limiteAtingido, setLimiteAtingido] = useState(false);
  const [showCinematicIntro, setShowCinematicIntro] = useState(true);
  const [showIntroByMemory, setShowIntroByMemory] = useState(false);
  const [introDismissed, setIntroDismissed] = useState(false);
  const [introGateResolved, setIntroGateResolved] = useState(false);
  const [fadeIntro, setFadeIntro] = useState(false);
  const [musicaIniciada, setMusicaIniciada] = useState(false);
  const [invokeClicked, setInvokeClicked] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [showMemory, setShowMemory] = useState(false);
  const [musicStopped, setMusicStopped] = useState(false);
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const [hasMusicHistory, setHasMusicHistory] = useState(false);
  const [memoryContent, setMemoryContent] = useState("");
  const [loadingMemory, setLoadingMemory] = useState(false);
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [memoryStatus, setMemoryStatus] = useState(null);
  const [keepLastN, setKeepLastN] = useState(10);
  const [showMemoryList, setShowMemoryList] = useState(false);
  const [memoryListItems, setMemoryListItems] = useState([]);
  const [showSessionsMemoryPanel, setShowSessionsMemoryPanel] = useState(false);
  const [expandedMemoryIndex, setExpandedMemoryIndex] = useState(null);
  const [conversationsAvailable, setConversationsAvailable] = useState(CONVERSATIONS_UI_ENABLED);
  const [conversationsDeleteEnabled, setConversationsDeleteEnabled] = useState(
    CONVERSATIONS_DELETE_UI_ENABLED
  );
  const [conversationsSummaryEnabled, setConversationsSummaryEnabled] = useState(false);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [conversationsError, setConversationsError] = useState("");
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState("");
  const [conversationSelection, setConversationSelection] = useState({});
  const [loadingConversationMessages, setLoadingConversationMessages] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState("");
  const [deletionBusy, setDeletionBusy] = useState(false);
  const [identityCardAvailable, setIdentityCardAvailable] = useState(false);
  const [identityCardDraft, setIdentityCardDraft] = useState({ tom: "default", formato: "default", evitar_frases: [], nivel_detalhe: "default" });
  const [vhRachelEnabled, setVhRachelEnabled] = useState(() => {
    try {
      const v = localStorage.getItem("vh_rachel_enabled");
      if (v === "1" || String(v || "").toLowerCase() === "true") return true;
      return false;
    } catch {
      return false;
    }
  });
  const [visualState, setVisualState] = useState(null);
  const [hasClimateVisualStarted, setHasClimateVisualStarted] = useState(false);
  const [showLadyLucroVideo, setShowLadyLucroVideo] = useState(false);
  const ladyLucroPlayCountRef = useRef(0);
  const ladyLucroVideoRef = useRef(null);
  const ladyLucroVisualStateRef = useRef(null);
  const [collapsed, setCollapsed] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [thinkingLine, setThinkingLine] = useState("");
  const [voiceLang, _setVoiceLang] = useState("pt-BR");
  const setVoiceLang = useCallback((lang) => {
    _setVoiceLang(lang);
    try { sessionStorage.setItem("vh_ui_lang", lang); } catch {}
  }, []);
  const [isRecording, setIsRecording] = useState(false);
  const [interimVoiceText, setInterimVoiceText] = useState("");
  const [geoBlocked, setGeoBlocked] = useState(false);
  const [geoBlockedCountry, setGeoBlockedCountry] = useState("");
  const [geoBlockedMessage, setGeoBlockedMessage] = useState("");
  const [bootstrapPending, setBootstrapPending] = useState(true);
  const [termsEnforced, setTermsEnforced] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(true);
  const [termsChecked, setTermsChecked] = useState(false);
  const [termsVersion, setTermsVersion] = useState("");
  const [termsBusy, setTermsBusy] = useState(false);
  const [termsError, setTermsError] = useState("");
  const [uiLang, setUiLang] = useState(() => detectUiLang());
  const [fontScale, setFontScale] = useState(() => readStoredFontScale());
  const [aiConsentChecked, setAiConsentChecked] = useState(false);
  const [showEuConsentModal, setShowEuConsentModal] = useState(false);
  const [euConsentTermsChecked, setEuConsentTermsChecked] = useState(false);
  const [euConsentWithdrawalChecked, setEuConsentWithdrawalChecked] = useState(false);
  const [pendingCheckoutInterval] = useState("month");
  const [dragaoFeatureEnabled, setDragaoFeatureEnabled] = useState(false);
  const [dragaoActive, setDragaoActive] = useState(false);
  const [dragaoState, setDragaoState] = useState(null);
  const [dragaoQuestions, setDragaoQuestions] = useState(null);
  const [dragaoRecentLog, setDragaoRecentLog] = useState([]);
  const [dragaoModalOpen, setDragaoModalOpen] = useState(false);
  const [dragaoAgeChecked, setDragaoAgeChecked] = useState(false);
  const [dragaoTermsChecked, setDragaoTermsChecked] = useState(false);
  const [dragaoBusy, setDragaoBusy] = useState(false);
  const [dragaoError, setDragaoError] = useState("");
  const [dragaoHomeConversationId, setDragaoHomeConversationId] = useState("");
  const [dragaoCelebration, setDragaoCelebration] = useState(null);
  const [dragaoIdentifyName, setDragaoIdentifyName] = useState("");
  const [dragaoIdentifyWhy, setDragaoIdentifyWhy] = useState("");
  const [dragaoIdentifyCosts, setDragaoIdentifyCosts] = useState("");
  const [dragaoBaselineForm, setDragaoBaselineForm] = useState({
    cedeu_7d: "",
    gatilho_principal: "",
    controle_0_10: "",
    dano_concreto: "",
    resistencia_tentada: "",
    acao_hoje: "",
    p1: "",
    p2: "",
    p3: "",
    p4: "",
    p5: "",
  });
  const [dragaoCheckinForm, setDragaoCheckinForm] = useState({
    result: "parcial",
    times: "",
    trigger: "",
    control_0_10: "",
    damage: "",
    action_executed: "",
    action_done: false,
    objective_metric: "",
    barrier_applied: "",
    repeat_count: "1",
  });
  const [dragaoArmorFlash, setDragaoArmorFlash] = useState({});
  const dragaoArmorTimersRef = useRef({});
  const dragaoArmorPrevStateRef = useRef(null);
  const dragaoArmorConversationRef = useRef("");
  const dragaoCelebrationTimerRef = useRef(null);
  const chatEndRef = useRef(null);
  const audioRef = useRef(null);
  const audiobookRef = useRef(null);
  const audiobookAutoplayRequestedRef = useRef(false);
  const pendingBgmTrackAfterAudiobookRef = useRef("");
  const audiobookQueueRef = useRef([]);
  const currentAudiobookOfferRef = useRef(null);
  const specialAudioStateRef = useRef(createDefaultSpecialAudioState());
  const specialAudioUidRef = useRef("");
  const emittedParableBadgeTelemetryRef = useRef(new Set());
  const videoRef = useRef(null);
  const introInvokeDelayTimerRef = useRef(null);
  const introEndCapTimerRef = useRef(null);
  const inputRef = useRef(null);
  const scopedMessagesForUi =
    activeMode === "dragao" ? dragaoMessages : normalMessages;
  const hasValidUserMessage = hasValidUserMessageInThread(scopedMessagesForUi);
  const recognizerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const waitBridgeTimerRef = useRef(null);
  const waitBridgeAbortRef = useRef(null);
  const waitBridgeTriggeredRef = useRef(false);
  const bridgeFrameRef = useRef(null);
  const waitBridgeVisibleLoggedRef = useRef(false);
  const waitBridgePersistRef = useRef({ requestId: "", text: "" });
  const turnStateRef = useRef({ requestId: "", mainCompleted: false });
  const [bridgeState, setBridgeState] = useState(null);
  const musicRetryTimeoutRef = useRef(null);
  const musicStoppedRef = useRef(false);
  const failedTracksRef = useRef(new Map());
  const isSwitchingTrackRef = useRef(false);
  const lastTrackSwitchAtRef = useRef(0);
  const voicePausedMusicRef = useRef(false);
  const musicHistoryRef = useRef([]);
  const wasMainChatVisibleRef = useRef(false);
  const suspendAudiobookQueueRef = useRef(false);
  const [audiobookQueueVersion, setAudiobookQueueVersion] = useState(0);
  const lastNonLegalRouteRef = useRef("");
  const hasEnteredMainAIRef = useRef(false);

  const [clientPath, setClientPath] = useState(() => getRoutePath());
  const routePath = clientPath;
  const showLoginRoute = normalizePath(routePath) === "/login";
  const showLegalPage = LEGAL_PAGES && (normalizePath(routePath) === "/legal");
  const showPrivacyPage = LEGAL_PAGES && (normalizePath(routePath) === "/privacy");
  const showTermsPage = LEGAL_PAGES && (normalizePath(routePath) === "/terms");
  const showPricingPage = LEGAL_PAGES && (normalizePath(routePath) === "/pricing");
  const showRefundPolicyPage = LEGAL_PAGES && (normalizePath(routePath) === "/refund-policy");
  const showDataRequestPage = LEGAL_PAGES && (normalizePath(routePath) === "/data-request");
  const showAboutPage = LEGAL_PAGES && (normalizePath(routePath) === "/about");
  const showArquivoPage = LEGAL_PAGES && (normalizePath(routePath) === "/arquivo");
  const showChatroomsPage = CHATROOMS_UI_ENABLED && normalizePath(routePath).startsWith("/chatrooms");
  const isLegalRouteActive = showLegalPage || showPrivacyPage || showTermsPage || showPricingPage || showRefundPolicyPage || showDataRequestPage || showAboutPage || showArquivoPage;
  const legalReturnTarget = String(lastNonLegalRouteRef.current || "");
  const legalReturnPath = resolveLegalReturnPath(legalReturnTarget);
  const canReturnToMainAiFromRoot = shouldCaptureMainAiReturn({
    currentPath: routePath,
    appReady: !bootstrapPending,
    showCinematicIntro,
    showChatroomsPage,
    hasEnteredMainAI: hasEnteredMainAIRef.current,
    introDismissed,
  });

  const navigateTo = useCallback((path) => {
    const p = resolveInAppNavigationPath(path);
    const current = normalizePath(routePath);
    if (current === p) return;
    if (!isLegalOrInstitutionalPath(current)) {
      if (current === "/" && canReturnToMainAiFromRoot) {
        lastNonLegalRouteRef.current = MAIN_AI_RETURN_SENTINEL;
      } else if (isValidInternalReturnRoute(current)) {
        lastNonLegalRouteRef.current = current;
      }
    }
    window.history.pushState({}, "", p);
    setClientPath(p);
  }, [canReturnToMainAiFromRoot, routePath]);

  useEffect(() => {
    const onPopState = () => setClientPath(getRoutePath());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useLayoutEffect(() => {
    if (!isLegalRouteActive) return;
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } catch {
      window.scrollTo(0, 0);
    }
    try {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    } catch {}
    try {
      const nodes = document.querySelectorAll(".legal-page-scroll, .vh-main, .vh-main-stack, .chat");
      nodes.forEach((node) => {
        if (node && typeof node.scrollTo === "function") {
          node.scrollTo({ top: 0, left: 0, behavior: "auto" });
        } else if (node && typeof node.scrollTop === "number") {
          node.scrollTop = 0;
        }
      });
    } catch {}
  }, [routePath, isLegalRouteActive]);

  useEffect(() => {
    const isAndroid = /android/i.test(String(navigator.userAgent || ""));
    const standaloneQuery =
      typeof window.matchMedia === "function"
        ? window.matchMedia("(display-mode: standalone)")
        : null;
    const isStandalone = Boolean(standaloneQuery && standaloneQuery.matches);
    const isAndroidAppReferrer = String(document.referrer || "").startsWith("android-app://");
    const isAndroidApp = isAndroid && (isStandalone || isAndroidAppReferrer);
    if (!isAndroidApp) return;
    document.body.classList.add("vh-android-app");
    return () => {
      document.body.classList.remove("vh-android-app");
    };
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(VH_FONT_SCALE_KEY, String(fontScale));
    } catch {}
  }, [fontScale]);

  useEffect(() => {
    specialAudioStateRef.current = specialAudioState;
  }, [specialAudioState]);

  useEffect(() => {
    currentAudiobookOfferRef.current = audiobookOffer || null;
  }, [audiobookOffer]);

  const videoURL =
    "https://pub-e6c5b271b3d4433797d52ab15624cedb.r2.dev/Intro1.mp4";

  // ======= Inicialização =======
  useEffect(() => {
    bootstrapAboutAuthFromUrl()
      .then(() => {
        if (hasAboutFullAiAccess()) {
          setBilling(buildAboutFullBillingStatus());
          setLimiteAtingido(false);
        }
      })
      .catch(() => {});
    setShowCinematicIntro(true);
    setShowIntroByMemory(false);
    setIntroDismissed(false);
    setIntroGateResolved(false);
    setFadeIntro(false);
    setLimiteAtingido(false);
    setPlayerMode("bgm");
    setAudiobookSrc(null);
    setAudiobookOffer(null);
    currentAudiobookOfferRef.current = null;
    pendingBgmTrackAfterAudiobookRef.current = "";
    audiobookQueueRef.current = [];
    suspendAudiobookQueueRef.current = false;
    specialAudioUidRef.current = "";
    setAudiobookQueueVersion(0);
    setResponseStyleBiasByScope({});
    setActiveMode("normal");
    setMessages([]);
    setNormalMessages([]);
    setDragaoMessages([]);
    setShortConclusionByMessageId({});
    setShortConclusionLoadingId("");
    setExpandedShortConclusionByMessageId({});
    setConversations([]);
    setActiveConversationId("");
    setConversationSelection({});
    setConversationsError("");
    setShowSessionsMemoryPanel(false);
    setDragaoFeatureEnabled(false);
    setDragaoActive(false);
    setDragaoState(null);
    setDragaoQuestions(null);
    setDragaoRecentLog([]);
    setDragaoModalOpen(false);
    setDragaoAgeChecked(false);
    setDragaoTermsChecked(false);
    setDragaoBusy(false);
    setDragaoError("");
    setDragaoHomeConversationId("");
    setDragaoCelebration(null);
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (bootstrapPending) {
      return () => {
        cancelled = true;
      };
    }

    const checkMemoryForCurrentEmail = async () => {
      try {
        const data = await getMemoryList();
        const hasMemory = Array.isArray(data?.items) && data.items.length > 0;
        if (!cancelled) {
          setShowIntroByMemory(!hasMemory);
          setIntroDismissed(false);
        }
      } catch {
        if (!cancelled) {
          // Fail-safe: se não conseguir validar, mantém o aviso visível.
          setShowIntroByMemory(true);
          setIntroDismissed(false);
        }
      } finally {
        if (!cancelled) {
          setIntroGateResolved(true);
        }
      }
    };

    checkMemoryForCurrentEmail();
    return () => {
      cancelled = true;
    };
  }, [bootstrapPending]);

  useEffect(() => {
    setDragaoHomeConversationId(readDragaoHomeCid());
  }, []);

  useEffect(() => {
    const uid = resolveSpecialAudioUid(getBoundUserId());
    const loadedState = loadSpecialAudioState(uid);
    specialAudioUidRef.current = uid;
    specialAudioStateRef.current = loadedState;
    setSpecialAudioState(loadedState);
    setSpecialAudioHydrated(true);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadBootstrap = async () => {
      setBootstrapPending(true);
      try {
        const boot = await bootstrapSession();
        if (cancelled) return;

        if (!boot?.ok) {
          if (boot?.status === 451 || boot?.error === "jurisdiction_blocked") {
            setGeoBlocked(true);
            setGeoBlockedCountry(String(boot?.country || ""));
            setGeoBlockedMessage(
              String(boot?.message || "Servico indisponivel na sua jurisdicao.")
            );
            return;
          }
          setGeoBlocked(false);
          return;
        }

        const payload = boot?.data || {};
        const flags = payload?.flags || {};
        const terms = payload?.terms || {};
        const enforce = Boolean(flags?.terms_enforce);
        const accepted = Boolean(terms?.accepted);
        const version = String(terms?.terms_version || TERMS_CONTENT_VERSION);
        const dragaoEnabledByFlag = Boolean(flags?.dragao_mode);

        setGeoBlocked(false);
        setGeoBlockedCountry("");
        setGeoBlockedMessage("");
        setTermsEnforced(enforce);
        setTermsAccepted(!enforce || accepted);
        setTermsChecked(Boolean(!enforce || accepted));
        setTermsVersion(version);
        setTermsError("");
        setDragaoFeatureEnabled(dragaoEnabledByFlag);

        if (enforce && !accepted) {
          setShowIntroByMemory(true);
          setIntroDismissed(false);
        }
      } catch {
        if (cancelled) return;
        setGeoBlocked(false);
      }
      finally {
        if (!cancelled) setBootstrapPending(false);
      }
    };

    loadBootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (bootstrapPending || geoBlocked) return;
    let cancelled = false;
    (async () => {
      try {
        if (hasAboutFullAiAccess()) {
          if (!cancelled) {
            setBilling(buildAboutFullBillingStatus());
            setLimiteAtingido(false);
          }
          return;
        }
        const st = await getBillingStatus();
        if (cancelled) return;
        const effectiveStatus = isAboutFullBillingStatus(st) ? buildAboutFullBillingStatus() : st;
        setBilling(effectiveStatus);
        const enforced =
          effectiveStatus && String(effectiveStatus.paywall_mode || "").toLowerCase() === "enforce";
        const isPremium = isAboutFullBillingStatus(effectiveStatus);
        if (isPremium) {
          setLimiteAtingido(false);
        } else if (enforced && effectiveStatus.plan === "free" && effectiveStatus.free_used >= effectiveStatus.free_limit) {
          setLimiteAtingido(true);
        } else {
          setLimiteAtingido(false);
        }
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bootstrapPending, geoBlocked]);

  useEffect(() => {
    try {
      localStorage.setItem("vh_rachel_enabled", vhRachelEnabled ? "1" : "0");
    } catch {}
  }, [vhRachelEnabled]);

  useEffect(() => {
    const next = activeMode === "dragao" ? dragaoMessages : normalMessages;
    setMessages((prev) => (prev === next ? prev : next));
  }, [activeMode, dragaoMessages, normalMessages]);

  useEffect(() => {
    emittedParableBadgeTelemetryRef.current = new Set();
  }, [activeConversationId]);

  useEffect(() => {
    const keys = getRenderableParableTelemetryKeys(messages, activeConversationId);
    keys.forEach((key) => {
      if (!key || emittedParableBadgeTelemetryRef.current.has(key)) return;
      emittedParableBadgeTelemetryRef.current.add(key);
      emitParableUiTelemetry("parable_badge_shown", { badge_key: key });
    });
  }, [activeConversationId, messages]);

  const appendMessageToScopedThread = useCallback(
    (msg) => {
      if ((dragaoActive || String(activeMode || "normal") === "dragao")) {
        setDragaoMessages((prev) => [...(Array.isArray(prev) ? prev : []), msg]);
        return;
      }
      setNormalMessages((prev) => [...(Array.isArray(prev) ? prev : []), msg]);
    },
    [activeMode, dragaoActive]
  );

  const appendMessageToNormalThread = useCallback((msg) => {
    setNormalMessages((prev) => [...(Array.isArray(prev) ? prev : []), msg]);
  }, []);

  const activateDirectPreferenceForConversation = useCallback((conversationId, mode = "normal") => {
    const scope = responseStyleBiasScopeKey({ mode, conversationId });
    setResponseStyleBiasByScope((prev) => ({
      ...(prev || {}),
      [scope]: "direct_preferred",
    }));
    const cid = String(conversationId || "").trim();
    if (cid) {
      writePersistedConversationResponseStyleBias(cid, "direct_preferred");
    }
    emitParableUiTelemetry("direct_preference_activated", {
      conversation_id: cid || "session",
      mode: String(mode || "normal"),
    });
  }, []);

  const resetDirectPreferenceForConversation = useCallback((conversationId, mode = "normal") => {
    const scope = responseStyleBiasScopeKey({ mode, conversationId });
    setResponseStyleBiasByScope((prev) => {
      const next = { ...(prev || {}) };
      delete next[scope];
      return next;
    });
    const cid = String(conversationId || "").trim();
    if (cid) {
      clearPersistedConversationResponseStyleBias(cid);
    }
    emitParableUiTelemetry("direct_preference_cleared", {
      conversation_id: cid || "session",
      mode: String(mode || "normal"),
    });
  }, []);

  const handleRequestShortConclusion = useCallback(
    async (message, shortConclusionKey = "", renderedText = "", triggerNode = null) => {
      const assistantMessageId = String(message?.assistantMessageId || "").trim();
      const stateKey = String(
        shortConclusionKey || getShortConclusionStateKey(message)
      ).trim();
      if (!stateKey) return;
      const unavailableLabel = tr(uiLang || detectUiLang(), "shortConclusionUnavailable");
      const domDebug = inspectRenderedMessageText(triggerNode);
      const domRenderedText = domDebug.text;
      const messageText = String(message?.text || "");
      const currentCachedValue = String(shortConclusionByMessageId?.[stateKey] || "").trim();
      emitShortConclusionDebug("click", {
        assistantMessageId,
        stateKey,
        hasAssistantMessageId: Boolean(assistantMessageId),
        cachedValueLen: currentCachedValue.length,
        cachedValuePreview: summarizeConclusionDebugText(currentCachedValue),
        messageTextLen: messageText.trim().length,
        messageTextPreview: summarizeConclusionDebugText(messageText),
        msgTextLen: String(renderedText || "").trim().length,
        msgTextPreview: summarizeConclusionDebugText(renderedText),
        domTextLen: domRenderedText.length,
        domTextPreview: summarizeConclusionDebugText(domRenderedText),
        domTextSource: domDebug.source,
        domEpicPlainLen: domDebug.epicPlainText.length,
        domEpicResponseLen: domDebug.epicResponseText.length,
        domRootLen: domDebug.rootText.length,
      });
      if (
        shortConclusionByMessageId &&
        Object.prototype.hasOwnProperty.call(shortConclusionByMessageId, stateKey) &&
        currentCachedValue &&
        currentCachedValue !== unavailableLabel
      ) {
        emitShortConclusionDebug("cached_return", {
          assistantMessageId,
          stateKey,
          cachedValueLen: currentCachedValue.length,
          cachedValuePreview: summarizeConclusionDebugText(currentCachedValue),
        });
        return;
      }
      const localFallback = buildLocalShortConclusionFallback(
        String(domRenderedText || renderedText || messageText || ""),
        unavailableLabel
      );
      setShortConclusionByMessageId((prev) => {
        const next = {
          ...(prev || {}),
          [stateKey]: localFallback,
        };
        emitShortConclusionDebug("local_fallback_saved", {
          assistantMessageId,
          stateKey,
          fallbackLen: localFallback.length,
          fallbackPreview: summarizeConclusionDebugText(localFallback),
          usedUnavailableLabel: localFallback === unavailableLabel,
          savedValueLen: String(next?.[stateKey] || "").trim().length,
        });
        return next;
      });
      setExpandedShortConclusionByMessageId((prev) => ({
        ...(prev || {}),
        [stateKey]: true,
      }));
      emitParableUiTelemetry("short_conclusion_requested", {
        assistant_message_id: assistantMessageId,
        short_conclusion_key: stateKey,
        conversation_id: String(activeConversationId || "").trim() || "session",
        local_fallback: localFallback !== unavailableLabel,
      });
      if (!assistantMessageId) {
        emitShortConclusionDebug("missing_assistant_message_id", {
          stateKey,
          fallbackLen: localFallback.length,
          fallbackPreview: summarizeConclusionDebugText(localFallback),
        });
        return;
      }
      if (!shouldFetchShortConclusion(shortConclusionByMessageId, assistantMessageId, shortConclusionLoadingId)) {
        emitShortConclusionDebug("fetch_skipped", {
          assistantMessageId,
          stateKey,
          loadingId: String(shortConclusionLoadingId || ""),
        });
        return;
      }
      setShortConclusionLoadingId(assistantMessageId);
      try {
        const result = await getParableShortConclusion({
          assistant_message_id: assistantMessageId,
          conversation_id: activeConversationId,
        });
        const shortConclusion = String(result?.short_conclusion || "").trim();
        const available = result?.available === true && Boolean(shortConclusion);
        setShortConclusionByMessageId((prev) => {
          const nextValue = available ? shortConclusion : localFallback;
          const next = {
            ...(prev || {}),
            [stateKey]: nextValue,
          };
          emitShortConclusionDebug("api_resolved", {
            assistantMessageId,
            stateKey,
            available,
            cached: result?.cached === true,
            apiShortConclusionLen: shortConclusion.length,
            apiShortConclusionPreview: summarizeConclusionDebugText(shortConclusion),
            savedValueLen: String(nextValue || "").trim().length,
            savedValuePreview: summarizeConclusionDebugText(nextValue),
            fellBackToLocal: !available,
          });
          return next;
        });
        emitParableUiTelemetry("short_conclusion_resolved", {
          assistant_message_id: assistantMessageId,
          short_conclusion_key: stateKey,
          conversation_id: String(activeConversationId || "").trim() || "session",
          available,
          local_fallback: !available && localFallback !== unavailableLabel,
          cached: result?.cached === true,
        });
        setExpandedShortConclusionByMessageId((prev) => ({
          ...(prev || {}),
          [stateKey]: true,
        }));
      } catch (error) {
        const localFallback = buildLocalShortConclusionFallback(
          String(domRenderedText || renderedText || messageText || ""),
          unavailableLabel
        );
        setShortConclusionByMessageId((prev) => {
          const next = {
            ...(prev || {}),
            [stateKey]: localFallback,
          };
          emitShortConclusionDebug("api_failed", {
            assistantMessageId,
            stateKey,
            errorMessage: String(error?.message || error || ""),
            fallbackLen: localFallback.length,
            fallbackPreview: summarizeConclusionDebugText(localFallback),
            usedUnavailableLabel: localFallback === unavailableLabel,
          });
          return next;
        });
        emitParableUiTelemetry("short_conclusion_failed", {
          assistant_message_id: assistantMessageId,
          short_conclusion_key: stateKey,
          conversation_id: String(activeConversationId || "").trim() || "session",
          local_fallback: localFallback !== unavailableLabel,
        });
        setExpandedShortConclusionByMessageId((prev) => ({
          ...(prev || {}),
          [stateKey]: true,
        }));
      } finally {
        setShortConclusionLoadingId((current) =>
          current === assistantMessageId ? "" : current
        );
      }
    },
    [activeConversationId, shortConclusionByMessageId, shortConclusionLoadingId, uiLang]
  );

  const responseStyleBiasScope = responseStyleBiasScopeKey({
    mode: dragaoActive || String(activeMode || "normal") === "dragao" ? "dragao" : "normal",
    conversationId: activeConversationId,
  });
  const responseStyleBias = String(responseStyleBiasByScope?.[responseStyleBiasScope] || "default");

  useEffect(() => {
    if (showLadyLucroVideo && ladyLucroVideoRef.current) {
      const v = ladyLucroVideoRef.current;
      v.currentTime = 0;
      v.play().catch(() => {});
    }
  }, [showLadyLucroVideo]);

  useEffect(() => {
    if (!Array.isArray(messages) || messages.length === 0) return;
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, bridgeState?.available, bridgeState?.bridge_text, bridgeState?.requestId]);

  useEffect(() => {
    const val = uiLang === "pt" ? "pt-BR" : uiLang === "es" ? "es-ES" : "en-US";
    try {
      sessionStorage.setItem("vh_ui_lang", val);
    } catch {}
    setVoiceLang(val);
  }, [uiLang, setVoiceLang]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "0px";
    const next = Math.min(Math.max(el.scrollHeight, 44), 220);
    el.style.height = `${next}px`;
  }, [input]);

  useEffect(() => {
    if (!isSending) {
      setThinkingLine("");
      return;
    }
    const lang = uiLang || detectUiLang();
    const frames = [
      tr(lang, "thinking1"),
      tr(lang, "thinking2"),
      tr(lang, "thinking3"),
      tr(lang, "thinking4"),
    ];
    let idx = 0;
    setThinkingLine(frames[idx]);
    const intervalId = setInterval(() => {
      idx = (idx + 1) % frames.length;
      setThinkingLine(frames[idx]);
    }, 1300);
    return () => clearInterval(intervalId);
  }, [isSending, uiLang]);

  useEffect(() => {
    musicStoppedRef.current = musicStopped;
  }, [musicStopped]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onPlay = () => setIsMusicPlaying(true);
    const onPauseOrError = () => setIsMusicPlaying(false);

    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPauseOrError);
    audio.addEventListener("ended", onPauseOrError);
    audio.addEventListener("error", onPauseOrError);

    return () => {
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPauseOrError);
      audio.removeEventListener("ended", onPauseOrError);
      audio.removeEventListener("error", onPauseOrError);
    };
  }, [showCinematicIntro]);

  useEffect(() => {
    return () => {
      stopSpeak();
      const recognizer = recognizerRef.current;
      recognizerRef.current = null;
      if (recognizer) {
        try {
          recognizer.stop();
        } catch {}
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (waitBridgeTimerRef.current != null) {
        clearTimeout(waitBridgeTimerRef.current);
        waitBridgeTimerRef.current = null;
      }
      if (waitBridgeAbortRef.current) {
        try {
          waitBridgeAbortRef.current.abort();
        } catch {}
        waitBridgeAbortRef.current = null;
      }
      if (musicRetryTimeoutRef.current) {
        clearTimeout(musicRetryTimeoutRef.current);
        musicRetryTimeoutRef.current = null;
      }
    };
  }, []);

  const cleanupWaitBridge = useCallback(() => {
    if (waitBridgeTimerRef.current != null) {
      clearTimeout(waitBridgeTimerRef.current);
      waitBridgeTimerRef.current = null;
    }
    if (waitBridgeAbortRef.current) {
      try {
        waitBridgeAbortRef.current.abort();
      } catch {}
      waitBridgeAbortRef.current = null;
    }
    setBridgeState(null);
  }, []);

  useEffect(() => {
    if (!bridgeState?.available || !bridgeFrameRef.current) return undefined;
    if (typeof IntersectionObserver === "undefined") return undefined;
    waitBridgeVisibleLoggedRef.current = false;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !waitBridgeVisibleLoggedRef.current) {
            waitBridgeVisibleLoggedRef.current = true;
            logWaitBridge("WAIT_BRIDGE_VISIBLE", {
              requestId: String(bridgeState.requestId || ""),
              len: String(bridgeState.bridge_text || "").length,
            });
          }
        }
      },
      { threshold: 0.5 }
    );
    io.observe(bridgeFrameRef.current);
    return () => io.disconnect();
  }, [bridgeState]);

  const clearIntroCinematicTimers = useCallback(() => {
    if (introInvokeDelayTimerRef.current != null) {
      clearTimeout(introInvokeDelayTimerRef.current);
      introInvokeDelayTimerRef.current = null;
    }
    if (introEndCapTimerRef.current != null) {
      clearTimeout(introEndCapTimerRef.current);
      introEndCapTimerRef.current = null;
    }
  }, []);

  const finishIntro = useCallback(() => {
    clearIntroCinematicTimers();
    setFadeIntro(true);
    window.setTimeout(() => {
      setShowCinematicIntro(false);
      setInvokeClicked(false);
      setShowVideo(false);
      setFadeIntro(false);
    }, 800);
  }, [clearIntroCinematicTimers]);

  useEffect(() => () => clearIntroCinematicTimers(), [clearIntroCinematicTimers]);

  useEffect(() => {
    if (!showCinematicIntro || !showVideo) return undefined;
    const v = videoRef.current;
    if (!v) return undefined;
    const armEndCap = () => {
      const d = Number(v.duration);
      if (!Number.isFinite(d) || d <= 0) return;
      if (introEndCapTimerRef.current != null) {
        clearTimeout(introEndCapTimerRef.current);
      }
      introEndCapTimerRef.current = window.setTimeout(() => {
        introEndCapTimerRef.current = null;
        finishIntro();
      }, Math.min(300000, (d + 2.5) * 1000));
    };
    if (v.readyState >= 1) armEndCap();
    else v.addEventListener("loadedmetadata", armEndCap, { once: true });
    return () => {
      v.removeEventListener("loadedmetadata", armEndCap);
    };
  }, [showCinematicIntro, showVideo, finishIntro]);

  const playIntroVideoSafely = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    try {
      video.defaultMuted = true;
      video.muted = true;
      video.volume = 0;
      if (video.currentTime <= 0) {
        video.currentTime = 0;
      }
      const playPromise = video.play();
      if (playPromise && typeof playPromise.then === "function") {
        playPromise
          .then(() => {
            if (!invokeClicked || !showVideo) return;
            try {
              video.muted = false;
              const fade = window.setInterval(() => {
                if (video.paused || video.ended) {
                  window.clearInterval(fade);
                  return;
                }
                if (video.volume < 0.95) {
                  video.volume = Math.min(1, video.volume + 0.05);
                  return;
                }
                video.volume = 1;
                window.clearInterval(fade);
              }, 120);
            } catch {}
          })
          .catch(() => {
            finishIntro();
          });
      } else if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {
          finishIntro();
        });
      }
    } catch {
      finishIntro();
    }
  }, [invokeClicked, showVideo, finishIntro]);

  const stopRecording = useCallback(() => {
    const rec = recognizerRef.current;
    recognizerRef.current = null;
    setIsRecording(false);
    setInterimVoiceText("");
    if (rec) {
      try {
        rec.stop();
      } catch {}
    }

    if (voicePausedMusicRef.current) {
      voicePausedMusicRef.current = false;
      if (!musicStoppedRef.current) {
        const audio = audioRef.current;
        if (audio && audio.src) {
          audio.play().then(() => {
            setIsMusicPlaying(true);
            setMusicStopped(false);
            setMusicaIniciada(true);
          }).catch(() => {
            setIsMusicPlaying(false);
          });
        }
      }
    }
  }, []);

  const startRecording = useCallback(() => {
    if (!isSTTSupported()) {
      alert(tr(uiLang || detectUiLang(), "voiceUnsupported"));
      return;
    }
    if (isRecording) return;

    if (!musicStoppedRef.current) {
      const audio = audioRef.current;
      if (audio && !audio.paused) {
        voicePausedMusicRef.current = true;
        try {
          audio.pause();
        } catch {}
        setIsMusicPlaying(false);
      }
    }

    setIsRecording(true);
    setInterimVoiceText("");

    const rec = createRecognizer({
      lang: voiceLang,
      onText: (text, isFinal) => {
        setInterimVoiceText(text);
        if (isFinal) {
          setInput((prev) => {
            const base = String(prev || "");
            const spacer = base && !base.endsWith(" ") ? " " : "";
            return `${base}${spacer}${text}`.trim();
          });
        }
      },
      onError: () => {
        stopRecording();
      },
      onEnd: () => {
        stopRecording();
      },
    });

    if (!rec) {
      alert(tr(uiLang || detectUiLang(), "voiceStartFail"));
      setIsRecording(false);
      return;
    }

    recognizerRef.current = rec;
    try {
      rec.start();
    } catch {
      stopRecording();
    }
  }, [isRecording, voiceLang, stopRecording, uiLang]);

  const refreshConversations = useCallback(async () => {
    if (!CONVERSATIONS_UI_ENABLED || bootstrapPending || geoBlocked) return;
    setConversationsLoading(true);
    setConversationsError("");
    try {
      const data = await listConversations({ limit: 50 });
      const items = Array.isArray(data?.items) ? data.items : [];
      const features = data?.features || {};
      setConversations(items);
      setConversationsAvailable(features.enabled !== false);
      setConversationsSummaryEnabled(Boolean(features.summary_enabled));
      setConversationsDeleteEnabled(
        Boolean(features.delete_enabled) && CONVERSATIONS_DELETE_UI_ENABLED
      );
    } catch (err) {
      const msg = String(err?.message || "");
      if (msg.includes("404")) {
        setConversationsAvailable(false);
      } else {
        setConversationsError(convT(uiLang || detectUiLang(), "sessionsFail"));
      }
    } finally {
      setConversationsLoading(false);
    }
  }, [bootstrapPending, geoBlocked, uiLang]);

  const openConversationById = useCallback(
    async (conversationId) => {
      const cid = String(conversationId || "").trim();
      if (!cid || !CONVERSATIONS_UI_ENABLED || !conversationsAvailable) return;
      if (String(activeMode || "normal") === "dragao") return;
      const nextScope = responseStyleBiasScopeKey({ mode: "normal", conversationId: cid });
      const sessionScope = responseStyleBiasScopeKey({ mode: "normal", conversationId: "" });
      setResponseStyleBiasByScope((prev) => {
        const next = { ...(prev || {}) };
        delete next[nextScope];
        delete next[sessionScope];
        return next;
      });
      setLoadingConversationMessages(true);
      setConversationsError("");
      try {
        const data = await getConversationMessages(cid, { limit: 200 });
        const nextMessages = normalizeConversationMessages(data?.items || []);
        const persistedBias = readPersistedConversationResponseStyleBias(cid);
        setNormalMessages(nextMessages);
        setMessages(nextMessages);
        setResponseStyleBiasByScope((prev) => {
          const next = { ...(prev || {}) };
          if (persistedBias === "direct_preferred") {
            next[nextScope] = "direct_preferred";
          } else {
            delete next[nextScope];
          }
          return next;
        });
        setActiveConversationId(cid);
        setConversationSelection({});
      } catch (err) {
        console.error("[CONVERSATIONS] Falha ao abrir conversa:", err);
        setConversationsError(convT(uiLang || detectUiLang(), "conversationFail"));
      } finally {
        setLoadingConversationMessages(false);
      }
    },
    [activeMode, conversationsAvailable, uiLang]
  );

  const ensureConversationIdForSend = useCallback(
    async (firstUserMessage) => {
      if (!CONVERSATIONS_UI_ENABLED || !conversationsAvailable) return "";
      const current = String(activeConversationId || "").trim();
      if (current) return current;
      try {
        const created = await createConversation({
          first_user_message: String(firstUserMessage || ""),
        });
        const cid = String(created?.conversation_id || "").trim();
        if (!cid) return "";
        setActiveConversationId(cid);
        await refreshConversations();
        return cid;
      } catch (err) {
        console.error("[CONVERSATIONS] Falha ao criar conversa:", err);
        setConversationsError(convT(uiLang || detectUiLang(), "sessionsFail"));
        return "";
      }
    },
    [activeConversationId, conversationsAvailable, refreshConversations, uiLang]
  );

  const handleNewConversation = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    cleanupWaitBridge();
    setActiveMode("normal");
    setActiveConversationId("");
    setResponseStyleBiasByScope({});
    setShortConclusionByMessageId({});
    setShortConclusionLoadingId("");
    setExpandedShortConclusionByMessageId({});
    setMessages([]);
    setNormalMessages([]);
    setConversationSelection({});
    setIsSending(false);
    setDragaoActive(false);
    setDragaoState(null);
    setDragaoQuestions(null);
    setDragaoRecentLog([]);
    setDragaoError("");
  }, [cleanupWaitBridge]);

  const toggleConversationMessageSelection = useCallback((messageId) => {
    const id = String(messageId || "").trim();
    if (!id) return;
    setConversationSelection((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  const handleDeleteConversationItem = useCallback(
    async (conversationId) => {
      const cid = String(conversationId || "").trim();
      if (!cid || !conversationsDeleteEnabled) return;
      if (!window.confirm(convT(uiLang || detectUiLang(), "deleteConv") + "?")) return;
      setDeletionBusy(true);
      try {
        await deleteConversation(cid);
        clearPersistedConversationResponseStyleBias(cid);
        if (String(activeConversationId || "").trim() === cid) {
          setActiveConversationId("");
          setResponseStyleBiasByScope({});
          setShortConclusionByMessageId({});
          setShortConclusionLoadingId("");
          setExpandedShortConclusionByMessageId({});
          setNormalMessages([]);
          if (String(activeMode || "normal") !== "dragao") {
            setMessages([]);
          }
          setConversationSelection({});
        }
        await refreshConversations();
      } catch (err) {
        console.error("[CONVERSATIONS] Falha ao apagar conversa:", err);
        alert(convT(uiLang || detectUiLang(), "deleteFail"));
      } finally {
        setDeletionBusy(false);
      }
    },
    [activeConversationId, activeMode, conversationsDeleteEnabled, refreshConversations, uiLang]
  );

  const handleDeleteSelectedConversationMessages = useCallback(async () => {
    if (!conversationsDeleteEnabled) return;
    const cid = String(activeConversationId || "").trim();
    if (!cid) return;
    const selectedIds = Object.keys(conversationSelection).filter((k) => conversationSelection[k]);
    if (selectedIds.length === 0) return;
    if (!window.confirm(convT(uiLang || detectUiLang(), "deleteSelected") + "?")) return;
    setDeletionBusy(true);
    try {
      await deleteConversationMessages(cid, selectedIds);
      setConversationSelection({});
      await openConversationById(cid);
      await refreshConversations();
    } catch (err) {
      console.error("[CONVERSATIONS] Falha ao apagar mensagens:", err);
      alert(convT(uiLang || detectUiLang(), "deleteFail"));
    } finally {
      setDeletionBusy(false);
    }
  }, [
    activeConversationId,
    conversationSelection,
    conversationsDeleteEnabled,
    openConversationById,
    refreshConversations,
    uiLang,
  ]);

  const handleDeleteAllConversationsHistory = useCallback(async () => {
    if (!conversationsDeleteEnabled) return;
    const requiredWord = convT(uiLang || detectUiLang(), "confirmWord");
    if (String(deleteConfirmInput || "").trim().toUpperCase() !== requiredWord.toUpperCase()) {
      return;
    }
    setDeletionBusy(true);
    try {
      await deleteAllConversations();
      clearAllPersistedConversationResponseStyleBias();
      setDeleteConfirmInput("");
      setConversations([]);
      setActiveConversationId("");
      setResponseStyleBiasByScope({});
      setShortConclusionByMessageId({});
      setShortConclusionLoadingId("");
      setExpandedShortConclusionByMessageId({});
      setNormalMessages([]);
      if (String(activeMode || "normal") !== "dragao") {
        setMessages([]);
      }
      setConversationSelection({});
    } catch (err) {
      console.error("[CONVERSATIONS] Falha no wipe total:", err);
      alert(convT(uiLang || detectUiLang(), "deleteFail"));
    } finally {
      setDeletionBusy(false);
    }
  }, [activeMode, conversationsDeleteEnabled, deleteConfirmInput, uiLang]);

  useEffect(() => {
    let cancelled = false;
    if (!CONVERSATIONS_UI_ENABLED || bootstrapPending || geoBlocked) {
      setConversations([]);
      setConversationsError("");
      setConversationsAvailable(CONVERSATIONS_UI_ENABLED);
      return () => {
        cancelled = true;
      };
    }
    if (!CONVERSATIONS_AUTO_LOAD_ON_OPEN) {
      setConversations([]);
      setConversationsError("");
      setConversationsAvailable(true);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      await refreshConversations();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [bootstrapPending, geoBlocked, refreshConversations]);

  const clearDragaoArmorFlashTimers = useCallback(() => {
    const timers = dragaoArmorTimersRef.current || {};
    Object.values(timers).forEach((timerId) => {
      if (timerId) {
        clearTimeout(timerId);
      }
    });
    dragaoArmorTimersRef.current = {};
  }, []);

  const clearDragaoArmorVisualState = useCallback(() => {
    clearDragaoArmorFlashTimers();
    setDragaoArmorFlash({});
    dragaoArmorPrevStateRef.current = null;
  }, [clearDragaoArmorFlashTimers]);

  const triggerDragaoCelebration = useCallback((pieceKey, armorStateSnapshot) => {
    const key = String(pieceKey || "").trim();
    if (!key) return;
    if (dragaoCelebrationTimerRef.current) {
      clearTimeout(dragaoCelebrationTimerRef.current);
      dragaoCelebrationTimerRef.current = null;
    }
    const idx = Math.max(1, DRAGAO_ARMOR_KEYS.indexOf(key) + 1);
    const armorState =
      armorStateSnapshot && typeof armorStateSnapshot === "object"
        ? { ...armorStateSnapshot }
        : null;
    setDragaoCelebration({
      key,
      idx,
      armorState,
      startedAt: Date.now(),
    });
    playDragaoUnlockChime();
    dragaoCelebrationTimerRef.current = setTimeout(() => {
      setDragaoCelebration(null);
      dragaoCelebrationTimerRef.current = null;
    }, DRAGAO_CELEBRATION_MS);
  }, []);

  const applyDragaoPayload = useCallback(
    (payload, contextConversationId = "") => {
      const stateObj = payload?.state && typeof payload.state === "object" ? payload.state : null;
      const active = Boolean(stateObj?.active);
      const modeHint = String(
        payload?.mode_active || stateObj?.mode_active || (active ? "dragao" : "normal")
      )
        .trim()
        .toLowerCase();
      const nextMode = modeHint === "dragao" || active ? "dragao" : "normal";
      const nextArmorState =
        stateObj?.armor_state && typeof stateObj.armor_state === "object"
          ? stateObj.armor_state
          : null;
      const sourceCid = String(
        contextConversationId || payload?.conversation_id || activeConversationId || ""
      ).trim();

      if (active && sourceCid) {
        setDragaoHomeConversationId(sourceCid);
        writeDragaoHomeCid(sourceCid);
      }

      if (dragaoArmorConversationRef.current !== sourceCid) {
        clearDragaoArmorVisualState();
        dragaoArmorConversationRef.current = sourceCid;
      }

      const prevArmorState = dragaoArmorPrevStateRef.current;
      if (active && prevArmorState && nextArmorState) {
        const justUnlocked = DRAGAO_ARMOR_KEYS.filter(
          (key) => !Boolean(prevArmorState?.[key]) && Boolean(nextArmorState?.[key])
        );
        if (justUnlocked.length > 0) {
          setDragaoArmorFlash((prev) => {
            const next = { ...(prev || {}) };
            justUnlocked.forEach((key) => {
              next[key] = true;
            });
            return next;
          });
          justUnlocked.forEach((key) => {
            const oldTimer = dragaoArmorTimersRef.current?.[key];
            if (oldTimer) clearTimeout(oldTimer);
            dragaoArmorTimersRef.current[key] = setTimeout(() => {
              setDragaoArmorFlash((prev) => {
                if (!prev || !prev[key]) return prev || {};
                const next = { ...prev };
                delete next[key];
                return next;
              });
              delete dragaoArmorTimersRef.current[key];
            }, DRAGAO_ARMOR_FLASH_MS);
          });
          triggerDragaoCelebration(justUnlocked[0], nextArmorState);
        }
      }

      if (!active || !nextArmorState) {
        clearDragaoArmorVisualState();
      }
      dragaoArmorPrevStateRef.current = nextArmorState ? { ...nextArmorState } : null;

      if (nextMode === "dragao" && Array.isArray(payload?.dragao_thread?.messages)) {
        setDragaoMessages(normalizeDragaoThreadMessages(payload.dragao_thread.messages));
      }
      if (nextMode === "normal" && payload?.normal_reset_done === true) {
        setNormalMessages([]);
      }

      setDragaoState(stateObj);
      setDragaoQuestions(payload?.questions || null);
      setDragaoRecentLog(Array.isArray(payload?.recent_log) ? payload.recent_log : []);
      setDragaoActive(active);
      setActiveMode(nextMode);
    },
    [activeConversationId, clearDragaoArmorVisualState, triggerDragaoCelebration]
  );

  useEffect(() => {
    return () => {
      clearDragaoArmorFlashTimers();
    };
  }, [clearDragaoArmorFlashTimers]);

  useEffect(() => {
    return () => {
      if (dragaoCelebrationTimerRef.current) {
        clearTimeout(dragaoCelebrationTimerRef.current);
        dragaoCelebrationTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!dragaoActive || !dragaoState) {
      clearDragaoArmorVisualState();
    }
  }, [clearDragaoArmorVisualState, dragaoActive, dragaoState]);

  const refreshDragaoStatus = useCallback(
    async (conversationId) => {
      if (bootstrapPending || geoBlocked || !dragaoFeatureEnabled) return;
      const cid = String(conversationId || dragaoHomeConversationId || activeConversationId || "").trim();
      if (!cid) return;
      const res = await getDragaoStatus(cid);
      if (!res?.ok) {
        if (res?.status === 404) setDragaoFeatureEnabled(false);
        return;
      }
      applyDragaoPayload(res, cid);
    },
    [activeConversationId, applyDragaoPayload, bootstrapPending, dragaoFeatureEnabled, dragaoHomeConversationId, geoBlocked]
  );

  useEffect(() => {
    if (bootstrapPending || geoBlocked || !dragaoFeatureEnabled) return;
    const cid = String(activeConversationId || dragaoHomeConversationId || "").trim();
    if (!cid) {
      setDragaoState(null);
      setDragaoQuestions(null);
      setDragaoRecentLog([]);
      setDragaoActive(false);
      setActiveMode("normal");
      return;
    }
    refreshDragaoStatus(cid).catch(() => {});
  }, [activeConversationId, bootstrapPending, dragaoFeatureEnabled, dragaoHomeConversationId, geoBlocked, refreshDragaoStatus]);

  const ensureDragaoConversationId = useCallback(async () => {
    let cid = String(dragaoHomeConversationId || "").trim();
    if (cid) {
      if (String(activeConversationId || "").trim() !== cid) {
        setActiveConversationId(cid);
      }
      return cid;
    }
    cid = String(activeConversationId || "").trim();
    if (cid) {
      setDragaoHomeConversationId(cid);
      writeDragaoHomeCid(cid);
      return cid;
    }
    if (CONVERSATIONS_UI_ENABLED && conversationsAvailable) {
      cid = await ensureConversationIdForSend("Modo Dragao");
      if (cid) {
        setActiveConversationId(cid);
        setDragaoHomeConversationId(cid);
        writeDragaoHomeCid(cid);
      }
    }
    if (!cid) {
      cid = String(genUuidV4() || "").trim();
      if (cid) {
        setActiveConversationId(cid);
        setDragaoHomeConversationId(cid);
        writeDragaoHomeCid(cid);
      }
    }
    return String(cid || "").trim();
  }, [activeConversationId, conversationsAvailable, dragaoHomeConversationId, ensureConversationIdForSend]);

  const handleOpenDragaoModal = useCallback(async () => {
    if (!dragaoFeatureEnabled) return;
    setDragaoError("");
    setDragaoModalOpen(true);
    try {
      const cid = await ensureDragaoConversationId();
      if (cid) await refreshDragaoStatus(cid);
    } catch {}
  }, [dragaoFeatureEnabled, ensureDragaoConversationId, refreshDragaoStatus]);

  const handleAcceptDragao = useCallback(async () => {
    if (!dragaoFeatureEnabled) return;
    setDragaoBusy(true);
    setDragaoError("");
    try {
      const cid = await ensureDragaoConversationId();
      if (!cid) {
        setDragaoError("Nao foi possivel criar conversa para o modo Dragao.");
        return;
      }
      const res = await acceptDragaoMode({
        conversationId: cid,
        ageConfirmed: dragaoAgeChecked,
        termsAccepted: dragaoTermsChecked,
      });
      if (!res?.ok) {
        if (res?.daily_limit_reached) {
          setDragaoError("Limite diario atingido para esta conversa. Tente novamente amanha.");
        } else {
          setDragaoError(res?.detail || "Nao foi possivel ativar o modo Dragao.");
        }
        if (res?.state) applyDragaoPayload(res, cid);
        return;
      }
      applyDragaoPayload(res, cid);
      setDragaoModalOpen(false);
      setDragaoAgeChecked(false);
      setDragaoTermsChecked(false);
    } catch {
      setDragaoError("Falha ao ativar o modo Dragao.");
    } finally {
      setDragaoBusy(false);
    }
  }, [
    applyDragaoPayload,
    dragaoAgeChecked,
    dragaoFeatureEnabled,
    dragaoTermsChecked,
    ensureDragaoConversationId,
  ]);

  const handleDisableDragao = useCallback(async () => {
    if (!dragaoFeatureEnabled) return;
    setDragaoBusy(true);
    setDragaoError("");
    try {
      const cid = await ensureDragaoConversationId();
      if (!cid) return;
      const res = await disableDragaoMode(cid);
      applyDragaoPayload(res || {}, cid);
      if (res?.normal_reset_done) {
        setConversationSelection({});
      }
    } catch {
      setDragaoError("Falha ao sair do modo Dragao.");
    } finally {
      setDragaoBusy(false);
    }
  }, [applyDragaoPayload, dragaoFeatureEnabled, ensureDragaoConversationId]);

  const handleDragaoIdentifySubmit = useCallback(async () => {
    setDragaoBusy(true);
    setDragaoError("");
    try {
      const cid = await ensureDragaoConversationId();
      if (!cid) return;
      const costs = String(dragaoIdentifyCosts || "")
        .split(/\r?\n/)
        .map((x) => String(x || "").trim())
        .filter(Boolean)
        .slice(0, 3);
      const res = await identifyDragao({
        conversation_id: cid,
        dragao_name: dragaoIdentifyName,
        why: dragaoIdentifyWhy,
        cost_items: costs,
      });
      if (!res?.ok) {
        setDragaoError(res?.detail || "Falha ao registrar identificacao.");
        return;
      }
      applyDragaoPayload(res, cid);
    } catch {
      setDragaoError("Falha ao registrar identificacao.");
    } finally {
      setDragaoBusy(false);
    }
  }, [
    applyDragaoPayload,
    dragaoIdentifyCosts,
    dragaoIdentifyName,
    dragaoIdentifyWhy,
    ensureDragaoConversationId,
  ]);

  const handleDragaoBaselineSubmit = useCallback(async () => {
    setDragaoBusy(true);
    setDragaoError("");
    try {
      const cid = await ensureDragaoConversationId();
      if (!cid) return;
      const f = dragaoBaselineForm || {};
      const res = await submitDragaoBaseline({
        conversation_id: cid,
        cedeu_7d: Number(f.cedeu_7d || 0),
        gatilho_principal: f.gatilho_principal || "",
        controle_0_10: Number(f.controle_0_10 || 0),
        dano_concreto: f.dano_concreto || "",
        resistencia_tentada: f.resistencia_tentada || "",
        acao_hoje: f.acao_hoje || "",
        pilar_baseline: {
          p1: Number(f.p1 || 0),
          p2: Number(f.p2 || 0),
          p3: Number(f.p3 || 0),
          p4: Number(f.p4 || 0),
          p5: Number(f.p5 || 0),
        },
      });
      if (!res?.ok) {
        setDragaoError(res?.detail || "Falha ao registrar baseline.");
        return;
      }
      applyDragaoPayload(res, cid);
    } catch {
      setDragaoError("Falha ao registrar baseline.");
    } finally {
      setDragaoBusy(false);
    }
  }, [applyDragaoPayload, dragaoBaselineForm, ensureDragaoConversationId]);

  const handleDragaoCheckinSubmit = useCallback(async () => {
    setDragaoBusy(true);
    setDragaoError("");
    try {
      const cid = await ensureDragaoConversationId();
      if (!cid) return;
      const f = dragaoCheckinForm || {};
      const res = await submitDragaoCheckin({
        conversation_id: cid,
        checkin_id: genUuidV4(),
        result: String(f.result || "parcial"),
        times: Number(f.times || 0),
        trigger: f.trigger || "",
        control_0_10: Number(f.control_0_10 || 0),
        damage: f.damage || "",
        action_executed: f.action_executed || "",
        action_done: !!f.action_done,
        objective_metric: f.objective_metric || "",
        barrier_applied: f.barrier_applied || "",
        repeat_count: Number(f.repeat_count || 1),
      });
      if (!res?.ok) {
        setDragaoError(res?.detail || "Falha no check-in do modo Dragao.");
        return;
      }
      applyDragaoPayload(res, cid);
    } catch {
      setDragaoError("Falha no check-in do modo Dragao.");
    } finally {
      setDragaoBusy(false);
    }
  }, [applyDragaoPayload, dragaoCheckinForm, ensureDragaoConversationId]);

  const handleDragaoReviewSubmit = useCallback(async () => {
    setDragaoBusy(true);
    setDragaoError("");
    try {
      const cid = await ensureDragaoConversationId();
      if (!cid) return;
      const res = await finalizeDragaoReview({ conversation_id: cid });
      if (!res?.ok) {
        setDragaoError(res?.detail || "Falha ao concluir review.");
        return;
      }
      applyDragaoPayload(res, cid);
    } catch {
      setDragaoError("Falha ao concluir review.");
    } finally {
      setDragaoBusy(false);
    }
  }, [applyDragaoPayload, ensureDragaoConversationId]);

  // ======= Funções auxiliares =======
  const commitSpecialAudioState = useCallback((nextValue) => {
    const rawNext =
      typeof nextValue === "function"
        ? nextValue(specialAudioStateRef.current)
        : nextValue;
    const nextState = persistSpecialAudioState(
      specialAudioUidRef.current,
      rawNext
    );
    specialAudioStateRef.current = nextState;
    setSpecialAudioState(nextState);
    return nextState;
  }, []);

  const normalizeBackendAudiobookOffer = useCallback((offer) => {
    const raw = offer && typeof offer === "object" ? offer : null;
    const audioId = String(raw?.audio_id || "").trim();
    if (!raw || !audioId) return null;
    const offerId = String(raw?.offer_id || audioId).trim();
    return {
      ...raw,
      source: "backend",
      offer_key: `backend:${offerId}`,
    };
  }, []);

  const queueAudiobookOffer = useCallback((offer) => {
    const nextOffer = offer && typeof offer === "object" ? offer : null;
    const offerKey = String(nextOffer?.offer_key || "").trim();
    if (!nextOffer || !offerKey) return;
    if (String(currentAudiobookOfferRef.current?.offer_key || "").trim() === offerKey) {
      return;
    }
    const nextQueue = insertAudiobookOfferInQueue(audiobookQueueRef.current, nextOffer);
    if (nextQueue.length === audiobookQueueRef.current.length) return;
    audiobookQueueRef.current = nextQueue;
    setAudiobookQueueVersion((prev) => prev + 1);
  }, []);

  const clearActiveAudiobookOffer = useCallback(
    ({ revealNextOffer = true, dismissBackendOffer = true } = {}) => {
      const activeOffer = currentAudiobookOfferRef.current;
      if (!activeOffer) return;
      suspendAudiobookQueueRef.current = !revealNextOffer;

      if (
        dismissBackendOffer &&
        !isLocalSpecialAudiobookOffer(activeOffer) &&
        activeOffer.audio_id
      ) {
        dismissAudiobookOffer(activeOffer.audio_id).catch(() => {});
      }

      if (isLocalSpecialAudiobookOffer(activeOffer)) {
        const localId = String(
          activeOffer.local_offer_id || activeOffer.audio_id || ""
        ).trim();
        if (localId) {
          commitSpecialAudioState((prev) =>
            clearPendingSpecialAudioOffer(prev, localId)
          );
        }
      }

      currentAudiobookOfferRef.current = null;
      setAudiobookOffer(null);
      if (revealNextOffer) {
        suspendAudiobookQueueRef.current = false;
        setAudiobookQueueVersion((prev) => prev + 1);
      }
    },
    [commitSpecialAudioState]
  );

  const revealLocalAudiobookUnlockPhrase = useCallback(
    (localId) => {
      const offerId = String(localId || "").trim();
      if (!offerId) return;

      const { state: nextState, phrase } = markSpecialAudioPhraseShown(
        specialAudioStateRef.current,
        offerId
      );
      if (!phrase) return;

      commitSpecialAudioState(nextState);
      appendMessageToNormalThread({
        id: `vh-local-phrase-${offerId}-${Date.now()}`,
        text: phrase,
        sender: "ia",
        mode: currentMode,
        localOnly: true,
      });
      emitSpecialAudioTelemetry("vh_audio_unlock_phrase_shown", {
        audio_id: offerId,
        phrase_index: Math.max(0, Number(nextState.phrase_index || 1) - 1),
      });
    },
    [appendMessageToNormalThread, commitSpecialAudioState, currentMode]
  );

  const activateAudiobookOffer = useCallback(
    (offer) => {
      const nextOffer = offer && typeof offer === "object" ? offer : null;
      if (!nextOffer) return;

      currentAudiobookOfferRef.current = nextOffer;
      setAudiobookOffer(nextOffer);
      emitSpecialAudioTelemetry("vh_audio_offer_shown", {
        audio_id: String(nextOffer.audio_id || "").trim(),
        source: isLocalSpecialAudiobookOffer(nextOffer) ? "local" : "backend",
      });
    },
    []
  );

  const queuePendingLocalAudiobookOffers = useCallback(() => {
    if (!VH_SPECIAL_AUDIO_UNLOCKS_ENABLED || !specialAudioHydrated) return;
    const pendingOffers = getPendingSpecialAudioOffers(specialAudioStateRef.current);
    pendingOffers.forEach((offer) => queueAudiobookOffer(offer));
  }, [queueAudiobookOffer, specialAudioHydrated]);

  const normalizeTrackPath = useCallback((track) => {
    try {
      return decodeURI(new URL(String(track || ""), window.location.origin).pathname);
    } catch {
      return String(track || "");
    }
  }, []);

  const getTrackFailures = useCallback((track) => {
    const key = normalizeTrackPath(track);
    return failedTracksRef.current.get(key) || 0;
  }, [normalizeTrackPath]);

  const markTrackFailure = useCallback((track) => {
    const key = normalizeTrackPath(track);
    if (!key) return;
    const current = failedTracksRef.current.get(key) || 0;
    failedTracksRef.current.set(key, current + 1);
  }, [normalizeTrackPath]);

  const tocarMusicaAleatoria = useCallback((options = {}) => {
    const force = options && options.force === true;
    const allowOutsideBgm = options && options.allowOutsideBgm === true;
    if (playerMode !== "bgm" && !allowOutsideBgm) return;
    const lista = todasMusicas;
    if (!lista || lista.length === 0) return;

    const now = Date.now();
    if (!force && isSwitchingTrackRef.current) return;
    if (!force && now - lastTrackSwitchAtRef.current < 700) return;

    const candidatas = lista.filter((track) => getTrackFailures(track) < 2);
    const pool = candidatas.length > 0 ? candidatas : lista;

    let nova;
    let attempts = 0;
    do {
      nova = pool[Math.floor(Math.random() * pool.length)];
      attempts += 1;
    } while (nova === musicaAtual && pool.length > 1 && attempts < 12);

    const audio = audioRef.current;
    if (!audio || typeof audio.volume === "undefined") return;

    if (musicRetryTimeoutRef.current) {
      clearTimeout(musicRetryTimeoutRef.current);
      musicRetryTimeoutRef.current = null;
    }

    isSwitchingTrackRef.current = true;
    lastTrackSwitchAtRef.current = now;

    try {
      audio.pause();
    } catch {}

    if (musicaAtual) {
      const hist = musicHistoryRef.current;
      hist.push(musicaAtual);
      if (hist.length > 20) hist.shift();
      setHasMusicHistory(true);
    }

    audio.src = nova;
    audio.currentTime = 0;
    audio.volume = VH_BGM_BASE_VOLUME;
    audio.play().then(() => {
      setIsMusicPlaying(true);
      setMusicaAtual(nova);
      setMusicStopped(false);
      isSwitchingTrackRef.current = false;
    }).catch(() => {
      markTrackFailure(nova);
      setIsMusicPlaying(false);
      isSwitchingTrackRef.current = false;
      if (!musicStoppedRef.current) {
        musicRetryTimeoutRef.current = setTimeout(() => {
          tocarMusicaAleatoria({ force: true });
        }, 900);
      }
    });
  }, [getTrackFailures, markTrackFailure, musicaAtual, playerMode]);

  const playBgmTrack = useCallback((track, options = {}) => {
    const src = String(track || "").trim();
    if (!src) return;

    const fallbackToRandom = options?.fallbackToRandom !== false;
    const allowOutsideBgm = options?.allowOutsideBgm === true;
    const audio = audioRef.current;

    if (!audio || typeof audio.volume === "undefined") {
      setMusicaIniciada(true);
      return;
    }

    if (musicRetryTimeoutRef.current) {
      clearTimeout(musicRetryTimeoutRef.current);
      musicRetryTimeoutRef.current = null;
    }

    isSwitchingTrackRef.current = true;
    lastTrackSwitchAtRef.current = Date.now();

    try {
      audio.pause();
    } catch {}

    audio.src = src;
    audio.currentTime = 0;
    audio.volume = VH_BGM_BASE_VOLUME;
    audio.play().then(() => {
      setIsMusicPlaying(true);
      setMusicaAtual(src);
      setMusicStopped(false);
      setMusicaIniciada(true);
      isSwitchingTrackRef.current = false;
    }).catch(() => {
      markTrackFailure(src);
      setIsMusicPlaying(false);
      isSwitchingTrackRef.current = false;
      setMusicaIniciada(true);
      if (fallbackToRandom && !musicStoppedRef.current) {
        tocarMusicaAleatoria({ force: true, allowOutsideBgm });
      }
    });
  }, [markTrackFailure, tocarMusicaAleatoria]);

  const closeAudiobookOffer = useCallback(() => {
    const currentOffer = currentAudiobookOfferRef.current;
    if (currentOffer) {
      emitSpecialAudioTelemetry("vh_audio_offer_dismissed", {
        audio_id: String(currentOffer.audio_id || "").trim(),
        source: isLocalSpecialAudiobookOffer(currentOffer) ? "local" : "backend",
      });
      if (isLocalSpecialAudiobookOffer(currentOffer)) {
        revealLocalAudiobookUnlockPhrase(
          currentOffer.local_offer_id || currentOffer.audio_id
        );
      }
    }
    clearActiveAudiobookOffer({
      revealNextOffer: true,
      dismissBackendOffer: true,
    });
  }, [clearActiveAudiobookOffer, revealLocalAudiobookUnlockPhrase]);

  const resumeBgmAfterAudiobook = useCallback(() => {
    if (musicStoppedRef.current) return;
    const pendingTrack = String(
      pendingBgmTrackAfterAudiobookRef.current || ""
    ).trim();
    if (pendingTrack) {
      pendingBgmTrackAfterAudiobookRef.current = "";
      playBgmTrack(pendingTrack, {
        fallbackToRandom: true,
        allowOutsideBgm: true,
      });
      return;
    }
    const bgm = audioRef.current;
    if (bgm && bgm.src) {
      bgm.play().then(() => {
        setIsMusicPlaying(true);
        setMusicStopped(false);
      }).catch(() => {
        setIsMusicPlaying(false);
        tocarMusicaAleatoria({ force: true, allowOutsideBgm: true });
      });
      return;
    }
    setIsMusicPlaying(false);
  }, [playBgmTrack, tocarMusicaAleatoria]);

  const pauseAudiobookPlayback = useCallback(() => {
    const audiobook = audiobookRef.current;
    if (!audiobook || !audiobookSrc) return;
    try {
      audiobook.pause();
    } catch {}
    setPlayerMode("bgm");
    setIsAudiobookPaused(true);
    resumeBgmAfterAudiobook();
  }, [audiobookSrc, resumeBgmAfterAudiobook]);

  const resumeAudiobookPlayback = useCallback(() => {
    const audiobook = audiobookRef.current;
    if (!audiobook || !audiobookSrc) return;

    const bgm = audioRef.current;
    if (bgm && !bgm.paused) {
      try {
        bgm.pause();
      } catch {}
    }
    setIsMusicPlaying(false);
    setPlayerMode("audiobook");
    audiobook.play().then(() => {
      setIsAudiobookPaused(false);
    }).catch(() => {
      setIsAudiobookPaused(true);
    });
  }, [audiobookSrc]);

  const handleAcceptAudiobook = useCallback(async (lang) => {
    if (!audiobookOffer || audiobookBusy) return;
    setAudiobookBusy(true);
    try {
      let src = "";
      if (isLocalSpecialAudiobookOffer(audiobookOffer)) {
        // Aceite local reaproveita o player atual e nao chama endpoint do backend.
        src = resolveLocalAudiobookSource(audiobookOffer, lang);
        revealLocalAudiobookUnlockPhrase(
          audiobookOffer.local_offer_id || audiobookOffer.audio_id
        );
        clearActiveAudiobookOffer({
          revealNextOffer: false,
          dismissBackendOffer: false,
        });
      } else {
        const data = await acceptAudiobookOffer(
          audiobookOffer.offer_id,
          audiobookOffer.audio_id,
          lang
        );
        src = String(data?.audio_url || "");
        clearActiveAudiobookOffer({
          revealNextOffer: false,
          dismissBackendOffer: false,
        });
      }
      if (!src) {
        suspendAudiobookQueueRef.current = false;
        setAudiobookQueueVersion((prev) => prev + 1);
        return;
      }

      const bgm = audioRef.current;
      if (bgm && !bgm.paused) {
        try {
          bgm.pause();
        } catch {}
      }
      setIsMusicPlaying(false);
      audiobookAutoplayRequestedRef.current = true;
      setPlayerMode("audiobook");
      setAudiobookSrc(src);
      setIsAudiobookPaused(false);
      emitSpecialAudioTelemetry("vh_audio_offer_accepted", {
        audio_id: String(audiobookOffer.audio_id || "").trim(),
        source: isLocalSpecialAudiobookOffer(audiobookOffer) ? "local" : "backend",
        lang: String(lang || "").trim() || "pt",
      });
    } catch (err) {
      console.error("[AUDIOBOOK] Falha ao aceitar oferta:", err);
      clearActiveAudiobookOffer({
        revealNextOffer: true,
        dismissBackendOffer: false,
      });
    } finally {
      setAudiobookBusy(false);
    }
  }, [
    audiobookBusy,
    audiobookOffer,
    clearActiveAudiobookOffer,
    revealLocalAudiobookUnlockPhrase,
  ]);

  useEffect(() => {
    if (playerMode !== "audiobook" || !audiobookSrc) return;
    const audiobook = audiobookRef.current;
    if (!audiobook) return;
    if (!audiobookAutoplayRequestedRef.current) return;

    audiobookAutoplayRequestedRef.current = false;
    setIsAudiobookPaused(false);
    audiobook.currentTime = 0;
    audiobook.play().catch(() => {
      suspendAudiobookQueueRef.current = false;
      setPlayerMode("bgm");
      setAudiobookSrc(null);
      setIsAudiobookPaused(false);
      resumeBgmAfterAudiobook();
    });
  }, [audiobookSrc, playerMode, resumeBgmAfterAudiobook]);

  useEffect(() => {
    const audiobook = audiobookRef.current;
    if (!audiobook) return;

    const onEndOrError = () => {
      suspendAudiobookQueueRef.current = false;
      setPlayerMode("bgm");
      setAudiobookSrc(null);
      setIsAudiobookPaused(false);
      resumeBgmAfterAudiobook();
    };

    audiobook.onended = onEndOrError;
    audiobook.onerror = onEndOrError;
    return () => {
      audiobook.onended = null;
      audiobook.onerror = null;
    };
  }, [resumeBgmAfterAudiobook]);

  // ======= Envio de mensagem =======
  const handleSend = async () => {
    const prompt = String(input || "").trim();
    if (hasAboutFullAiAccess() && limiteAtingido) {
      setBilling(buildAboutFullBillingStatus());
      setLimiteAtingido(false);
    }
    if (!prompt || (limiteAtingido && !hasAboutFullAiAccess()) || isSending || bootstrapPending) return;
    if (geoBlocked) return;
    if (termsEnforced && !termsAccepted) {
      setTermsError(uiLang === "pt" ? "Aceite os termos para continuar." : uiLang === "es" ? "Acepte los términos para continuar." : "Accept the terms to continue.");
      setShowIntroByMemory(true);
      setIntroDismissed(false);
      return;
    }
    const gateActive = (showIntroByMemory || (termsEnforced && !termsAccepted)) && !introDismissed;
    if (gateActive || !aiConsentChecked) {
      setShowIntroByMemory(true);
      setIntroDismissed(false);
      setTermsError(uiLang === "pt" ? "Marque o aviso e clique em Prosseguir para acessar o chat." : uiLang === "es" ? "Marque el aviso y haga clic en Continuar para acceder al chat." : "Check the notice and click Proceed to access the chat.");
      return;
    }
    const isDragaoThreadForRequest =
      dragaoActive || String(activeMode || "normal") === "dragao";
    const scopedThreadMessages = isDragaoThreadForRequest ? dragaoMessages : normalMessages;
    const sessionTurns = buildSessionTurnsPayload(scopedThreadMessages, prompt);
    const newMsg = { text: prompt, sender: "user" };
    setQuickStartReopened(false);
    setQuickStartDismissed(false);
    setHunterSuggestionReopened(false);
    setHunterSuggestionDismissed(false);
    appendMessageToScopedThread(newMsg);
    setInput("");
    clearActiveAudiobookOffer({
      revealNextOffer: false,
      dismissBackendOffer: true,
    });

    const shouldShowLadyLucroVideo = /lady\s*lucro|lady\s*profit|se[ñn]ora\s*beneficio/i.test(prompt);
    if (shouldShowLadyLucroVideo) {
      ladyLucroPlayCountRef.current = 0;
      ladyLucroVisualStateRef.current =
        visualState && typeof visualState.mode === "string" ? visualState : null;
      setShowLadyLucroVideo(true);
    }

    if (!musicaIniciada) {
      if (playerMode === "audiobook" && audiobookSrc) {
        pendingBgmTrackAfterAudiobookRef.current = FIRST_QUESTION_TRACK;
      } else {
        playBgmTrack(FIRST_QUESTION_TRACK, {
          fallbackToRandom: true,
        });
      }
    } else if (!musicStopped && playerMode !== "audiobook") {
      const audio = audioRef.current;
      if (!audio || audio.paused || !audio.src) {
        tocarMusicaAleatoria();
        setMusicaIniciada(true);
      }
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    setIsSending(true);

    const newRequestId =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    waitBridgePersistRef.current = { requestId: "", text: "" };
    turnStateRef.current.requestId = newRequestId;
    turnStateRef.current.mainCompleted = false;
    waitBridgeTriggeredRef.current = false;
    setBridgeState(null);

    notifyVillainRequestStarted({
      lang: uiLang || detectUiLang(),
      backgroundAudioRefs: [audioRef, audiobookRef],
      isBlocked: () => showLadyLucroVideo === true || shouldShowLadyLucroVideo === true,
    });

    try {
      let conversationIdForRequest = String(activeConversationId || "").trim();
      const responseStyleMode =
        dragaoActive || String(activeMode || "normal") === "dragao" ? "dragao" : "normal";
      if (dragaoActive || String(activeMode || "normal") === "dragao") {
        const dragaoCid = await ensureDragaoConversationId();
        if (dragaoCid) {
          conversationIdForRequest = dragaoCid;
        }
      }
      if (!conversationIdForRequest && CONVERSATIONS_UI_ENABLED && conversationsAvailable) {
        conversationIdForRequest = await ensureConversationIdForSend(prompt);
      }
      if (!conversationIdForRequest && (dragaoActive || String(activeMode || "normal") === "dragao")) {
        conversationIdForRequest = await ensureDragaoConversationId();
      }
      const responseStyleBiasScopeForRequest = responseStyleBiasScopeKey({
        mode: responseStyleMode,
        conversationId: conversationIdForRequest,
      });
      const responseStyleBiasForRequest = String(
        responseStyleBiasByScope?.[responseStyleBiasScopeForRequest]
          || responseStyleBiasByScope?.[responseStyleBiasScope]
          || "default"
      );

      const mainRequestPromise = sendMessageToAPI(prompt, currentMode, {
        signal: controller.signal,
        requestId: newRequestId,
        conversationId: conversationIdForRequest || undefined,
        vh_rachel_enabled: vhRachelEnabled,
        session_turns: sessionTurns,
        response_style_bias: responseStyleBiasForRequest,
      });
      if (isWaitBridgeEnabled()) {
        const waitAc = new AbortController();
        waitBridgeAbortRef.current = waitAc;
        waitBridgeTimerRef.current = setTimeout(async () => {
          const requestId = newRequestId;
          if (waitBridgeTriggeredRef.current) {
            logWaitBridge("WAIT_BRIDGE_SKIPPED", { reason: "already_triggered_this_turn", requestId });
            return;
          }
          waitBridgeTriggeredRef.current = true;
          logWaitBridge("WAIT_BRIDGE_ARMED", { requestId, armDelayMs: WAIT_BRIDGE_ARM_DELAY_MS });
          const t0 = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
          logWaitBridge("WAIT_BRIDGE_FETCH_START", { requestId });
          let bridgeResult = null;
          try {
            bridgeResult = await fetchWaitBridge({
              apiBaseUrl: API_BASE_URL,
              requestId,
              question: prompt,
              lang: uiLang || detectUiLang(),
              modeHint: String(currentMode || ""),
              signal: waitAc.signal,
            });
          } catch (e) {
            const elapsedMs = Math.round(
              (typeof performance !== "undefined" && performance.now ? performance.now() : Date.now()) - t0
            );
            if (e?.name === "AbortError") {
              logWaitBridge("WAIT_BRIDGE_FETCH_ABORTED", { requestId, elapsedMs });
              return;
            }
            if (e?.name === "TimeoutError" || /timeout/i.test(String(e?.message || ""))) {
              logWaitBridge("WAIT_BRIDGE_FETCH_TIMEOUT", { requestId, elapsedMs });
            } else {
              logWaitBridge("WAIT_BRIDGE_FETCH_ERROR", { requestId, elapsedMs, name: String(e?.name || "Error") });
            }
            logWaitBridge("WAIT_BRIDGE_DISCARDED", { reason: "fetch_failed", requestId });
            return;
          }
          const elapsedMs = Math.round(
            (typeof performance !== "undefined" && performance.now ? performance.now() : Date.now()) - t0
          );
          logWaitBridge("WAIT_BRIDGE_FETCH_DONE", {
            requestId,
            elapsedMs,
            status: bridgeResult?.available ? "ok" : String(bridgeResult?.reason || "unknown"),
          });

          if (String(turnStateRef.current.requestId || "") !== requestId) {
            logWaitBridge("WAIT_BRIDGE_DISCARDED", { reason: "turn_changed", requestId });
            return;
          }
          if (turnStateRef.current.mainCompleted) {
            logWaitBridge("WAIT_BRIDGE_DISCARDED", { reason: "main_completed", requestId });
            return;
          }
          if (!bridgeResult?.available || String(bridgeResult?.request_id || "") !== requestId) {
            logWaitBridge("WAIT_BRIDGE_DISCARDED", {
              reason: bridgeResult?.available ? "request_id_mismatch" : String(bridgeResult?.reason || "invalid"),
              requestId,
            });
            return;
          }
          setBridgeState({
            available: true,
            requestId,
            bridge_text: String(bridgeResult.bridge_text || "").trim(),
          });
          waitBridgePersistRef.current = {
            requestId,
            text: String(bridgeResult.bridge_text || "").trim(),
          };
        }, WAIT_BRIDGE_ARM_DELAY_MS);
      }
      const result = await mainRequestPromise;
      const pendingBridge =
        waitBridgePersistRef.current.requestId === newRequestId
          ? String(waitBridgePersistRef.current.text || "").trim()
          : "";
      waitBridgePersistRef.current = { requestId: "", text: "" };
      turnStateRef.current.mainCompleted = true;
      cleanupWaitBridge();

      if (result?.aborted) return;
      const nextVisualState = resolveVisualStateFromPayload(result);
      if (nextVisualState) {
        setHasClimateVisualStarted(true);
        if (!shouldShowLadyLucroVideo && (!visualState || shouldApplyVisualState(visualState, nextVisualState))) {
          setVisualState(withAppliedAt(nextVisualState));
        }
      }
      const conversationIdFromResponse = String(result?.conversationId || "").trim();
      const conversationIdFinal = conversationIdFromResponse || conversationIdForRequest;
      if (conversationIdFinal) {
        setActiveConversationId(conversationIdFinal);
        if (responseStyleBiasForRequest === "direct_preferred") {
          const finalScope = responseStyleBiasScopeKey({
            mode: responseStyleMode,
            conversationId: conversationIdFinal,
          });
          writePersistedConversationResponseStyleBias(conversationIdFinal, "direct_preferred");
          setResponseStyleBiasByScope((prev) => {
            const next = { ...(prev || {}) };
            next[finalScope] = "direct_preferred";
            if (responseStyleBiasScope !== finalScope) {
              delete next[responseStyleBiasScope];
            }
            return next;
          });
        }
      }
      if (result?.status === 451 || result?.error === "jurisdiction_blocked") {
        setGeoBlocked(true);
        setGeoBlockedCountry(String(result?.country || ""));
        setGeoBlockedMessage(String(result?.text || "Servico indisponivel na sua jurisdicao."));
        return;
      }
      if (result?.status === 403 && result?.error === "terms_required") {
        setTermsEnforced(true);
        setTermsAccepted(false);
        setTermsChecked(false);
        setTermsVersion(String(result?.terms?.terms_version || termsVersion || TERMS_CONTENT_VERSION));
        setTermsError("Aceite os termos para continuar.");
        setShowIntroByMemory(true);
        setIntroDismissed(false);
        return;
      }

      const replyText = result?.text || "❌ Erro: resposta vazia do Oráculo.";
      const parsedReply = splitUltraDayHeader(replyText);
      appendMessageToScopedThread({
        text: parsedReply.bodyText,
        sender: "ia",
        mode: currentMode,
        dayLabel: parsedReply.dayLabel || null,
        usedParable: result?.usedParable === true,
        assistantMessageId: String(result?.assistantMessageId || "").trim(),
        waitBridgePrefix: pendingBridge || undefined,
      });
      const backendOfferToQueue =
        result && result.ok !== false && result?.audiobookOffer
          ? normalizeBackendAudiobookOffer(result.audiobookOffer)
          : null;
      let unlockedLocalAudioId = "";
      let unlockedLocalAudioReason = null;

      if (
        result &&
        result.ok === true &&
        VH_SPECIAL_AUDIO_UNLOCKS_ENABLED &&
        specialAudioHydrated &&
        !(dragaoActive || String(activeMode || "normal") === "dragao")
      ) {
        const {
          state: nextSpecialState,
          unlockedId,
          reason,
          relevantEvent,
          cooldownOk,
          fallbackDue,
        } = registerSuccessfulNormalTurn(specialAudioStateRef.current, {
          message: prompt,
          recentContext: scopedThreadMessages,
          hasBackendOffer: Boolean(backendOfferToQueue),
        });
        commitSpecialAudioState(nextSpecialState);
        unlockedLocalAudioId = String(unlockedId || "").trim();
        unlockedLocalAudioReason = String(reason || "").trim() || null;
        if (backendOfferToQueue) {
          console.info(
            `[SPECIAL_AUDIOBOOK_LOCAL] backend_offer=true local_skipped=true valid_turn=true count=${Number(
              nextSpecialState.interaction_count || 0
            )}`
          );
        } else {
          const effectiveReason =
            unlockedLocalAudioReason ||
            (relevantEvent && cooldownOk === false ? "cooldown_block" : "none");
          console.info(
            `[SPECIAL_AUDIOBOOK_LOCAL] backend_offer=false valid_turn=true count=${Number(
              nextSpecialState.interaction_count || 0
            )} relevant_event=${Boolean(relevantEvent)} cooldown_ok=${Boolean(
              cooldownOk
            )} fallback_due=${Boolean(fallbackDue)} offered=${Boolean(
              unlockedLocalAudioId
            )} reason=${effectiveReason}`
          );
        }
        if (unlockedLocalAudioId) {
          emitSpecialAudioTelemetry("vh_audio_special_unlocked", {
            audio_id: unlockedLocalAudioId,
            interaction_count: Number(nextSpecialState.interaction_count || 0),
            source: unlockedLocalAudioReason || "fallback",
          });
        }
      }

      if (result && result.ok === false && result.status === 402 && !hasAboutFullAiAccess()) {
        setLimiteAtingido(true);
      }

      if (conversationIdFinal && result && result.ok !== false) {
        /* Não chamar openConversationById após send: o refetch substituía normalMessages e
         * apagava waitBridgePrefix (campo só no cliente) dos turnos anteriores. */
        await refreshConversations();
        if ((dragaoActive || String(activeMode || "normal") === "dragao") && dragaoFeatureEnabled) {
          await refreshDragaoStatus(conversationIdFinal);
        }
      }

      if (backendOfferToQueue) {
        console.log("[AUDIOBOOK-OFFER] Received:", JSON.stringify(backendOfferToQueue));
        queueAudiobookOffer(backendOfferToQueue);
      }
      if (unlockedLocalAudioId) {
        const localOffer = getSpecialAudioOffer(unlockedLocalAudioId);
        if (localOffer) {
          queueAudiobookOffer(localOffer);
        }
      }
      suspendAudiobookQueueRef.current = false;
      setAudiobookQueueVersion((prev) => prev + 1);

      // Atualizar status do billing (não bloquear se falhar)
      try {
        setBillingLoading(true);
        if (hasAboutFullAiAccess()) {
          setBilling(buildAboutFullBillingStatus());
          setLimiteAtingido(false);
          return;
        }
        const st = await getBillingStatus();
        const effectiveStatus = isAboutFullBillingStatus(st) ? buildAboutFullBillingStatus() : st;
        setBilling(effectiveStatus);
        const enforced =
          effectiveStatus && String(effectiveStatus.paywall_mode || "").toLowerCase() === "enforce";
        const isPremium = isAboutFullBillingStatus(effectiveStatus);
        if (isPremium) {
          setLimiteAtingido(false);
        } else if (enforced && effectiveStatus.plan === "free" && effectiveStatus.free_used >= effectiveStatus.free_limit) {
          setLimiteAtingido(true);
        } else {
          setLimiteAtingido(false);
        }
      } catch {
        // ignore
      } finally {
        setBillingLoading(false);
      }
    } finally {
      notifyVillainResponseFinished();
      abortControllerRef.current = null;
      cleanupWaitBridge();
      if (suspendAudiobookQueueRef.current && playerMode !== "audiobook") {
        suspendAudiobookQueueRef.current = false;
        setAudiobookQueueVersion((prev) => prev + 1);
      }
      setIsSending(false);
    }
  };

  const handleStopGeneration = () => {
    cancelPendingVillainInterruption();
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    cleanupWaitBridge();
  };

  // ======= Música contínua =======
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const handleTrackEnded = () => {
      if (playerMode === "bgm" && !musicStopped && !isSwitchingTrackRef.current) {
        tocarMusicaAleatoria({ force: true });
      }
    };
    const handleTrackFault = () => {
      const failing = audio.currentSrc || audio.src || musicaAtual;
      if (failing) markTrackFailure(failing);
      if (playerMode === "bgm" && !musicStopped && !isSwitchingTrackRef.current) {
        tocarMusicaAleatoria({ force: true });
      }
    };
    audio.onended = handleTrackEnded;
    audio.onerror = handleTrackFault;
    audio.onstalled = handleTrackFault;
    audio.onabort = handleTrackFault;
    return () => {
      audio.onended = null;
      audio.onerror = null;
      audio.onstalled = null;
      audio.onabort = null;
    };
  }, [markTrackFailure, musicStopped, musicaAtual, tocarMusicaAleatoria, playerMode]);

  useEffect(() => {
    if (bootstrapPending || geoBlocked || showCinematicIntro || musicStopped || isRecording || !musicaIniciada || playerMode !== "bgm") return;
    const watchdog = setInterval(() => {
      const audio = audioRef.current;
      if (!audio) return;
      if (isSwitchingTrackRef.current) return;
      const semFonte = !audio.src;
      const pausadoSemPedido = audio.paused && !musicStoppedRef.current;
      if (semFonte || pausadoSemPedido) {
        tocarMusicaAleatoria({ force: true });
      }
    }, 4000);
    return () => clearInterval(watchdog);
  }, [bootstrapPending, geoBlocked, showCinematicIntro, musicStopped, isRecording, musicaIniciada, tocarMusicaAleatoria, playerMode]);

  const toggleMode = () =>
    setCurrentMode((p) => (p === "heroi" ? "financeiro" : "heroi"));

  const handleToggleMusic = () => {
    if (playerMode !== "bgm") return;
    const audio = audioRef.current;
    if (!audio) return;

    if (musicStopped || audio.paused) {
      if (audio.src) {
        audio.play().then(() => {
          setIsMusicPlaying(true);
        }).catch(() => {
          setIsMusicPlaying(false);
          tocarMusicaAleatoria({ force: true });
        });
        setMusicStopped(false);
        setMusicaIniciada(true);
        return;
      }
      tocarMusicaAleatoria();
      setMusicaIniciada(true);
      return;
    }

    audio.pause();
    audio.currentTime = 0;
    setMusicStopped(true);
    setIsMusicPlaying(false);
  };

  const handleToggleAudiobook = () => {
    const audiobook = audiobookRef.current;
    if (!audiobook || !audiobookSrc) return;
    if (isAudiobookPaused || audiobook.paused) {
      resumeAudiobookPlayback();
      return;
    }
    pauseAudiobookPlayback();
  };

  const handleNextMusic = () => {
    if (playerMode !== "bgm") return;
    if (musicRetryTimeoutRef.current) {
      clearTimeout(musicRetryTimeoutRef.current);
      musicRetryTimeoutRef.current = null;
    }
    setMusicaIniciada(true);
    tocarMusicaAleatoria({ force: true });
  };

  const handlePrevMusic = () => {
    if (playerMode !== "bgm") return;
    const hist = musicHistoryRef.current;
    if (hist.length === 0) return;
    if (isSwitchingTrackRef.current) return;

    const prev = hist.pop();
    setHasMusicHistory(hist.length > 0);
    const audio = audioRef.current;
    if (!audio || typeof audio.volume === "undefined") return;

    if (musicRetryTimeoutRef.current) {
      clearTimeout(musicRetryTimeoutRef.current);
      musicRetryTimeoutRef.current = null;
    }

    isSwitchingTrackRef.current = true;
    lastTrackSwitchAtRef.current = Date.now();

    try {
      audio.pause();
    } catch {}

    audio.src = prev;
    audio.currentTime = 0;
    audio.volume = VH_BGM_BASE_VOLUME;
    audio.play().then(() => {
      setIsMusicPlaying(true);
      setMusicaAtual(prev);
      setMusicStopped(false);
      isSwitchingTrackRef.current = false;
    }).catch(() => {
      setIsMusicPlaying(false);
      isSwitchingTrackRef.current = false;
      if (!musicStoppedRef.current) {
        musicRetryTimeoutRef.current = setTimeout(() => {
          tocarMusicaAleatoria({ force: true });
        }, 900);
      }
    });
  };

  // ======= Conta / Billing / Memória =======
  const handleOpenPanel = async () => {
    const memoryNamespace = String(activeMode || "normal") === "dragao" ? "dragao" : "normal";
    setShowMemory(true);
    setShowMemoryList(false); // Reset para o painel principal
    setShowSessionsMemoryPanel(false);
    setDeleteConfirmInput("");
    setMemoryContent("");
    setLoadingMemory(true);
    try {
      const [st, ms, idCard] = await Promise.allSettled([
        getBillingStatus(),
        getMemoryStatus(memoryNamespace),
        getIdentityCard(memoryNamespace),
      ]);
      if (hasAboutFullAiAccess()) {
        setBilling(buildAboutFullBillingStatus());
      } else if (st.status === "fulfilled") {
        setBilling(isAboutFullBillingStatus(st.value) ? buildAboutFullBillingStatus() : st.value);
      }
      if (ms.status === "fulfilled") {
        setMemoryStatus(ms.value);
        
        // Se memória está ligada mas consent é false, dar consent automaticamente (fix LGPD)
        if (ms.value && ms.value.memory_enabled && !ms.value.consent) {
          try {
            await setConsent(true);
            const updatedStatus = await getMemoryStatus(memoryNamespace);
            setMemoryStatus(updatedStatus);
          } catch (err) {
            console.error("Erro ao dar consent automático:", err);
          }
        }
      }
      if (idCard.status === "fulfilled" && idCard.value) {
        setIdentityCardAvailable(true);
        const c = idCard.value.card || {};
        const ev = Array.isArray(c.evitar_frases) ? c.evitar_frases : [];
        const validEv = ["evitar_faca", "evitar_voce_precisa", "evitar_o_melhor_e"];
        setIdentityCardDraft({
          tom: ["default", "curto", "normal"].includes(c.tom) ? c.tom : "default",
          formato: ["default", "bullets", "paragrafos"].includes(c.formato) ? c.formato : "default",
          evitar_frases: ev.filter((x) => validEv.includes(x)),
          nivel_detalhe: ["default", "baixo", "medio"].includes(c.nivel_detalhe) ? c.nivel_detalhe : "default",
        });
      } else {
        setIdentityCardAvailable(false);
      }
      if (CONVERSATIONS_UI_ENABLED) {
        await refreshConversations();
      }
    } catch {
      // ignore
    } finally {
      setLoadingMemory(false);
    }
  };

  const openAboutFullCheckout = useCallback(() => {
    window.open(VH_ABOUT_FULL, "_blank", "noopener,noreferrer");
  }, []);

  const openAboutLogin = useCallback(() => {
    window.open(getAboutLoginUrl(uiLang || detectUiLang()), "_blank", "noopener,noreferrer");
  }, [uiLang]);
  const openAboutRecover = useCallback(() => {
    window.open(getAboutRecoverUrl(uiLang || detectUiLang()), "_blank", "noopener,noreferrer");
  }, [uiLang]);

  const handleEuConsentConfirm = () => {
    if (!euConsentTermsChecked || !euConsentWithdrawalChecked) return;
    setShowEuConsentModal(false);
    openAboutFullCheckout();
  };

  const handleToggleMemory = async () => {
    if (!memoryStatus) return;
    const memoryNamespace = String(activeMode || "normal") === "dragao" ? "dragao" : "normal";
    setLoadingMemory(true);
    try {
      const enabled = !(memoryStatus.memory_enabled === true);
      
      // Ao LIGAR: sempre garantir consent=true primeiro
      if (enabled) {
        console.log("[MEMORY] Ligando memória: dando consent primeiro...");
        await setConsent(true);
        console.log("[MEMORY] Consent OK, agora ligando memory_enabled...");
        await setMemoryEnabled(true, memoryNamespace);
      } else {
        // Ao DESLIGAR: apenas desliga memory_enabled (consent permanece)
        console.log("[MEMORY] Desligando memória (consent permanece)...");
        await setMemoryEnabled(false, memoryNamespace);
      }
      
      // Recarregar status do servidor para confirmar
      console.log("[MEMORY] Recarregando status do servidor...");
      const newStatus = await getMemoryStatus(memoryNamespace);
      console.log("[MEMORY] Novo status:", newStatus);
      setMemoryStatus(newStatus);
      
      // Validação: se ligou mas consent ainda está false, avisar
      const lang = uiLang || detectUiLang();
      if (enabled && !newStatus.consent) {
        setMemoryContent("⚠️ " + tr(lang, "memoryConsentFail"));
      } else {
        setMemoryContent(enabled 
          ? "✅ " + tr(lang, "memoryOnOk") 
          : "✅ " + tr(lang, "memoryOffOk"));
      }
    } catch (err) {
      console.error("Erro ao atualizar memória:", err);
      setMemoryContent("❌ " + tr(uiLang || detectUiLang(), "memoryError") + ": " + (err.message || err));
    } finally {
      setLoadingMemory(false);
    }
  };

  const handleClearHistory = async () => {
    if (!window.confirm(tr(uiLang || detectUiLang(), "clearHistoryConfirm"))) return;
    const memoryNamespace = String(activeMode || "normal") === "dragao" ? "dragao" : "normal";
    setLoadingMemory(true);
    try {
      await trimMemoryHistory(0, memoryNamespace);
      setMemoryContent("✅ " + tr(uiLang || detectUiLang(), "historyDeleted"));
    } catch {
      setMemoryContent("❌ " + tr(uiLang || detectUiLang(), "historyError"));
    } finally {
      setLoadingMemory(false);
    }
  };

  const handleTrimHistory = async () => {
    const memoryNamespace = String(activeMode || "normal") === "dragao" ? "dragao" : "normal";
    setLoadingMemory(true);
    try {
      await trimMemoryHistory(keepLastN, memoryNamespace);
      setMemoryContent("✅ " + tr(uiLang || detectUiLang(), "historyTrimmed", { n: keepLastN }));
    } catch {
      setMemoryContent("❌ " + tr(uiLang || detectUiLang(), "historyError"));
    } finally {
      setLoadingMemory(false);
    }
  };

  const handleDeleteSummaries = async () => {
    const memoryNamespace = String(activeMode || "normal") === "dragao" ? "dragao" : "normal";
    setLoadingMemory(true);
    try {
      await deleteMemorySummaries(memoryNamespace);
      setMemoryContent("✅ " + tr(uiLang || detectUiLang(), "summariesDeleted"));
    } catch {
      setMemoryContent("❌ " + tr(uiLang || detectUiLang(), "summariesError"));
    } finally {
      setLoadingMemory(false);
    }
  };

  const handleIdentityCardSave = async () => {
    if (!identityCardAvailable) return;
    const memoryNamespace = String(activeMode || "normal") === "dragao" ? "dragao" : "normal";
    setLoadingMemory(true);
    try {
      const card = {
        tom: identityCardDraft.tom,
        formato: identityCardDraft.formato,
        evitar_frases: identityCardDraft.evitar_frases || [],
        nivel_detalhe: identityCardDraft.nivel_detalhe,
      };
      await postIdentityCard(card, memoryNamespace);
      setMemoryContent(tr(uiLang || detectUiLang(), "cardSaved"));
    } catch (err) {
      setMemoryContent(tr(uiLang || detectUiLang(), "cardSaveFail"));
    } finally {
      setLoadingMemory(false);
    }
  };

  const handleIdentityCardRestore = () => {
    setIdentityCardDraft({ tom: "default", formato: "default", evitar_frases: [], nivel_detalhe: "default" });
    setMemoryContent(tr(uiLang || detectUiLang(), "formCleared"));
  };

  const _toggleEvitar = (key) => {
    const ev = identityCardDraft.evitar_frases || [];
    const has = ev.includes(key);
    setIdentityCardDraft((d) => ({
      ...d,
      evitar_frases: has ? ev.filter((x) => x !== key) : [...ev, key],
    }));
  };

  const handleIdentityCardClear = async () => {
    if (!identityCardAvailable) return;
    const memoryNamespace = String(activeMode || "normal") === "dragao" ? "dragao" : "normal";
    setLoadingMemory(true);
    try {
      try {
        await deleteIdentityCard(memoryNamespace);
      } catch {
        await postIdentityCard({}, memoryNamespace);
      }
      setIdentityCardDraft({ tom: "default", formato: "default", evitar_frases: [], nivel_detalhe: "default" });
      setMemoryContent(tr(uiLang || detectUiLang(), "cardCleared"));
    } catch (err) {
      setMemoryContent(tr(uiLang || detectUiLang(), "cardClearFail"));
    } finally {
      setLoadingMemory(false);
    }
  };

  const handleForgetMe = async () => {
    if (!window.confirm(tr(uiLang || detectUiLang(), "forgetConfirm"))) return;
    setLoadingMemory(true);
    const oldDragaoKey = dragaoHomeCidKey();
    const oldSpecialAudioUid = String(
      specialAudioUidRef.current || getBoundUserId() || ""
    ).trim();
    try {
      await forgetMe();
      clearStoredIdentity();
      clearAllPersistedConversationResponseStyleBias();
      if (oldSpecialAudioUid) {
        clearSpecialAudioState(oldSpecialAudioUid);
      }
      if (oldDragaoKey) {
        try {
          localStorage.removeItem(oldDragaoKey);
        } catch {}
      }
      setActiveMode("normal");
      setDragaoActive(false);
      setDragaoState(null);
      setDragaoQuestions(null);
      setDragaoRecentLog([]);
      setDragaoHomeConversationId("");
      setMessages([]);
      setNormalMessages([]);
      setDragaoMessages([]);
      setConversations([]);
      setActiveConversationId("");
      setResponseStyleBiasByScope({});
      setShortConclusionByMessageId({});
      setShortConclusionLoadingId("");
      setExpandedShortConclusionByMessageId({});
      setConversationSelection({});
      setAudiobookOffer(null);
      currentAudiobookOfferRef.current = null;
      pendingBgmTrackAfterAudiobookRef.current = "";
      audiobookQueueRef.current = [];
      suspendAudiobookQueueRef.current = false;
      specialAudioUidRef.current = "";
      setAudiobookQueueVersion(0);
      setSpecialAudioState(createDefaultSpecialAudioState());
      setSpecialAudioHydrated(false);
      setLimiteAtingido(false);
      setBilling(null);
      setMemoryStatus(null);
      setMemoryContent("✅ " + tr(uiLang || detectUiLang(), "forgetDone"));
    } catch {
      setMemoryContent("❌ " + tr(uiLang || detectUiLang(), "forgetError"));
    } finally {
      setLoadingMemory(false);
    }
  };

  const handleOpenMemoryList = async () => {
    const memoryNamespace = String(activeMode || "normal") === "dragao" ? "dragao" : "normal";
    setLoadingMemory(true);
    try {
      if (!getBoundUserId()) {
        await bootstrapSession();
      }
      const data = await getMemoryList(memoryNamespace);
      setMemoryListItems(data.items || []);
      setShowSessionsMemoryPanel(false);
      setShowMemoryList(true);
      setExpandedMemoryIndex(null);
    } catch (err) {
      alert("❌ " + tr(uiLang || detectUiLang(), "loadMemoriesError"));
      console.error(err);
    } finally {
      setLoadingMemory(false);
    }
  };

  const handleDeleteMemoryItem = async (index) => {
    if (!window.confirm(tr(uiLang || detectUiLang(), "deleteMemoryConfirm", { n: index }))) return;
    const memoryNamespace = String(activeMode || "normal") === "dragao" ? "dragao" : "normal";
    setLoadingMemory(true);
    try {
      await deleteMemoryItem(index, memoryNamespace);
      // Refetch para garantir re-indexação correta
      const data = await getMemoryList(memoryNamespace);
      setMemoryListItems(data.items || []);
      setExpandedMemoryIndex(null);
    } catch (err) {
      alert("❌ " + tr(uiLang || detectUiLang(), "deleteMemoryError"));
      console.error(err);
    } finally {
      setLoadingMemory(false);
    }
  };

  // ======= Upload real =======
  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    const boundUid = getBoundUserId() || "";
    const boundEmail = getBoundEmail();
    if (boundEmail) {
      formData.append("email", boundEmail);
    }
    formData.append("user_id", boundUid);
    formData.append("file", file);

    try {
      const response = await fetch(`${API_BASE_URL}/upload`, {
        method: "POST",
        headers: (() => {
          const h = { "X-VH-Language": navigator.language || "pt-BR" };
          const uid = boundUid;
          const em = boundEmail;
          if (uid) h["X-VH-User-ID"] = uid;
          if (em) h["X-VH-User-Email"] = em;
          return h;
        })(),
        body: formData,
      });
      const data = await response.json();
      const uploadLang = uiLang || detectUiLang();
      appendMessageToScopedThread({
        text: `📄 ${data.mensagem || tr(uploadLang, "uploadDone") + ": " + file.name}`,
        sender: "upload",
      });
    } catch (err) {
      console.error("Erro no upload:", err);
      appendMessageToScopedThread({
        text: "❌ " + tr(uiLang || detectUiLang(), "uploadError"),
        sender: "upload",
      });
    }

    e.target.value = "";
  };

  const handleAcceptTermsGate = useCallback(async () => {
    const effectiveTermsVersion = String(termsVersion || TERMS_CONTENT_VERSION).trim();
    if (!aiConsentChecked) {
      setTermsError(uiLang === "pt" ? "Marque o aviso da IA para prosseguir." : uiLang === "es" ? "Marque el aviso de IA para continuar." : "Check the AI notice to continue.");
      return;
    }
    if (!termsEnforced) {
      setTermsAccepted(true);
      setTermsError("");
      setIntroDismissed(true);
      return;
    }
    if (!termsChecked) {
      setTermsError(uiLang === "pt" ? "Marque \"Li e aceito\" para prosseguir." : uiLang === "es" ? "Marque \"He leído y acepto\" para continuar." : "Check \"I have read and accept\" to continue.");
      return;
    }
    setTermsBusy(true);
    setTermsError("");
    try {
      await acceptTerms(effectiveTermsVersion || undefined);
      const st = await getTermsStatus();
      const accepted = Boolean(st?.terms?.accepted);
      const v = String(st?.terms?.terms_version || effectiveTermsVersion || "");
      setTermsAccepted(accepted);
      setTermsVersion(v);
      if (!accepted) {
        setTermsError(uiLang === "pt" ? "Nao foi possivel confirmar o aceite dos termos." : uiLang === "es" ? "No se pudo confirmar la aceptación." : "Could not confirm acceptance.");
        return;
      }
      setIntroDismissed(true);
    } catch (err) {
      setTermsError(uiLang === "pt" ? "Falha ao registrar aceite dos termos." : uiLang === "es" ? "Error al registrar aceptación." : "Failed to register acceptance.");
      console.error("[TERMS] accept failed:", err);
    } finally {
      setTermsBusy(false);
    }
  }, [aiConsentChecked, termsChecked, termsEnforced, termsVersion, uiLang]);

  const handleDismissIntroWarning = useCallback(() => {
    if (!aiConsentChecked) {
      setTermsError(uiLang === "pt" ? "Marque o aviso da IA para prosseguir." : uiLang === "es" ? "Marque el aviso de IA para continuar." : "Check the AI notice to continue.");
      return;
    }
    if (termsEnforced && !termsAccepted) {
      setTermsError(uiLang === "pt" ? "Marque e confirme o aceite dos termos para continuar." : uiLang === "es" ? "Marque y confirme la aceptación para continuar." : "Check and confirm acceptance of terms to continue.");
      return;
    }
    setIntroDismissed(true);
  }, [aiConsentChecked, termsAccepted, termsEnforced, uiLang]);

  const handleLangChange = useCallback((code) => {
    const val = code === "pt" ? "pt-BR" : code === "es" ? "es-ES" : "en-US";
    setUiLang(code);
    try {
      sessionStorage.setItem("vh_ui_lang", val);
    } catch {}
    setVoiceLang(val);
  }, [setVoiceLang]);

  // ======= PÁGINAS LEGAIS (quando não logado — visitante direto em /legal etc.)
  // Quando logado, o return do chat trata as páginas legais no mesmo tree para manter a música.
  const showAnyLegalPage = isLegalRouteActive;
  const showIntro = (showIntroByMemory || (termsEnforced && !termsAccepted)) && !introDismissed;
  const introGateBlocking = showIntro && !showAnyLegalPage;
  const appReady = !bootstrapPending;
  const isMainChatVisible =
    appReady &&
    !showCinematicIntro &&
    !geoBlocked &&
    !showAnyLegalPage &&
    !showChatroomsPage;

  const maybeShowNextQueuedAudiobookOffer = useCallback(() => {
    if (!isMainChatVisible) return;
    if (loadingConversationMessages) return;
    if (playerMode === "audiobook") return;
    if (suspendAudiobookQueueRef.current) return;
    if (currentAudiobookOfferRef.current) return;

    const { offer, queue } = pullNextQueuedAudiobookOffer(audiobookQueueRef.current, {
      allowLocal:
        !dragaoActive && String(activeMode || "normal") !== "dragao",
    });
    if (!offer) return;

    audiobookQueueRef.current = queue;
    setAudiobookQueueVersion((prev) => prev + 1);
    activateAudiobookOffer(offer);
  }, [
    activateAudiobookOffer,
    activeMode,
    dragaoActive,
    isMainChatVisible,
    loadingConversationMessages,
    playerMode,
  ]);

  const syncPrologueAudiobookOffer = useCallback(() => {
    if (!PROLOGUE_AUTO_UNLOCK || !specialAudioHydrated) return;

    if (!specialAudioStateRef.current.prologue_unlocked) {
      const { state: nextState, unlockedId } = ensureAutoPrologueUnlocked(
        specialAudioStateRef.current
      );
      if (unlockedId) {
        commitSpecialAudioState(nextState);
        emitSpecialAudioTelemetry("vh_audio_special_unlocked", {
          audio_id: unlockedId,
          interaction_count: Number(nextState.interaction_count || 0),
          source: "auto_prologue",
        });
      }
    } else {
      const repairedState = ensurePrologueOfferPending(specialAudioStateRef.current);
      const hadPending = specialAudioStateRef.current.pending_local_offer_ids.includes(
        PROLOGUE_AUDIO_ID
      );
      const hasPending = repairedState.pending_local_offer_ids.includes(PROLOGUE_AUDIO_ID);
      if (hasPending && !hadPending) {
        commitSpecialAudioState(repairedState);
      }
    }

    const pendingPrologueOffer = getPendingPrologueOffer(specialAudioStateRef.current);
    if (pendingPrologueOffer) {
      queueAudiobookOffer(pendingPrologueOffer);
    }

    if (VH_SPECIAL_AUDIO_UNLOCKS_ENABLED) {
      queuePendingLocalAudiobookOffers();
    }

    if (!isMainChatVisible || loadingConversationMessages || introGateBlocking || !introGateResolved) return;

    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => {
        maybeShowNextQueuedAudiobookOffer();
      });
      return;
    }
    maybeShowNextQueuedAudiobookOffer();
  }, [
    commitSpecialAudioState,
    introGateBlocking,
    introGateResolved,
    isMainChatVisible,
    loadingConversationMessages,
    maybeShowNextQueuedAudiobookOffer,
    queueAudiobookOffer,
    queuePendingLocalAudiobookOffers,
    specialAudioHydrated,
  ]);

  useEffect(() => {
    if (isMainChatVisible && !wasMainChatVisibleRef.current) {
      hasEnteredMainAIRef.current = true;
      const suggestionPool = getHunterSuggestionsByLocale(uiLang || detectUiLang());
      setSelectedHunterSuggestion(
        getRandomHunterSuggestion(suggestionPool, selectedHunterSuggestion)
      );
      setHasUserStartedTyping(String(input || "").length > 0);
      syncPrologueAudiobookOffer();
    } else if (isMainChatVisible) {
      const suggestionPool = getHunterSuggestionsByLocale(uiLang || detectUiLang());
      if (
        suggestionPool.length > 0 &&
        (!selectedHunterSuggestion || !suggestionPool.includes(selectedHunterSuggestion))
      ) {
        setSelectedHunterSuggestion(
          getRandomHunterSuggestion(suggestionPool, selectedHunterSuggestion)
        );
      }
    }

    wasMainChatVisibleRef.current = isMainChatVisible;
  }, [
    bootstrapPending,
    geoBlocked,
    input,
    isMainChatVisible,
    selectedHunterSuggestion,
    showAnyLegalPage,
    showChatroomsPage,
    showCinematicIntro,
    uiLang,
    syncPrologueAudiobookOffer,
  ]);

  useEffect(() => {
    syncPrologueAudiobookOffer();
  }, [
    audiobookQueueVersion,
    introDismissed,
    introGateResolved,
    isMainChatVisible,
    loadingConversationMessages,
    playerMode,
    showIntro,
    specialAudioHydrated,
    syncPrologueAudiobookOffer,
  ]);

  useEffect(() => {
    if (!hasUserStartedTyping && String(input || "").length > 0) {
      setHasUserStartedTyping(true);
    }
  }, [hasUserStartedTyping, input]);

  useEffect(() => {
    setQuickStartReopened(false);
    setQuickStartDismissed(false);
    setHunterSuggestionReopened(false);
    setHunterSuggestionDismissed(false);
  }, [activeConversationId, activeMode]);

  if (showAnyLegalPage && bootstrapPending) {
    return (
      <div className="app gothic-text">
        <audio ref={audioRef} hidden preload="auto" />
        <audio ref={audiobookRef} hidden preload="auto" src={audiobookSrc || undefined} />
        <div
          className="background"
          style={{ backgroundImage: "url('/fotos/vanhelsin.jpg')" }}
        />
        {showLegalPage && <LegalStaticPage onLangChange={handleLangChange} onNavigate={navigateTo} returnPath={legalReturnPath} returnTarget={legalReturnTarget} />}
        {showPrivacyPage && <PrivacyStaticPage onLangChange={handleLangChange} onNavigate={navigateTo} returnPath={legalReturnPath} returnTarget={legalReturnTarget} />}
        {showTermsPage && <TermsStaticPage onLangChange={handleLangChange} onNavigate={navigateTo} returnPath={legalReturnPath} returnTarget={legalReturnTarget} />}
        {showPricingPage && <PricingStaticPage onLangChange={handleLangChange} onNavigate={navigateTo} returnPath={legalReturnPath} returnTarget={legalReturnTarget} />}
        {showRefundPolicyPage && <RefundPolicyStaticPage onLangChange={handleLangChange} onNavigate={navigateTo} returnPath={legalReturnPath} returnTarget={legalReturnTarget} />}
        {showDataRequestPage && <DataRequestStaticPage onLangChange={handleLangChange} onNavigate={navigateTo} returnPath={legalReturnPath} returnTarget={legalReturnTarget} />}
        {showAboutPage && <AboutStaticPage onLangChange={handleLangChange} onNavigate={navigateTo} returnPath={legalReturnPath} returnTarget={legalReturnTarget} />}
        {showArquivoPage && <ArquivoConfidencialPage onLangChange={handleLangChange} onNavigate={navigateTo} returnPath={legalReturnPath} returnTarget={legalReturnTarget} />}
      </div>
    );
  }

  // ======= INTRO =======
  if (showCinematicIntro) {
    return (
      <div className={`intro-container ${fadeIntro ? "fade-out" : ""}`}>
        {/* 🎬 VÍDEO (R2) — aparece 4s após trovão */}
        <video
          ref={videoRef}
          className="intro-video"
          src={videoURL}
          muted
          autoPlay={showVideo}
          playsInline
          preload="auto"
          onEnded={finishIntro}
          onCanPlay={() => {
            if (showVideo) playIntroVideoSafely();
          }}
          onError={(e) => {
            console.error("❌ Erro no vídeo:", e);
            finishIntro();
          }}
          style={{
            filter: "brightness(0.85)",
            opacity: showVideo ? 1 : 0,
            transition: "opacity 1s ease-in-out",
            zIndex: 1,
          }}
        />

        <div id="flash" className="flash-effect"></div>

        {!invokeClicked && (
          <div className="invoke-btn-container fade-in">
            <button
              className="invoke-btn"
              onClick={() => {
                setInvokeClicked(true);
                try {
                  const thunder = new Audio(
                    process.env.PUBLIC_URL + "/sounds/thunder.mp3"
                  );
                  thunder.volume = 1.0;
                  thunder.play().then(() => {
                    thunder.currentTime = 0;
                    thunder.play();
                    setTimeout(() => {
                      const echo = new Audio(
                        process.env.PUBLIC_URL + "/sounds/thunder.mp3"
                      );
                      echo.volume = 0.6;
                      echo.play().catch(() => {});
                    }, 600);
                  });
                } catch {}
                const flash = document.getElementById("flash");
                if (flash) {
                  flash.classList.add("active");
                  setTimeout(() => flash.classList.remove("active"), 400);
                }
                document.body.classList.add("shake");
                setTimeout(() => document.body.classList.remove("shake"), 500);

                if (introInvokeDelayTimerRef.current != null) {
                  clearTimeout(introInvokeDelayTimerRef.current);
                }
                introInvokeDelayTimerRef.current = window.setTimeout(() => {
                  introInvokeDelayTimerRef.current = null;
                  setShowVideo(true);
                  playIntroVideoSafely();
                }, 4000);
              }}
            >
              ⚡ {tr(detectUiLang(), "invokeBtn")} ⚡
            </button>
          </div>
        )}

        {invokeClicked && !showVideo ? (
          <p className="intro-preparing-hint" aria-live="polite">
            {tr(detectUiLang(), "introPreparing")}
          </p>
        ) : null}

        {/* 🔘 Botão “Pular Intro” (fixo sobre o vídeo) */}
        <div className="intro-buttons" style={{ zIndex: 4001 }}>
          <button className="skip-button gothic-btn" onClick={finishIntro}>
            {tr(detectUiLang(), "skipIntro")}
          </button>
        </div>
        {showLoginRoute ? (
          <div
            style={{
              position: "fixed",
              left: "16px",
              right: "16px",
              bottom: "20px",
              zIndex: 4002,
              maxWidth: "640px",
              margin: "0 auto",
            }}
          >
            <LoginAccessRecoverBridge
              lang={detectUiLang()}
              onRecover={openAboutRecover}
            />
          </div>
        ) : null}
      </div>
    );
  }

  if (bootstrapPending) {
    const bpLang = uiLang || detectUiLang();
    return (
      <div className="legal-page gothic-text">
        <h1>{tr(bpLang, "validatingJurisdiction")}</h1>
        <p>{tr(bpLang, "validatingWait")}</p>
      </div>
    );
  }

  if (geoBlocked) {
    const glLang = uiLang || detectUiLang();
    return (
      <div className="legal-page gothic-text">
        <h1>{tr(glLang, "geoUnavailable")}</h1>
        <p>{geoBlockedMessage || tr(glLang, "geoUnavailable")}.</p>
        <p>{tr(glLang, "geoCountry")}: {geoBlockedCountry || "N/D"}.</p>
        <p>{tr(glLang, "geoBrEuOnly")}</p>
        <a className="legal-back-link" href="/legal" onClick={(e) => { e.preventDefault(); navigateTo("/legal"); }}>{tr(glLang, "viewLegal")}</a>
      </div>
    );
  }

  if (showChatroomsPage) {
    return (
      <div
        className="app gothic-text vh-chatrooms-app vh-chatrooms-host"
        style={{ "--vh-font-scale": String(fontScale) }}
      >
        <audio ref={audioRef} hidden preload="auto" />
        <audio ref={audiobookRef} hidden preload="auto" src={audiobookSrc || undefined} />
        <VHChatRoomInterface
          onNavigate={navigateTo}
          uiLang={uiLang || detectUiLang()}
          disableLocalAudio
        />
      </div>
    );
  }

  // ======= CHAT =======
  const currentLang = uiLang || detectUiLang();
  const L = currentLang;
  const legalFooterLabel = currentLang === "pt"
    ? "Aviso Legal e Limites da IA"
    : currentLang === "es"
    ? "Aviso Legal y Límites de la IA"
    : "Legal Notice and AI Limits";
  const hunterSuggestionPool = getHunterSuggestionsByLocale(L);
  const quickStartOptions = [
    { id: "clarity", label: tr(L, "quickStartClarity"), prefix: tr(L, "quickStartClarityPrefix") },
    { id: "direct", label: tr(L, "quickStartDirect"), prefix: tr(L, "quickStartDirectPrefix") },
    { id: "judgment", label: tr(L, "quickStartJudgment"), prefix: tr(L, "quickStartJudgmentPrefix") },
  ];
  const hasAudiobookLoaded = Boolean(audiobookSrc);
  const musicStatusText = playerMode === "audiobook"
    ? tr(L, "audiobookActive")
    : musicStopped
    ? tr(L, "musicPaused")
    : isMusicPlaying
    ? tr(L, "musicActive")
    : musicaIniciada
    ? tr(L, "musicReconnecting")
    : tr(L, "musicWaiting");
  const audiobookToggleLabel = isAudiobookPaused
    ? tr(L, "resumeAudiobook")
    : tr(L, "stopAudiobook");
  const musicToggleLabel = musicStopped ? tr(L, "resumeMusic") : tr(L, "stopMusic");
  const sessionDayLabel = (() => {
    for (let i = 0; i < messages.length; i += 1) {
      const msg = messages[i];
      if (!msg || msg.sender !== "ia") continue;
      if (typeof msg.dayLabel === "string" && msg.dayLabel.trim()) return msg.dayLabel.trim();
      const parsed = splitUltraDayHeader(msg.text);
      if (parsed.dayLabel) return parsed.dayLabel;
    }
    return "";
  })();
  const lockFutureControls = true;
  const sttSupported = typeof window !== "undefined" && isSTTSupported();
  const shouldShowHunterSuggestion =
    !showAnyLegalPage &&
    !showIntro &&
    !showCinematicIntro &&
    String(input || "").length === 0 &&
    Boolean(selectedHunterSuggestion) &&
    !hunterSuggestionDismissed &&
    (!hasUserStartedTyping || hunterSuggestionReopened);
  const shouldShowHunterSuggestionReopenLink = shouldShowHunterSuggestionReopen({
    hunterSuggestionVisible: shouldShowHunterSuggestion,
    hasSuggestion: Boolean(selectedHunterSuggestion),
    inputValue: input,
    hasUserStartedTyping,
    hunterSuggestionDismissed,
    showAnyLegalPage,
    showIntro,
    showCinematicIntro,
  });
  const shouldShowQuickStart =
    !dragaoActive &&
    String(activeMode || "normal") !== "dragao" &&
    shouldShowQuickStartGuide({
      hasValidUserMessage,
      quickStartReopened,
      quickStartDismissed,
      showAnyLegalPage,
      showIntro,
      showCinematicIntro,
    });
  const shouldShowQuickStartReopenLink =
    !dragaoActive &&
    String(activeMode || "normal") !== "dragao" &&
    shouldShowQuickStartReopen({
      hasValidUserMessage,
      quickStartVisible: shouldShowQuickStart,
      quickStartDismissed,
      showAnyLegalPage,
      showIntro,
      showCinematicIntro,
    });
  const handleInputChange = (event) => {
    const nextValue = event.target.value;
    if (!hasUserStartedTyping && nextValue.length > 0) {
      setHasUserStartedTyping(true);
    }
    if (nextValue.length > 0 && hunterSuggestionReopened) {
      setHunterSuggestionReopened(false);
    }
    setInput(nextValue);
  };
  const handleRefreshHunterSuggestion = () => {
    setSelectedHunterSuggestion((current) =>
      getRandomHunterSuggestion(hunterSuggestionPool, current)
    );
  };
  const handleApplyHunterSuggestion = () => {
    const suggestionText = String(selectedHunterSuggestion || "").trim();
    if (!suggestionText) return;
    setHunterSuggestionDismissed(false);
    setHunterSuggestionReopened(false);
    setInput(suggestionText);
    setHasUserStartedTyping(true);
    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => {
        const field = inputRef.current;
        if (!field) return;
        field.focus();
        try {
          field.setSelectionRange(suggestionText.length, suggestionText.length);
        } catch {}
      });
    }
  };
  const focusInputAtEnd = (nextValue = null) => {
    if (typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") return;
    window.requestAnimationFrame(() => {
      const field = inputRef.current;
      if (!field) return;
      field.focus();
      const targetText = typeof nextValue === "string" ? nextValue : String(field.value || "");
      const caret = targetText.length;
      try {
        field.setSelectionRange(caret, caret);
      } catch {}
    });
  };
  const handleQuickStartApply = (prefix) => {
    setInput((current) => {
      const nextValue = buildQuickStartInputValue(current, prefix);
      focusInputAtEnd(nextValue);
      return nextValue;
    });
    setQuickStartDismissed(false);
    setHasUserStartedTyping(true);
  };
  const handleReopenQuickStart = () => {
    setQuickStartDismissed(false);
    setQuickStartReopened(true);
    focusInputAtEnd();
  };
  const handleDismissQuickStart = () => {
    setQuickStartReopened(false);
    setQuickStartDismissed(true);
  };
  const handleReopenHunterSuggestion = () => {
    setHunterSuggestionDismissed(false);
    setHunterSuggestionReopened(true);
    focusInputAtEnd();
  };
  const handleDismissHunterSuggestion = () => {
    setHunterSuggestionReopened(false);
    setHunterSuggestionDismissed(true);
  };
  const hideFutureTopControls = lockFutureControls === true;
  const sidebarEnabled = CONVERSATIONS_UI_ENABLED && conversationsAvailable;
  const isDragaoUiMode = dragaoActive || String(activeMode || "normal") === "dragao";
  const dragaoConversationScopeId = String(
    dragaoHomeConversationId || activeConversationId || ""
  ).trim();
  const visibleConversations = isDragaoUiMode
    ? conversations.filter(
        (item) => String(item?.id || "").trim() === dragaoConversationScopeId
      )
    : conversations;
  const selectedConversationMessageIds = Object.keys(conversationSelection).filter(
    (k) => conversationSelection[k]
  );
  const dragaoStage = String(dragaoState?.stage || "idle");
  const dragaoPilarLabel =
    dragaoState?.pilar_label ||
    `P${Number(dragaoState?.pilar_focus || 1)}`;
  const dragaoCelebrationPieceLabel = dragaoCelebration?.idx
    ? `Peca ${dragaoCelebration.idx} desbloqueada`
    : "";
  const memoryNamespaceUiLabel = String(activeMode || "normal") === "dragao" ? "Dragao" : "Normal";
  const deleteConfirmWord = convT(L, "confirmWord");
  const canDeleteAllConversations =
    String(deleteConfirmInput || "").trim().toUpperCase() ===
    String(deleteConfirmWord || "").trim().toUpperCase();
  const fullAiUnlocked = isAboutFullBillingStatus(billing);
  const inputBlockedByLimit = limiteAtingido && !fullAiUnlocked;
  const topAccessStatusText = billing
    ? fullAiUnlocked
      ? tr(L, "loggedFull")
      : billing.plan === "paid"
      ? tr(L, "planPaid", { date: billing.plan_expires_at_iso || "?" })
      : String(billing.paywall_mode || "").toLowerCase() === "enforce"
      ? tr(L, "planFree", { left: Math.max(0, billing.free_limit - billing.free_used), total: billing.free_limit })
      : tr(L, "accessReleased")
    : billingLoading
    ? tr(L, "loading")
    : "";
  const topAccessStatusSymbol = billingLoading ? "…" : "✓";
  const topMusicStatusSymbol = musicStopped ? "🔇" : "🔊";
  const topDayStatusSymbol = sessionDayLabel ? "◔" : "";
  const topDragaoStatusSymbol = dragaoActive ? "🐉" : "";
  const renderTopMenuContents = () => (
    <div className="top-actions vh-menu-contents">
      <div className="top-status">
        <span
          className={`top-status-text top-status-badge ${fullAiUnlocked ? "is-full-access" : ""}`}
          title={topAccessStatusText}
          aria-label={topAccessStatusText}
        >
          <span className="top-status-full">{topAccessStatusText}</span>
          <span className="top-status-compact" aria-hidden="true">{topAccessStatusSymbol}</span>
        </span>
        <span
          className="music-status-badge"
          title={musicStatusText}
          aria-label={musicStatusText}
        >
          <span className="top-status-full">{musicStatusText}</span>
          <span className="top-status-compact" aria-hidden="true">{topMusicStatusSymbol}</span>
        </span>
        {sessionDayLabel ? (
          <span
            className="day-status-badge"
            title={sessionDayLabel}
            aria-label={sessionDayLabel}
          >
            <span className="top-status-full">{sessionDayLabel}</span>
            <span className="top-status-compact" aria-hidden="true">{topDayStatusSymbol}</span>
          </span>
        ) : null}
        {dragaoFeatureEnabled ? (
          <span
            className={`dragao-status-badge ${dragaoActive ? "is-active" : "is-inactive"}`}
            title={dragaoActive ? "DRAGAO ATIVO" : "Dragao off"}
            aria-label={dragaoActive ? "DRAGAO ATIVO" : "Dragao off"}
          >
            <span className="top-status-full">{dragaoActive ? "DRAGAO ATIVO" : "Dragao off"}</span>
            <span className="top-status-compact" aria-hidden="true">{topDragaoStatusSymbol}</span>
          </span>
        ) : null}
      </div>
      {!hideFutureTopControls ? (
        <span className="hint-wrap" data-hint="Função futura: alternar entre as personas de IA.">
          <button
            className={`btn gothic-btn ${lockFutureControls ? "btn-beta-disabled" : ""}`}
            onClick={toggleMode}
            disabled={lockFutureControls}
          >
            {tr(L, "switchAI")}
          </button>
        </span>
      ) : null}
      <span className="hint-wrap" data-hint={tr(L, "ctaLoginTooltip")}>
        <button
          type="button"
          className="btn gothic-btn"
          onClick={openAboutLogin}
        >
          {tr(L, "ctaEnterArchive")}
        </button>
      </span>
      <span className="hint-wrap" data-hint={tr(L, "ctaObtainFullSeal")}>
        <button
          className="btn gothic-btn"
          onClick={openAboutFullCheckout}
          title={tr(L, "ctaObtainFullSeal")}
        >
          {billing && (billing.plan === "paid" || isAboutFullBillingStatus(billing)) ? tr(L, "planActive") : tr(L, "ctaObtainFullSeal")}
        </button>
      </span>
      <span className="hint-wrap" data-hint={tr(L, "rachelTooltip")}>
        <button
          type="button"
          className={`btn gothic-btn ${!vhRachelEnabled ? "btn-rachel-off" : ""}`}
          onClick={() => setVhRachelEnabled((v) => !v)}
          title={tr(L, "rachelTooltip")}
        >
          {vhRachelEnabled ? tr(L, "rachelOn") : tr(L, "rachelOff")}
        </button>
      </span>
      <span className="hint-wrap" data-hint="Abre o painel de conta e gerenciamento de memória.">
        <button
          className="btn gothic-btn"
          onClick={handleOpenPanel}
          disabled={loadingMemory}
        >
          {loadingMemory ? tr(L, "loading") : tr(L, "accountMemory")}
        </button>
      </span>
      {CHATROOMS_UI_ENABLED ? (
        <span className="hint-wrap" data-hint="Abre o modulo isolado de salas do Van Helsin.">
          <button
            className="btn gothic-btn"
            onClick={() => navigateTo("/chatrooms")}
          >
            Van Helsin Chat
          </button>
        </span>
      ) : null}
      {dragaoFeatureEnabled ? (
        <span className="hint-wrap" data-hint="Modo Dragao isolado por conversa (18+ e termo).">
          <button
            className={`btn gothic-btn ${dragaoActive ? "btn-dragao-active" : ""}`}
            onClick={dragaoActive ? handleDisableDragao : handleOpenDragaoModal}
            disabled={dragaoBusy}
          >
            {dragaoBusy
              ? "..."
              : dragaoActive
              ? "Sair Dragao"
              : "Modo Dragao"}
          </button>
        </span>
      ) : null}
      {hasAudiobookLoaded ? (
        <span className="hint-wrap" data-hint="Pausa ou retoma o audiobook atual sem perder o ponto.">
          <button
            className="btn gothic-btn vh-player-btn"
            onClick={handleToggleAudiobook}
            disabled={!hasAudiobookLoaded}
          >
            {audiobookToggleLabel}
          </button>
        </span>
      ) : null}
      <span className="hint-wrap" data-hint="Pausa ou retoma a trilha sonora do chat.">
        <button
          className="btn gothic-btn vh-player-btn"
          onClick={handleToggleMusic}
          disabled={playerMode !== "bgm"}
        >
          {musicToggleLabel}
        </button>
      </span>
      <span className="hint-wrap" data-hint="Volta para a faixa anterior.">
        <button
          className="btn gothic-btn"
          onClick={handlePrevMusic}
          title={tr(L, "prevMusic")}
          disabled={playerMode !== "bgm" || !hasMusicHistory}
        >
          {tr(L, "prevMusic")}
        </button>
      </span>
      <span className="hint-wrap" data-hint="Troca imediatamente para outra faixa aleatória.">
        <button
          className="btn gothic-btn"
          onClick={handleNextMusic}
          title={tr(L, "nextMusic")}
          disabled={playerMode !== "bgm"}
        >
          {tr(L, "nextMusic")}
        </button>
      </span>
      {!hideFutureTopControls ? (
        <span className="hint-wrap" data-hint="Função futura: envio de documentos para análise.">
          <label
            className={`btn upload-label gothic-btn ${lockFutureControls ? "btn-beta-disabled" : ""}`}
            onClick={(e) => {
              if (lockFutureControls) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            📄 Upload
            <input
              type="file"
              accept=".pdf,.txt"
              onChange={handleUpload}
              disabled={lockFutureControls}
            />
          </label>
        </span>
      ) : null}
    </div>
  );

  const renderDesktopHeaderActions = () => (
    <div className="top-actions vh-menu-contents">
      <div className="top-status">
        <span
          className={`top-status-text top-status-badge ${fullAiUnlocked ? "is-full-access" : ""}`}
          title={topAccessStatusText}
          aria-label={topAccessStatusText}
        >
          <span className="top-status-full">{topAccessStatusText}</span>
          <span className="top-status-compact" aria-hidden="true">{topAccessStatusSymbol}</span>
        </span>
        <span
          className="music-status-badge"
          title={musicStatusText}
          aria-label={musicStatusText}
        >
          <span className="top-status-full">{musicStatusText}</span>
          <span className="top-status-compact" aria-hidden="true">{topMusicStatusSymbol}</span>
        </span>
        {sessionDayLabel ? (
          <span
            className="day-status-badge"
            title={sessionDayLabel}
            aria-label={sessionDayLabel}
          >
            <span className="top-status-full">{sessionDayLabel}</span>
            <span className="top-status-compact" aria-hidden="true">{topDayStatusSymbol}</span>
          </span>
        ) : null}
        {dragaoFeatureEnabled ? (
          <span
            className={`dragao-status-badge ${dragaoActive ? "is-active" : "is-inactive"}`}
            title={dragaoActive ? "DRAGAO ATIVO" : "Dragao off"}
            aria-label={dragaoActive ? "DRAGAO ATIVO" : "Dragao off"}
          >
            <span className="top-status-full">{dragaoActive ? "DRAGAO ATIVO" : "Dragao off"}</span>
            <span className="top-status-compact" aria-hidden="true">{topDragaoStatusSymbol}</span>
          </span>
        ) : null}
      </div>
      {!hideFutureTopControls ? (
        <span className="hint-wrap" data-hint="Função futura: alternar entre as personas de IA.">
          <button
            className={`btn gothic-btn ${lockFutureControls ? "btn-beta-disabled" : ""}`}
            onClick={toggleMode}
            disabled={lockFutureControls}
          >
            {tr(L, "switchAI")}
          </button>
        </span>
      ) : null}
      <span className="hint-wrap" data-hint={tr(L, "ctaLoginTooltip")}>
        <button
          type="button"
          className="btn gothic-btn"
          onClick={openAboutLogin}
        >
          {tr(L, "ctaEnterArchive")}
        </button>
      </span>
      <span className="hint-wrap" data-hint={tr(L, "ctaObtainFullSeal")}>
        <button
          className="btn gothic-btn"
          onClick={openAboutFullCheckout}
          title={tr(L, "ctaObtainFullSeal")}
        >
          {billing && (billing.plan === "paid" || isAboutFullBillingStatus(billing)) ? tr(L, "planActive") : tr(L, "ctaObtainFullSeal")}
        </button>
      </span>
      <span className="hint-wrap" data-hint={tr(L, "rachelTooltip")}>
        <button
          type="button"
          className={`btn gothic-btn ${!vhRachelEnabled ? "btn-rachel-off" : ""}`}
          onClick={() => setVhRachelEnabled((v) => !v)}
          title={tr(L, "rachelTooltip")}
        >
          {vhRachelEnabled ? tr(L, "rachelOn") : tr(L, "rachelOff")}
        </button>
      </span>
      <span className="hint-wrap" data-hint="Abre o painel de conta e gerenciamento de memória.">
        <button
          className="btn gothic-btn"
          onClick={handleOpenPanel}
          disabled={loadingMemory}
        >
          {loadingMemory ? tr(L, "loading") : tr(L, "accountMemory")}
        </button>
      </span>
      {dragaoFeatureEnabled ? (
        <span className="hint-wrap" data-hint="Modo Dragao isolado por conversa (18+ e termo).">
          <button
            className={`btn gothic-btn ${dragaoActive ? "btn-dragao-active" : ""}`}
            onClick={dragaoActive ? handleDisableDragao : handleOpenDragaoModal}
            disabled={dragaoBusy}
          >
            {dragaoBusy
              ? "..."
              : dragaoActive
              ? "Sair Dragao"
              : "Modo Dragao"}
          </button>
        </span>
      ) : null}
      {hasAudiobookLoaded ? (
        <span className="hint-wrap" data-hint="Pausa ou retoma o audiobook atual sem perder o ponto.">
          <button
            className="btn gothic-btn vh-player-btn"
            onClick={handleToggleAudiobook}
            disabled={!hasAudiobookLoaded}
          >
            {audiobookToggleLabel}
          </button>
        </span>
      ) : null}
      <span className="hint-wrap" data-hint="Pausa ou retoma a trilha sonora do chat.">
        <button
          className="btn gothic-btn vh-player-btn"
          onClick={handleToggleMusic}
          disabled={playerMode !== "bgm"}
        >
          {musicToggleLabel}
        </button>
      </span>
      <span className="hint-wrap" data-hint="Volta para a faixa anterior.">
        <button
          className="btn gothic-btn"
          onClick={handlePrevMusic}
          title={tr(L, "prevMusic")}
          disabled={playerMode !== "bgm" || !hasMusicHistory}
        >
          {tr(L, "prevMusic")}
        </button>
      </span>
      <span className="hint-wrap" data-hint="Troca imediatamente para outra faixa aleatória.">
        <button
          className="btn gothic-btn"
          onClick={handleNextMusic}
          title={tr(L, "nextMusic")}
          disabled={playerMode !== "bgm"}
        >
          {tr(L, "nextMusic")}
        </button>
      </span>
      {!hideFutureTopControls ? (
        <span className="hint-wrap" data-hint="Função futura: envio de documentos para análise.">
          <label
            className={`btn upload-label gothic-btn ${lockFutureControls ? "btn-beta-disabled" : ""}`}
            onClick={(e) => {
              if (lockFutureControls) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            📄 Upload
            <input
              type="file"
              accept=".pdf,.txt"
              onChange={handleUpload}
              disabled={lockFutureControls}
            />
          </label>
        </span>
      ) : null}
    </div>
  );

  const renderSidebarControls = () => (
    <div className="vh-sidebar-controls" aria-label="Controles">
      <div className="vh-sidebar-controls-title">Controles</div>
      <button
        type="button"
        className="vh-sidebar-control-button"
        onClick={handleOpenPanel}
        disabled={loadingMemory}
      >
        {loadingMemory ? tr(L, "loading") : tr(L, "accountMemory")}
      </button>
      {CHATROOMS_UI_ENABLED ? (
        <button
          type="button"
          className="vh-sidebar-control-button"
          onClick={() => navigateTo("/chatrooms")}
        >
          Van Helsin Chat
        </button>
      ) : null}
    </div>
  );

  return (
    <div
      className={`app gothic-text ${dragaoActive ? "vh-mode-dragao" : ""} ${showMemory ? "vh-memory-open" : ""} ${showAnyLegalPage ? "vh-legal-route" : ""}`}
      style={{ "--vh-font-scale": String(fontScale) }}
    >
      <audio ref={audioRef} hidden preload="auto" />
      <audio ref={audiobookRef} hidden preload="auto" src={audiobookSrc || undefined} />
      <div className="background">
        <div className="background-media-box">
          {dragaoActive ? (
            <>
              {[1, 2, 3, 4, 5].map((idx) => {
                const key = DRAGAO_ARMOR_KEYS[idx - 1] || "p1_visao";
                const unlocked = Boolean(dragaoState?.armor_state?.[key]);
                const flashUnlock = Boolean(dragaoArmorFlash?.[key]);
                return (
                  <img
                    key={`armor-${idx}`}
                    src={`/dragao_armor_p${idx}.png`}
                    alt=""
                    className={`background-media background-armor armor-p${idx} ${unlocked ? "unlocked" : ""} ${flashUnlock ? "armor-new-unlock" : ""}`}
                    aria-hidden
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                );
              })}
              <div className="dragao-overlay" aria-hidden />
            </>
          ) : null}
          <img
            src={dragaoActive ? "/dragao.png" : "/fotos/novotema.png"}
            alt=""
            className={`background-media background-photo ${showLadyLucroVideo ? "background-photo--lady-active" : ""}`}
            aria-hidden
          />
          {!dragaoActive && hasClimateVisualStarted && visualState ? (
            <VHAtmosphereLayer
              visualState={visualState}
              backgroundMediaMode={showLadyLucroVideo ? "lady_lucro_video" : "scene_video"}
            />
          ) : null}
          <video
            ref={ladyLucroVideoRef}
            className={`background-media background-video ${showLadyLucroVideo ? "background-video--active" : ""}`}
            src="/fotos/video_ladylucrovanhelsin.mp4"
            preload="auto"
            muted
            playsInline
            onEnded={() => {
              ladyLucroPlayCountRef.current += 1;
              if (ladyLucroPlayCountRef.current >= 4) {
                setShowLadyLucroVideo(false);
                const restoredVisualState = ladyLucroVisualStateRef.current;
                if (restoredVisualState && typeof restoredVisualState.mode === "string") {
                  setVisualState(withAppliedAt(restoredVisualState));
                }
                ladyLucroVisualStateRef.current = null;
              } else if (ladyLucroVideoRef.current) {
                ladyLucroVideoRef.current.currentTime = 0;
                ladyLucroVideoRef.current.play();
              }
            }}
          />
        </div>
      </div>
      {dragaoCelebration ? (
        <div className="dragao-celebration-overlay" aria-live="assertive" role="status">
          <div className="dragao-celebration-hud">
            <div className="dragao-celebration-title">Avanco Conquistado</div>
            <div className="dragao-celebration-subtitle">{dragaoCelebrationPieceLabel}</div>
          </div>
          <div className="dragao-celebration-media-box">
            <img
              src="/dragao.png"
              alt="Van Helsin enfrentando o dragao"
              className="dragao-celebration-media dragao-celebration-base"
            />
            {[1, 2, 3, 4, 5].map((idx) => {
              const key = DRAGAO_ARMOR_KEYS[idx - 1] || "p1_visao";
              const unlocked =
                Boolean(dragaoCelebration?.armorState?.[key]) ||
                String(dragaoCelebration?.key || "") === key;
              return (
                <img
                  key={`celebration-armor-${idx}`}
                  src={`/dragao_armor_p${idx}.png`}
                  alt=""
                  aria-hidden
                  className={[
                    "dragao-celebration-media",
                    "dragao-celebration-armor",
                    `armor-p${idx}`,
                    unlocked ? "unlocked" : "",
                    String(dragaoCelebration?.key || "") === key ? "celebration-new-unlock" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              );
            })}
          </div>
        </div>
      ) : null}
      {showAnyLegalPage ? (
        <>
          {showLegalPage && <LegalStaticPage onLangChange={handleLangChange} onNavigate={navigateTo} returnPath={legalReturnPath} returnTarget={legalReturnTarget} />}
          {showPrivacyPage && <PrivacyStaticPage onLangChange={handleLangChange} onNavigate={navigateTo} returnPath={legalReturnPath} returnTarget={legalReturnTarget} />}
          {showTermsPage && <TermsStaticPage onLangChange={handleLangChange} onNavigate={navigateTo} returnPath={legalReturnPath} returnTarget={legalReturnTarget} />}
          {showPricingPage && <PricingStaticPage onLangChange={handleLangChange} onNavigate={navigateTo} returnPath={legalReturnPath} returnTarget={legalReturnTarget} />}
          {showRefundPolicyPage && <RefundPolicyStaticPage onLangChange={handleLangChange} onNavigate={navigateTo} returnPath={legalReturnPath} returnTarget={legalReturnTarget} />}
          {showDataRequestPage && <DataRequestStaticPage onLangChange={handleLangChange} onNavigate={navigateTo} returnPath={legalReturnPath} returnTarget={legalReturnTarget} />}
          {showAboutPage && <AboutStaticPage onLangChange={handleLangChange} onNavigate={navigateTo} returnPath={legalReturnPath} returnTarget={legalReturnTarget} />}
          {showArquivoPage && <ArquivoConfidencialPage onLangChange={handleLangChange} onNavigate={navigateTo} returnPath={legalReturnPath} returnTarget={legalReturnTarget} />}
        </>
      ) : (
        <>
      {/* Barra lateral de idiomas — altera toda a página */}
      {!introGateBlocking ? (
      <div className="vh-lang-sidebar" role="navigation" aria-label={tr(L, "shieldSubtitle")}>
        {LANG_OPTIONS.map((opt) => (
          <button
            key={opt.code}
            type="button"
            className={`vh-lang-sidebar-btn ${(uiLang || L) === opt.code ? "active" : ""}`}
            onClick={() => handleLangChange(opt.code)}
            title={opt.label}
          >
            <span className="vh-lang-sidebar-flag">{opt.flag}</span>
            <span className="vh-lang-sidebar-label">{opt.label}</span>
          </button>
        ))}
        {LEGAL_PAGES ? (
          <button
            type="button"
            className="vh-lang-sidebar-btn vh-lang-orb-mobile-btn"
            onClick={() => navigateTo("/arquivo")}
            title={tr(L, "confidentialFile")}
            aria-label={tr(L, "confidentialFile")}
          >
            <span className="vh-lang-sidebar-flag">
              <img src="/fotos/orb.png" alt="" className="vh-lang-orb-mobile-img" />
            </span>
            <span className="vh-lang-sidebar-label">ARQ</span>
          </button>
        ) : null}
      </div>
      ) : null}
      {!introGateBlocking ? <MobileMenuFab>{renderTopMenuContents()}</MobileMenuFab> : null}
      <div className="top-bar vh-topbar-desktop vh-app-header">
        <h1 className="text-glow top-title">
          {sidebarEnabled ? (
            <button
              type="button"
              className={`vh-toggle vh-toggle--title ${collapsed ? "is-collapsed" : "is-open"}`}
              onClick={() => setCollapsed(!collapsed)}
              aria-label={collapsed ? "Abrir histórico" : "Fechar histórico"}
              title={collapsed ? "Abrir histórico" : "Fechar histórico"}
            >
              <span className="vh-toggle-icon" aria-hidden="true">
                <svg
                  className="vh-cross"
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 0h4v10h6v4h-6v10h-4v-10h-6v-4h6v-10z"
                    fill="currentColor"
                  />
                </svg>
              </span>
            </button>
          ) : null}
          <span className="vh-title-brand">⚔️ Van Helsin IA —{" "}</span>
          <span className="top-title-persona-wrap">
            {currentMode === "heroi"
              ? tr(L, "heroOfShadows")
              : tr(L, "financialExorcist")}
            {LEGAL_PAGES ? (
              <button
                type="button"
                className="arquivo-orb-btn"
                onClick={() => navigateTo("/arquivo")}
                title={tr(L, "confidentialFile")}
                aria-label={tr(L, "confidentialFile")}
              >
                <img src="/fotos/orb.png" alt="" className="arquivo-orb-img" />
              </button>
            ) : null}
          </span>
        </h1>
        {renderDesktopHeaderActions()}
      </div>
      {introGateBlocking
        ? createPortal(
            <IntroGateNotice
              currentLang={currentLang}
              aiConsentChecked={aiConsentChecked}
              setAiConsentChecked={setAiConsentChecked}
              termsEnforced={termsEnforced}
              termsAccepted={termsAccepted}
              termsChecked={termsChecked}
              setTermsChecked={setTermsChecked}
              termsBusy={termsBusy}
              termsError={termsError}
              navigateTo={navigateTo}
              handleLangChange={handleLangChange}
              handleAcceptTermsGate={handleAcceptTermsGate}
              handleDismissIntroWarning={handleDismissIntroWarning}
              showAccessRecoverCta={showLoginRoute}
              onAccessRecoverClick={openAboutRecover}
            />,
            document.body
          )
        : null}
      {showLoginRoute && !introGateBlocking ? (
        <div style={{ padding: "0 16px" }}>
          <div style={{ maxWidth: "760px", margin: "0 auto 16px" }}>
            <LoginAccessRecoverBridge
              lang={currentLang}
              onRecover={openAboutRecover}
            />
          </div>
        </div>
      ) : null}
      <div className="vh-layout">
        {sidebarEnabled ? (
          <>
            {!collapsed && (
              <div
                className="vh-backdrop"
                onClick={() => setCollapsed(true)}
                aria-hidden="true"
              />
            )}
            <div className={`vh-sidebar ${collapsed ? "collapsed" : ""}`}>
              <aside className="conversations-sidebar">
                <div className="vh-sidebar-header">
                  <button
                    type="button"
                    className="vh-sidebar-close"
                    onClick={() => setCollapsed(true)}
                    aria-label="Fechar histórico"
                    title="Fechar histórico"
                  >
                    <span aria-hidden="true" className="vh-close-icon">✕</span>
                  </button>
                  <div className="conversations-sidebar-header">
                <div className="conversations-sidebar-title">{convT(L, "historyTitle")}</div>
                <button
                  type="button"
                  className="btn gothic-btn conversations-new-btn"
                  title={convT(L, "newConversationHint")}
                  aria-label={convT(L, "newConversationHint")}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleNewConversation();
                  }}
                >
                  {convT(L, "newConversation")}
                </button>
                  </div>
                </div>
              {conversationsLoading ? (
                <div className="conversations-sidebar-loading">{convT(L, "loading")}</div>
              ) : null}
              {conversationsError ? (
                <div className="conversations-sidebar-error">{conversationsError}</div>
              ) : null}
              {renderSidebarControls()}
              <div className="vh-accessibility-panel">
                <div className="vh-accessibility-title">Leitura</div>
                <label className="vh-accessibility-field">
                  <span>Tamanho do texto: {Math.round(fontScale * 100)}%</span>
                  <input
                    type="range"
                    min="0.9"
                    max="1.6"
                    step="0.05"
                    value={fontScale}
                    onChange={(e) => setFontScale(normalizeFontScale(e.target.value))}
                  />
                </label>
                <div className="vh-accessibility-actions">
                  <button
                    type="button"
                    className="btn gothic-btn"
                    onClick={() => setFontScale((value) => normalizeFontScale(Number(value) - 0.1))}
                  >
                    A-
                  </button>
                  <button type="button" className="btn gothic-btn" onClick={() => setFontScale(1)}>
                    Padrao
                  </button>
                  <button
                    type="button"
                    className="btn gothic-btn"
                    onClick={() => setFontScale((value) => normalizeFontScale(Number(value) + 0.1))}
                  >
                    A+
                  </button>
                </div>
              </div>
              <div className="conversations-sidebar-list">
                {visibleConversations.length === 0 && !conversationsLoading ? (
                  <div className="conversations-empty">{convT(L, "noConversations")}</div>
                ) : (
                  visibleConversations.map((item) => {
                    const cid = String(item?.id || "");
                    const isActive = cid && cid === activeConversationId;
                    const updatedAtText = item?.updated_at
                      ? new Date(Number(item.updated_at) * 1000).toLocaleString()
                      : "";
                    return (
                      <div
                        key={cid}
                        className={`conversation-item ${isActive ? "active" : ""}`}
                      >
                        <button
                          type="button"
                          className="conversation-open-btn"
                          onClick={() => openConversationById(cid)}
                          disabled={loadingConversationMessages}
                        >
                          <div className="conversation-title">{String(item?.title || convT(L, "newConversation"))}</div>
                          <div className="conversation-preview">{String(item?.last_message_preview || "")}</div>
                          <div className="conversation-time">{updatedAtText}</div>
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </aside>
          </div>
          </>
        ) : null}
          <div className={`vh-main ${sidebarEnabled ? "vh-has-sidebar" : ""}`}>
          <div className="vh-main-stack">
      <div className="chat-layout">
          <section className="vh-trust-ribbon" aria-label={tr(L, "authorityStripTitle")}>
            <div className="vh-trust-ribbon-copy">
              <span>{tr(L, "aiBanner")}</span>
            </div>
            <button
              type="button"
              className="vh-trust-ribbon-link"
              onClick={() => window.open(getAuthorityPortalUrl(L), "_blank", "noopener,noreferrer")}
            >
              {tr(L, "authorityStripButton")}
            </button>
          </section>
          <div className="vh-mobile-trust-stack">
            <div className="vh-ai-banner" role="status" aria-live="polite">
              {tr(L, "aiBanner")}
            </div>
            <section className="vh-authority-strip" aria-label={tr(L, "authorityStripTitle")}>
              <div className="vh-authority-strip-mark" aria-hidden="true">VH</div>
              <div className="vh-authority-strip-copy">
                <strong>{tr(L, "authorityStripTitle")}</strong>
              </div>
              <button
                type="button"
                className="vh-authority-strip-link"
                onClick={() => window.open(getAuthorityPortalUrl(L), "_blank", "noopener,noreferrer")}
              >
                {tr(L, "authorityStripButton")}
              </button>
            </section>
          </div>
        <div className="chat">
          {dragaoActive ? (
            <div className="dragao-mini-panel">
              <div className="dragao-mini-row">
                <strong>Dragao:</strong> {String(dragaoState?.name || "nao definido")}
              </div>
              <div className="dragao-mini-row">
                <strong>Pilar atual:</strong> {dragaoPilarLabel}
              </div>
              <div className="dragao-mini-row">
                <strong>Score total:</strong> {Number(dragaoState?.score_total || 0)} | <strong>Ultimo:</strong>{" "}
                {Number(dragaoState?.last_score || 0)} | <strong>Stage:</strong> {dragaoStage}
              </div>
              {DRAGAO_CHAT_LED_MODE ? (
                <div className="dragao-mini-row dragao-mini-hint">
                  O LLM conduz o Modo Dragao no chat. Envie uma mensagem para iniciar.
                </div>
              ) : null}
              {DRAGAO_CHAT_LED_MODE && dragaoError ? <div className="dragao-error">{dragaoError}</div> : null}
            </div>
          ) : null}
          {loadingConversationMessages ? (
            <div className="conversations-sidebar-loading">{convT(L, "loading")}</div>
          ) : null}
          {messages.map((m, idx) => {
            let cls = "msg fade-in",
              pref = "";
            const parsedMsg = m.sender === "ia" ? splitUltraDayHeader(m.text) : { dayLabel: null, bodyText: m.text };
            const msgText = m.sender === "ia" ? parsedMsg.bodyText : m.text;
            const shortConclusionKey = getShortConclusionStateKey(m, `idx:${idx}`);
            if (m.sender === "user") {
              cls += " user-msg";
              pref = tr(L, "you");
            } else if (m.sender === "upload") {
              cls += " upload-msg";
              pref = "📄 Upload";
            } else if (m.sender === "ia") {
              if (m.mode === "heroi") {
                cls += " ia-heroi";
                pref = tr(L, "heroOfShadows");
              } else {
                cls += " ia-financeiro";
                pref = tr(L, "financialExorcist");
              }
            }
            return (
              <div key={idx} className={cls}>
                <strong>{pref}:</strong>{" "}
                {m.sender === "ia" ? (
                  <>
                    {String(m.waitBridgePrefix || "").trim() ? (
                      <div
                        className="vh-wait-bridge-frame vh-wait-bridge-embedded"
                        role="note"
                        aria-label={tr(L, "waitBridgeAriaLabel")}
                      >
                        {String(m.waitBridgePrefix).trim()}
                      </div>
                    ) : null}
                    <EpicResponseRenderer text={msgText} audioRef={audioRef} />
                    {shouldShowParableContextControls(m) ? (
                      <div className="msg-context-badges" data-testid="parable-badge">
                        <span className="msg-badge">{tr(L, "parableBadge")}</span>
                        {shouldShowShortConclusionAction(m, shortConclusionByMessageId, shortConclusionKey) ? (
                          <button
                            type="button"
                            className="msg-badge-action"
                            data-short-conclusion-key={shortConclusionKey}
                            data-assistant-message-id={String(m.assistantMessageId || "").trim()}
                            onClick={(e) =>
                              handleRequestShortConclusion(
                                m,
                                shortConclusionKey,
                                msgText,
                                e.currentTarget
                              )
                            }
                            disabled={shortConclusionLoadingId === String(m.assistantMessageId || "").trim()}
                          >
                            {shortConclusionLoadingId === String(m.assistantMessageId || "").trim()
                              ? tr(L, "loading")
                              : tr(L, "onlyConclusion")}
                          </button>
                        ) : null}
                        {shouldShowDirectPreferenceAction(messages, idx) ? (
                          <button
                            type="button"
                            className="msg-badge-action"
                            onClick={() =>
                              shouldShowResetDirectPreferenceAction(messages, idx, responseStyleBias)
                                ? resetDirectPreferenceForConversation(activeConversationId, "normal")
                                : activateDirectPreferenceForConversation(activeConversationId, "normal")
                            }
                          >
                            {shouldShowResetDirectPreferenceAction(messages, idx, responseStyleBias)
                              ? tr(L, "restoreAutoStyle")
                              : tr(L, "moreDirectNext")}
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                    {String(shortConclusionKey || "").trim()
                    && String(shortConclusionByMessageId?.[shortConclusionKey] || "").trim() ? (
                      <div className="msg-short-conclusion" data-testid="short-conclusion">
                        <div className="msg-short-conclusion-head">
                          <strong>{tr(L, "onlyConclusion")}:</strong>
                          <button
                            type="button"
                            className="msg-short-conclusion-toggle"
                            onClick={() =>
                              setExpandedShortConclusionByMessageId((prev) => ({
                                ...(prev || {}),
                                [shortConclusionKey]:
                                  !shouldShowShortConclusionContent(
                                    prev || {},
                                    shortConclusionKey
                                  ),
                              }))
                            }
                          >
                            {shouldShowShortConclusionContent(
                              expandedShortConclusionByMessageId,
                              shortConclusionKey
                            )
                              ? tr(L, "hideConclusion")
                              : tr(L, "showConclusion")}
                          </button>
                        </div>
                        {shouldShowShortConclusionContent(
                          expandedShortConclusionByMessageId,
                          shortConclusionKey
                        ) ? (
                          <>
                            {" "}
                            {String(shortConclusionByMessageId[shortConclusionKey] || "").trim()}
                          </>
                        ) : null}
                      </div>
                    ) : null}
                  </>
                ) : (
                  msgText
                )}
              </div>
            );
          })}
          {shouldRenderWaitBridge(bridgeState, turnStateRef.current.requestId) ? (
            <div
              ref={bridgeFrameRef}
              className="vh-wait-bridge-frame"
              role="status"
              aria-live="polite"
              aria-label={tr(L, "waitBridgeAriaLabel")}
            >
              {String(bridgeState?.bridge_text || "")}
            </div>
          ) : null}
          <div ref={chatEndRef} />
        </div>
      </div>

      {dragaoActive && !DRAGAO_CHAT_LED_MODE ? (
        <div className="dragao-workbench">
          <div className="dragao-workbench-head">
            <div className="dragao-workbench-title">
              Pilar em foco: {dragaoPilarLabel}
            </div>
            <div className="dragao-workbench-meta">
              Armadura: {Number(dragaoState?.armor_unlocked || 0)}/5 | Balance: {Number(dragaoState?.score_balance || 0)}
            </div>
          </div>

          {dragaoQuestions?.questions?.length ? (
            <ol className="dragao-questions">
              {dragaoQuestions.questions.slice(0, 6).map((q, idx) => (
                <li key={`dq-${idx}`}>{String(q || "")}</li>
              ))}
            </ol>
          ) : null}

          {dragaoError ? <div className="dragao-error">{dragaoError}</div> : null}

          {DRAGAO_CHAT_LED_MODE ? (
            <div className="dragao-chat-led-hint">
              Perguntas do Modo Dragao sao conduzidas no chat pelo LLM. Responda no campo principal abaixo.
            </div>
          ) : null}

          {!DRAGAO_CHAT_LED_MODE && dragaoStage === "identify" ? (
            <div className="dragao-form-grid">
              <input
                className="dragao-input"
                placeholder="Nome do dragao"
                value={dragaoIdentifyName}
                onChange={(e) => setDragaoIdentifyName(e.target.value)}
              />
              <input
                className="dragao-input"
                placeholder="Por que esse dragao existe? (1 frase)"
                value={dragaoIdentifyWhy}
                onChange={(e) => setDragaoIdentifyWhy(e.target.value)}
              />
              <textarea
                className="dragao-input"
                placeholder="Custos reais (1 por linha, max 3)"
                value={dragaoIdentifyCosts}
                onChange={(e) => setDragaoIdentifyCosts(e.target.value)}
                rows={3}
              />
              <button className="btn gothic-btn" onClick={handleDragaoIdentifySubmit} disabled={dragaoBusy}>
                {dragaoBusy ? "..." : "Salvar identificacao"}
              </button>
            </div>
          ) : null}

          {!DRAGAO_CHAT_LED_MODE && dragaoStage === "baseline" ? (
            <div className="dragao-form-grid">
              <input className="dragao-input" placeholder="Cedeu em 7 dias (numero)" value={dragaoBaselineForm.cedeu_7d} onChange={(e) => setDragaoBaselineForm((p) => ({ ...p, cedeu_7d: e.target.value }))} />
              <input className="dragao-input" placeholder="Gatilho principal" value={dragaoBaselineForm.gatilho_principal} onChange={(e) => setDragaoBaselineForm((p) => ({ ...p, gatilho_principal: e.target.value }))} />
              <input className="dragao-input" placeholder="Controle 0-10" value={dragaoBaselineForm.controle_0_10} onChange={(e) => setDragaoBaselineForm((p) => ({ ...p, controle_0_10: e.target.value }))} />
              <input className="dragao-input" placeholder="Dano concreto" value={dragaoBaselineForm.dano_concreto} onChange={(e) => setDragaoBaselineForm((p) => ({ ...p, dano_concreto: e.target.value }))} />
              <input className="dragao-input" placeholder="Resistencia tentada" value={dragaoBaselineForm.resistencia_tentada} onChange={(e) => setDragaoBaselineForm((p) => ({ ...p, resistencia_tentada: e.target.value }))} />
              <input className="dragao-input" placeholder="Acao de hoje" value={dragaoBaselineForm.acao_hoje} onChange={(e) => setDragaoBaselineForm((p) => ({ ...p, acao_hoje: e.target.value }))} />
              <input className="dragao-input" placeholder="P1 0-10" value={dragaoBaselineForm.p1} onChange={(e) => setDragaoBaselineForm((p) => ({ ...p, p1: e.target.value }))} />
              <input className="dragao-input" placeholder="P2 0-10" value={dragaoBaselineForm.p2} onChange={(e) => setDragaoBaselineForm((p) => ({ ...p, p2: e.target.value }))} />
              <input className="dragao-input" placeholder="P3 0-10" value={dragaoBaselineForm.p3} onChange={(e) => setDragaoBaselineForm((p) => ({ ...p, p3: e.target.value }))} />
              <input className="dragao-input" placeholder="P4 0-10" value={dragaoBaselineForm.p4} onChange={(e) => setDragaoBaselineForm((p) => ({ ...p, p4: e.target.value }))} />
              <input className="dragao-input" placeholder="P5 0-10" value={dragaoBaselineForm.p5} onChange={(e) => setDragaoBaselineForm((p) => ({ ...p, p5: e.target.value }))} />
              <button className="btn gothic-btn" onClick={handleDragaoBaselineSubmit} disabled={dragaoBusy}>
                {dragaoBusy ? "..." : "Salvar baseline"}
              </button>
            </div>
          ) : null}

          {!DRAGAO_CHAT_LED_MODE && dragaoStage === "loop" ? (
            <div className="dragao-form-grid">
              <select className="dragao-input" value={dragaoCheckinForm.result} onChange={(e) => setDragaoCheckinForm((p) => ({ ...p, result: e.target.value }))}>
                <option value="venci">venci</option>
                <option value="parcial">parcial</option>
                <option value="cedi">cedi</option>
              </select>
              <input className="dragao-input" placeholder="Quantas vezes?" value={dragaoCheckinForm.times} onChange={(e) => setDragaoCheckinForm((p) => ({ ...p, times: e.target.value }))} />
              <input className="dragao-input" placeholder="Gatilho dominante" value={dragaoCheckinForm.trigger} onChange={(e) => setDragaoCheckinForm((p) => ({ ...p, trigger: e.target.value }))} />
              <input className="dragao-input" placeholder="Controle 0-10" value={dragaoCheckinForm.control_0_10} onChange={(e) => setDragaoCheckinForm((p) => ({ ...p, control_0_10: e.target.value }))} />
              <input className="dragao-input" placeholder="Dano (tempo/dinheiro/...)" value={dragaoCheckinForm.damage} onChange={(e) => setDragaoCheckinForm((p) => ({ ...p, damage: e.target.value }))} />
              <input className="dragao-input" placeholder="Acao executada" value={dragaoCheckinForm.action_executed} onChange={(e) => setDragaoCheckinForm((p) => ({ ...p, action_executed: e.target.value }))} />
              <input className="dragao-input" placeholder="Metrica objetiva (numero)" value={dragaoCheckinForm.objective_metric} onChange={(e) => setDragaoCheckinForm((p) => ({ ...p, objective_metric: e.target.value }))} />
              <input className="dragao-input" placeholder="Barreira aplicada" value={dragaoCheckinForm.barrier_applied} onChange={(e) => setDragaoCheckinForm((p) => ({ ...p, barrier_applied: e.target.value }))} />
              <input className="dragao-input" placeholder="Repeticoes da acao" value={dragaoCheckinForm.repeat_count} onChange={(e) => setDragaoCheckinForm((p) => ({ ...p, repeat_count: e.target.value }))} />
              <label className="dragao-checkbox">
                <input type="checkbox" checked={Boolean(dragaoCheckinForm.action_done)} onChange={(e) => setDragaoCheckinForm((p) => ({ ...p, action_done: e.target.checked }))} />
                acao concluida no mundo real
              </label>
              <button className="btn gothic-btn" onClick={handleDragaoCheckinSubmit} disabled={dragaoBusy}>
                {dragaoBusy ? "..." : "Enviar check-in"}
              </button>
            </div>
          ) : null}

          {!DRAGAO_CHAT_LED_MODE && dragaoStage === "review" ? (
            <div className="dragao-form-grid">
              <button className="btn gothic-btn" onClick={handleDragaoReviewSubmit} disabled={dragaoBusy}>
                {dragaoBusy ? "..." : "Concluir review e iniciar novo ciclo"}
              </button>
            </div>
          ) : null}

          {dragaoRecentLog.length > 0 ? (
            <div className="dragao-recent-log">
              <strong>Ultimos check-ins:</strong>
              <div>
                {dragaoRecentLog.map((it, idx) => (
                  <span key={`drl-${idx}`} className="dragao-recent-item">
                    {String(it?.date || "-")} | {String(it?.result || "-")} | score {Number(it?.score || 0)}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {isSending && thinkingLine ? (
        <div className="thinking-line">{thinkingLine}</div>
      ) : null}

      {dragaoModalOpen ? (
        <div className="dragao-modal-overlay">
          <div className="dragao-modal">
            <div className="dragao-modal-head">
              <h2>Modo Dragao</h2>
              <button className="memory-close-btn" onClick={() => setDragaoModalOpen(false)}>✕</button>
            </div>
            <div className="dragao-modal-body">
              <p>
                Modo separado do chat normal. Tom direto, perguntas objetivas, score backend-driven e persistencia por conversa.
              </p>
              <label className="dragao-checkbox">
                <input
                  type="checkbox"
                  checked={dragaoAgeChecked}
                  onChange={(e) => setDragaoAgeChecked(e.target.checked)}
                />
                Confirmo que tenho 18 anos ou mais.
              </label>
              <label className="dragao-checkbox">
                <input
                  type="checkbox"
                  checked={dragaoTermsChecked}
                  onChange={(e) => setDragaoTermsChecked(e.target.checked)}
                />
                Li e aceito os termos do Modo Dragao.
              </label>
              {dragaoError ? <div className="dragao-error">{dragaoError}</div> : null}
              <div className="dragao-modal-actions">
                <button
                  className="btn gothic-btn"
                  onClick={handleAcceptDragao}
                  disabled={dragaoBusy || !dragaoAgeChecked || !dragaoTermsChecked}
                >
                  {dragaoBusy ? "Ativando..." : "Ativar"}
                </button>
                <button className="btn gothic-btn" onClick={() => setDragaoModalOpen(false)} disabled={dragaoBusy}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {showEuConsentModal ? (
        <div className="memory-overlay" style={{ zIndex: 2000 }}>
          <div className="memory-panel" style={{ maxWidth: "480px" }}>
            <div className="memory-panel-header">
              <h2>{tr(L, "euConsentTitle")}</h2>
              <button className="memory-close-btn" onClick={() => setShowEuConsentModal(false)}>✕</button>
            </div>
            <div className="memory-content" style={{ padding: "16px" }}>
              <p style={{ marginBottom: "14px", opacity: 0.95 }}>
                {tr(L, "checkboxShort")}
              </p>
              <label style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: "12px", cursor: "pointer" }}>
                <input type="checkbox" checked={euConsentTermsChecked} onChange={(e) => setEuConsentTermsChecked(e.target.checked)} />
                <span>{tr(L, "euConsentTerms")}</span>
              </label>
              <label style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: "16px", cursor: "pointer" }}>
                <input type="checkbox" checked={euConsentWithdrawalChecked} onChange={(e) => setEuConsentWithdrawalChecked(e.target.checked)} />
                <span>{tr(L, "euConsentWithdrawal")}</span>
              </label>
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  className="btn gothic-btn"
                  onClick={handleEuConsentConfirm}
                  disabled={!euConsentTermsChecked || !euConsentWithdrawalChecked || billingLoading}
                >
                  {billingLoading ? tr(L, "opening") : tr(L, "euConsentConfirm")}
                </button>
                <button className="btn gothic-btn" onClick={() => setShowEuConsentModal(false)} style={{ backgroundColor: "#333" }}>
                  {tr(L, "notNow")}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {shouldShowHunterSuggestion ? (
        <HunterSuggestion
          suggestion={selectedHunterSuggestion}
          onRefresh={handleRefreshHunterSuggestion}
          onApply={handleApplyHunterSuggestion}
          onClose={handleDismissHunterSuggestion}
          kickerLabel={`✠ ${tr(L, "hunterSuggestionKicker")}`}
          refreshLabel={tr(L, "anotherSuggestion")}
          applyLabel={tr(L, "useSuggestion")}
          closeLabel={tr(L, "closeMiniGuide")}
        />
      ) : null}

      {shouldShowHunterSuggestionReopenLink ? (
        <div className="quick-start-reopen-wrap">
          <button
            type="button"
            className="quick-start-reopen"
            onClick={handleReopenHunterSuggestion}
          >
            {tr(L, "reopenHunterSuggestion")}
          </button>
        </div>
      ) : null}

      {QUICK_START_GUIDE_RENDER_ENABLED && shouldShowQuickStart ? (
        <div className="quick-start-card" role="note" aria-live="polite">
          <div className="mini-guide-header">
            <div className="quick-start-kicker">✠ {tr(L, "quickStartKicker")}</div>
            <button
              type="button"
              className="mini-guide-close"
              onClick={handleDismissQuickStart}
              aria-label={tr(L, "closeMiniGuide")}
              title={tr(L, "closeMiniGuide")}
            >
              ×
            </button>
          </div>
          <div className="quick-start-actions">
            {quickStartOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                className="quick-start-btn"
                onClick={() => handleQuickStartApply(option.prefix)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {QUICK_START_GUIDE_RENDER_ENABLED && shouldShowQuickStartReopenLink ? (
        <div className="quick-start-reopen-wrap">
          <button
            type="button"
            className="quick-start-reopen"
            onClick={handleReopenQuickStart}
          >
            {tr(L, "quickStartReopen")}
          </button>
        </div>
      ) : null}

      </div>

      <AudiobookOfferCard
        lang={L}
        audiobookOffer={audiobookOffer}
        audiobookBusy={audiobookBusy}
        interactionLocked={introGateBlocking}
        onAccept={handleAcceptAudiobook}
        onClose={closeAudiobookOffer}
      />

      <div className="vh-main-input-region">
      <div className="input-bar">
        <div className="voice-controls">
          <select
            value={voiceLang}
            onChange={(e) => setVoiceLang(e.target.value)}
            title={tr(L, "voiceTitle")}
          >
            {VOICE_LANGS.map((l) => (
              <option key={l.id} value={l.id}>
                {l.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn gothic-btn"
            onClick={isRecording ? stopRecording : startRecording}
            disabled={!sttSupported}
            title={
              sttSupported
                ? isRecording
                  ? tr(L, "stopRecording")
                  : tr(L, "startRecording")
                : tr(L, "voiceUnavailable")
            }
          >
            {isRecording ? "⏹️" : "🎤"}
          </button>
        </div>
        <textarea
          ref={inputRef}
          rows={1}
          placeholder={
            inputBlockedByLimit
              ? "⚠️ " + tr(L, "placeholderBlocked")
              : currentMode === "heroi"
              ? tr(L, "placeholderHero")
              : tr(L, "placeholderFinance")
          }
          value={input}
          onChange={handleInputChange}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          className={
            currentMode === "heroi" ? "input-heroi" : "input-financeiro"
          }
          disabled={inputBlockedByLimit || showIntro}
        />
        {isSending ? (
          <button
            onClick={handleStopGeneration}
            className="send-btn gothic-btn stop-btn"
          >
            {tr(L, "stop")}
          </button>
        ) : inputBlockedByLimit ? (
          <button
            type="button"
            onClick={() => window.open(VH_ABOUT_FULL, "_blank", "noopener,noreferrer")}
            className="send-btn gothic-btn"
          >
            {tr(L, "ctaBreakSeal")}
          </button>
        ) : (
          <button
            onClick={handleSend}
            disabled={showIntro}
            className="send-btn gothic-btn"
          >
            {tr(L, "send")}
          </button>
        )}
      </div>
      {inputBlockedByLimit ? (
        <div className="vh-paywall-login-hint">
          <button
            type="button"
            className="vh-paywall-login-link"
            onClick={openAboutLogin}
          >
            {tr(L, "ctaAlreadyHaveSeal")}
          </button>
        </div>
      ) : null}
      {interimVoiceText ? <div className="voice-interim">🎙️ {interimVoiceText}</div> : null}
      </div>

      <div className="legal-disclaimer">
        {tr(L, "fictionDisclaimer")}
        {LEGAL_PAGES ? (
          <>
            {" · "}
            <a href={`${INSTITUTIONAL_BASE_URL}/legal`} onClick={(e) => { e.preventDefault(); navigateTo("/legal"); }}>{legalFooterLabel}</a>
          </>
        ) : null}
      </div>

          </div>
        </div>

      {/* Modal Conta/Memória */}
      {showMemory && (
        <div
          className="memory-modal-overlay"
          onClick={() => {
            setShowMemory(false);
            setShowSessionsMemoryPanel(false);
            setDeleteConfirmInput("");
          }}
        >
          <div className="memory-modal" onClick={(e) => e.stopPropagation()}>
            <div className="memory-modal-header">
              <h2>{tr(L, "accountMemoryTitle")}</h2>
              <button
                className="memory-close-btn"
                onClick={() => {
                  setShowMemory(false);
                  setShowSessionsMemoryPanel(false);
                  setDeleteConfirmInput("");
                }}
              >
                ✕
              </button>
            </div>

            <div className="memory-content">
              {showSessionsMemoryPanel ? (
                <>
                  <div style={{ marginBottom: "14px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    <button
                      className="btn gothic-btn"
                      onClick={() => {
                        setShowSessionsMemoryPanel(false);
                        setDeleteConfirmInput("");
                      }}
                    >
                      {convT(L, "sessionsBack")}
                    </button>
                    <button
                      className="btn gothic-btn"
                      onClick={() => refreshConversations()}
                      disabled={conversationsLoading}
                    >
                      {conversationsLoading ? convT(L, "loading") : convT(L, "refreshList")}
                    </button>
                  </div>
                  {conversationsSummaryEnabled ? (
                    <div style={{ marginBottom: "10px", opacity: 0.85 }}>
                      summary por conversa: ON
                    </div>
                  ) : null}

                  {conversationsDeleteEnabled ? (
                    <div style={{ marginBottom: "14px", padding: "10px", border: "1px solid #555", borderRadius: "6px" }}>
                      <div style={{ marginBottom: "6px", fontWeight: 700 }}>{convT(L, "deleteAll")}</div>
                      <div style={{ marginBottom: "8px", fontSize: "0.88em", opacity: 0.9 }}>
                        {convT(L, "deleteAllWarn")}
                      </div>
                      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        <input
                          type="text"
                          value={deleteConfirmInput}
                          onChange={(e) => setDeleteConfirmInput(e.target.value)}
                          placeholder={deleteConfirmWord}
                          style={{ padding: "6px 8px", minWidth: "130px" }}
                        />
                        <button
                          className="btn gothic-btn"
                          onClick={handleDeleteAllConversationsHistory}
                          disabled={!canDeleteAllConversations || deletionBusy}
                          style={{ backgroundColor: "#6f1d1d" }}
                        >
                          {convT(L, "deleteAll")}
                        </button>
                      </div>
                    </div>
                  ) : null}

                  <div style={{ maxHeight: "230px", overflowY: "auto", marginBottom: "12px" }}>
                    {conversationsLoading ? (
                      <p>{convT(L, "loading")}</p>
                    ) : visibleConversations.length === 0 ? (
                      <p>{convT(L, "noConversations")}</p>
                    ) : (
                      visibleConversations.map((item) => {
                        const cid = String(item?.id || "");
                        const isActive = cid && cid === activeConversationId;
                        const updatedAt = item?.updated_at
                          ? new Date(Number(item.updated_at) * 1000).toLocaleString()
                          : "";
                        return (
                          <div
                            key={cid}
                            style={{
                              marginBottom: "10px",
                              border: isActive ? "1px solid #b34c4c" : "1px solid #555",
                              borderRadius: "6px",
                              padding: "8px",
                            }}
                          >
                            <div style={{ fontWeight: 700 }}>{String(item?.title || convT(L, "newConversation"))}</div>
                            <div style={{ fontSize: "0.85em", opacity: 0.8 }}>{String(item?.last_message_preview || "")}</div>
                            <div style={{ fontSize: "0.78em", opacity: 0.7, marginTop: "4px" }}>{updatedAt}</div>
                            <div style={{ marginTop: "8px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                              <button
                                className="btn gothic-btn"
                                style={{ fontSize: "0.82em", padding: "4px 8px" }}
                                onClick={() => openConversationById(cid)}
                                disabled={loadingConversationMessages}
                              >
                                {convT(L, "open")}
                              </button>
                              {conversationsDeleteEnabled ? (
                                <button
                                  className="btn gothic-btn"
                                  style={{ fontSize: "0.82em", padding: "4px 8px", backgroundColor: "#6f1d1d" }}
                                  onClick={() => handleDeleteConversationItem(cid)}
                                  disabled={deletionBusy}
                                >
                                  {convT(L, "deleteConv")}
                                </button>
                              ) : null}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  <div style={{ borderTop: "1px solid rgba(255,255,255,0.2)", paddingTop: "10px" }}>
                    <div style={{ marginBottom: "8px", fontWeight: 700 }}>
                      {convT(L, "historyTitle")}
                    </div>
                    {!activeConversationId ? (
                      <p style={{ opacity: 0.9 }}>{convT(L, "noMessages")}</p>
                    ) : messages.length === 0 ? (
                      <p style={{ opacity: 0.9 }}>{convT(L, "noMessages")}</p>
                    ) : (
                      <>
                        <div style={{ maxHeight: "220px", overflowY: "auto", paddingRight: "4px" }}>
                          {messages.map((msg, idx) => {
                            const mid = String(msg?.id || `local-${idx}`);
                            const roleLabel = msg?.sender === "user" ? tr(L, "you") : "Van Helsin";
                            const selectable = conversationsDeleteEnabled && !mid.startsWith("local-");
                            return (
                              <div
                                key={`${mid}-${idx}`}
                                style={{ border: "1px solid #444", borderRadius: "6px", padding: "8px", marginBottom: "8px" }}
                              >
                                <div style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
                                  <strong>{roleLabel}</strong>
                                  {selectable ? (
                                    <label style={{ fontSize: "0.8em", display: "flex", alignItems: "center", gap: "4px" }}>
                                      <input
                                        type="checkbox"
                                        checked={Boolean(conversationSelection[mid])}
                                        onChange={() => toggleConversationMessageSelection(mid)}
                                      />
                                      {convT(L, "deleteSelected")}
                                    </label>
                                  ) : null}
                                </div>
                                <div style={{ marginTop: "6px", whiteSpace: "pre-wrap" }}>
                                  {String(msg?.waitBridgePrefix || "").trim()
                                    ? `${String(msg.waitBridgePrefix).trim()}\n\n`
                                    : ""}
                                  {String(msg?.text || "")}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {conversationsDeleteEnabled ? (
                          <button
                            className="btn gothic-btn"
                            onClick={handleDeleteSelectedConversationMessages}
                            disabled={selectedConversationMessageIds.length === 0 || deletionBusy}
                            style={{ backgroundColor: "#6f1d1d" }}
                          >
                            {convT(L, "deleteSelected")} ({selectedConversationMessageIds.length})
                          </button>
                        ) : null}
                      </>
                    )}
                  </div>
                </>
              ) : !showMemoryList ? (
                <>
                  <div style={{ opacity: 0.95 }}>
                    {billing
                      ? isAboutFullBillingStatus(billing)
                        ? tr(L, "loggedFullActive")
                        : billing.plan === "paid"
                        ? tr(L, "planPaidActive", { date: billing.plan_expires_at_iso || "?" })
                        : String(billing.paywall_mode || "").toLowerCase() === "enforce"
                        ? tr(L, "planFreeUsed", { used: billing.free_used, limit: billing.free_limit })
                        : tr(L, "accessReleasedPaywall")
                      : tr(L, "billingUnavailable")}
                  </div>

                  {billing && billing.plan !== "paid" && !isAboutFullBillingStatus(billing) && (
                    <div style={{ marginTop: "10px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      <button
                        type="button"
                        className="btn gothic-btn"
                        onClick={openAboutLogin}
                      >
                        {tr(L, "ctaAlreadyHaveSeal")}
                      </button>
                      <button
                        type="button"
                        className="btn gothic-btn"
                        onClick={openAboutFullCheckout}
                      >
                        {tr(L, "ctaObtainFullSeal")}
                      </button>
                    </div>
                  )}

                  <hr style={{ margin: "14px 0", opacity: 0.3 }} />

                  <div style={{ opacity: 0.95 }}>
                    {memoryStatus
                      ? `${tr(L, "memoryLabel")}: ${memoryStatus.memory_enabled ? tr(L, "memoryOn") : tr(L, "memoryOff")} | Consent: ${
                          memoryStatus.consent ? tr(L, "consentYes") : tr(L, "consentNo")
                        } | Modo: ${memoryNamespaceUiLabel}`
                      : tr(L, "memoryStatus")}
                  </div>

                  {!memoryStatus?.consent && (
                    <div style={{ 
                      marginTop: "10px", 
                      padding: "10px", 
                      backgroundColor: "rgba(139, 0, 0, 0.2)", 
                      borderRadius: "4px",
                      fontSize: "0.9em"
                    }}>
                      ⚠️ {tr(L, "consentWarning")}
                    </div>
                  )}

                  <div style={{ marginTop: "10px" }}>
                    <button 
                      className="btn gothic-btn" 
                      onClick={handleToggleMemory} 
                      disabled={loadingMemory || !memoryStatus}
                      style={{ 
                        backgroundColor: memoryStatus && !memoryStatus.memory_enabled ? "#2d5016" : undefined 
                      }}
                    >
                      {memoryStatus && memoryStatus.memory_enabled 
                        ? tr(L, "turnOffMemory") 
                        : tr(L, "turnOnMemory")}
                    </button>
                  </div>

                  <div style={{ marginTop: "10px" }}>
                    <button className="btn gothic-btn" onClick={handleDeleteSummaries} disabled={loadingMemory}>
                      {tr(L, "deleteSummaries")}
                    </button>
                    <button
                      className="btn gothic-btn"
                      onClick={handleClearHistory}
                      disabled={loadingMemory}
                      style={{ marginLeft: "10px" }}
                    >
                      {tr(L, "deleteHistory")}
                    </button>
                  </div>

                  <div style={{ marginTop: "10px" }}>
                    <label style={{ marginRight: "10px" }}>{tr(L, "keepLast")}</label>
                    <input
                      type="number"
                      min="0"
                      value={keepLastN}
                      onChange={(e) => setKeepLastN(e.target.value)}
                      style={{ width: "90px" }}
                    />
                    <button className="btn gothic-btn" onClick={handleTrimHistory} disabled={loadingMemory} style={{ marginLeft: "10px" }}>
                      {tr(L, "apply")}
                    </button>
                  </div>

                  <hr style={{ margin: "14px 0", opacity: 0.3 }} />

                  {identityCardAvailable ? (
                    <div style={{ marginTop: "10px" }}>
                      <div style={{ marginBottom: "8px", fontSize: "0.95em", opacity: 0.95 }}>
                        <strong>{tr(L, "identityCardTitle")}</strong>
                        <p style={{ margin: "6px 0", fontSize: "0.85em", opacity: 0.85 }}>
                          {tr(L, "identityCardHint")}
                        </p>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <label style={{ fontSize: "0.85em" }}>{tr(L, "tom")}</label>
                        <select value={identityCardDraft.tom} onChange={(e) => setIdentityCardDraft((d) => ({ ...d, tom: e.target.value }))} style={{ padding: "6px", maxWidth: "100%" }}>
                          <option value="default">{tr(L, "default")}</option>
                          <option value="curto">{tr(L, "short")}</option>
                          <option value="normal">{tr(L, "normal")}</option>
                        </select>
                        <label style={{ fontSize: "0.85em" }}>{tr(L, "formato")}</label>
                        <select value={identityCardDraft.formato} onChange={(e) => setIdentityCardDraft((d) => ({ ...d, formato: e.target.value }))} style={{ padding: "6px", maxWidth: "100%" }}>
                          <option value="default">{tr(L, "default")}</option>
                          <option value="bullets">{tr(L, "bullets")}</option>
                          <option value="paragrafos">{tr(L, "paragraphs")}</option>
                        </select>
                        <label style={{ fontSize: "0.85em" }}>{tr(L, "detailLevel")}</label>
                        <select value={identityCardDraft.nivel_detalhe} onChange={(e) => setIdentityCardDraft((d) => ({ ...d, nivel_detalhe: e.target.value }))} style={{ padding: "6px", maxWidth: "100%" }}>
                          <option value="default">{tr(L, "default")}</option>
                          <option value="baixo">{tr(L, "low")}</option>
                          <option value="medio">{tr(L, "medium")}</option>
                        </select>
                        <label style={{ fontSize: "0.85em" }}>{tr(L, "avoidPhrases")}</label>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <label><input type="checkbox" checked={(identityCardDraft.evitar_frases || []).includes("evitar_faca")} onChange={() => _toggleEvitar("evitar_faca")} /> {tr(L, "avoidFaca")}</label>
                          <label><input type="checkbox" checked={(identityCardDraft.evitar_frases || []).includes("evitar_voce_precisa")} onChange={() => _toggleEvitar("evitar_voce_precisa")} /> {tr(L, "avoidPrecisa")}</label>
                          <label><input type="checkbox" checked={(identityCardDraft.evitar_frases || []).includes("evitar_o_melhor_e")} onChange={() => _toggleEvitar("evitar_o_melhor_e")} /> {tr(L, "avoidMelhor")}</label>
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "8px" }}>
                          <button className="btn gothic-btn" onClick={handleIdentityCardSave} disabled={loadingMemory}>
                            {tr(L, "save")}
                          </button>
                          <button className="btn gothic-btn" onClick={handleIdentityCardRestore} disabled={loadingMemory}>
                            {tr(L, "restoreDefault")}
                          </button>
                          <button className="btn gothic-btn" onClick={handleIdentityCardClear} disabled={loadingMemory} style={{ backgroundColor: "#5a1a1a" }}>
                            {tr(L, "clear")}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  <div style={{ marginTop: "14px" }}>
                    <button className="btn gothic-btn" onClick={handleForgetMe} disabled={loadingMemory}>
                      {tr(L, "forgetMe")}
                    </button>
                  </div>

                  <div style={{ marginTop: "14px" }}>
                    <button className="btn gothic-btn" onClick={handleOpenMemoryList} disabled={loadingMemory}>
                      {tr(L, "viewMemories")}
                    </button>
                  </div>

                  {CONVERSATIONS_UI_ENABLED ? (
                    <div style={{ marginTop: "14px" }}>
                      <button
                        className="btn gothic-btn"
                        onClick={async () => {
                          await refreshConversations();
                          setShowMemoryList(false);
                          setShowSessionsMemoryPanel(true);
                        }}
                        disabled={loadingMemory || conversationsLoading || !conversationsAvailable}
                      >
                        {convT(L, "sessionsManage")}
                      </button>
                    </div>
                  ) : null}

                  {memoryContent ? (
                    <pre style={{ marginTop: "12px", whiteSpace: "pre-wrap", opacity: 0.95 }}>
                      {memoryContent}
                    </pre>
                  ) : null}
                </>
              ) : (
                <>
                  <div style={{ marginBottom: "14px" }}>
                    <button className="btn gothic-btn" onClick={() => { setShowMemoryList(false); setExpandedMemoryIndex(null); }}>
                      {tr(L, "back")}
                    </button>
                  </div>

                  <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                    {memoryListItems.length === 0 ? (
                      <div>
                        <p>{tr(L, "noMemories")}</p>
                        <p style={{ fontSize: "0.85em", opacity: 0.8, marginTop: "8px" }}>
                          {memoryStatus?.memory_enabled && memoryStatus?.consent
                            ? "Ligue a memória, dê consentimento, envie mensagens no chat e abra de novo."
                            : tr(L, "turnOnMemory") + " Depois envie mensagens e abra esta lista."}
                        </p>
                      </div>
                    ) : (
                      memoryListItems.map((item) => (
                        <div key={item.index} style={{ marginBottom: "14px", border: "1px solid #555", padding: "10px", borderRadius: "4px" }}>
                          <div style={{ fontSize: "0.85em", opacity: 0.7 }}>{item.momento}</div>
                          <div style={{ marginTop: "6px" }}>
                            <strong>{tr(L, "you")}:</strong> {item.texto.length > 100 ? item.texto.substring(0, 100) + "..." : item.texto}
                          </div>
                          <div style={{ marginTop: "8px" }}>
                            <button 
                              className="btn gothic-btn" 
                              style={{ fontSize: "0.85em", padding: "4px 8px" }}
                              onClick={() => setExpandedMemoryIndex(expandedMemoryIndex === item.index ? null : item.index)}
                            >
                              {expandedMemoryIndex === item.index ? tr(L, "hideReply") : tr(L, "showReply")}
                            </button>
                            <button 
                              className="btn gothic-btn" 
                              style={{ fontSize: "0.85em", padding: "4px 8px", marginLeft: "8px", backgroundColor: "#8b0000" }}
                              onClick={() => handleDeleteMemoryItem(item.index)}
                              disabled={loadingMemory}
                            >
                              {tr(L, "clear")}
                            </button>
                          </div>
                          {expandedMemoryIndex === item.index && (
                            <div style={{ marginTop: "8px", padding: "8px", backgroundColor: "#222", borderRadius: "4px" }}>
                              <div style={{ marginBottom: "8px" }}>
                                <strong>{tr(L, "youFull")}:</strong> {item.texto}
                              </div>
                              <div>
                                <strong>Van Helsin:</strong> {item.resposta}
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

        </>
      )}
    </div>
  );
}
